import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api, { apiService } from '../services/api';
import { useNavGuard } from '../contexts/NavGuardContext';
import PptxGenJS from 'pptxgenjs';
import AddMissingChildModal from '../components/AddMissingChildModal';
import html2pdf from 'html2pdf.js';

const DEFAULT_PLAN_ID = 'default_proposal_plan';

const NODE_TYPES = [
  { value: 'custom_ingredient', label: 'Custom Ingredient' },
  { value: 'title', label: 'Title' },
  { value: 'problem_statement', label: 'Problem Statement' },
  { value: 'agenda', label: 'Agenda' },
  { value: 'journey', label: 'Journey' },
  { value: 'journey_day', label: 'Journey Day' },
  { value: 'training', label: 'Training' },
  { value: 'activity', label: 'Activity' },
  { value: 'game', label: 'Game' },
  { value: 'framework', label: 'Framework' },
  { value: 'post_work', label: 'Post Work' },
  { value: 'roi', label: 'ROI' },
  { value: 'commercials', label: 'Commercials' },
];

const RESOURCE_TYPES = [
  { value: '', label: 'None' },
  { value: 'game', label: 'Game' },
  { value: 'activity', label: 'Activity' },
  { value: 'training', label: 'Training Module' },
  { value: 'framework', label: 'Framework' },
  { value: 'tool', label: 'Tool' },
  { value: 'psychometric', label: 'Psychometric' },
];

const RESOURCE_CATEGORY_MAP = {
  proposals: 'proposals',
  games: 'games',
  frameworks: 'frameworks',
  tools: 'tools',
  psychometrics: 'psychometricTools',
};

const RESOURCE_ATTACH_CATEGORIES = ['games', 'frameworks', 'tools', 'psychometrics'];
const RESOURCE_ATTACH_PAGE_SIZE = 25;

const EMPTY_ATTACHED_CONTEXT = {
  proposals: [],
  games: [],
  frameworks: [],
  tools: [],
  psychometricTools: [],
  activities: [],
  transcripts: [],
};

const dashboardTheme = {
  backgroundGradient: 'linear-gradient(135deg, #e1e6f7 0%, #cfd5ec 45%, #dfe4f5 100%)',
  cardBorder: '1px solid rgba(15, 23, 42, 0.08)',
  cardShadow: '0 35px 65px rgba(15, 23, 42, 0.08)',
  cardBg: 'rgba(255, 255, 255, 0.92)',
  insetBg: 'rgba(245, 246, 255, 0.85)',
  accentPrimary: '#5866f2',
  accentNavy: '#1f2a44',
  accentCoral: '#ff7b6a',
  accentSlate: '#8b95b2',
  accentSoftBlue: '#dfe4ff',
  accentSoftGray: '#f2f3f8',
  accentSuccess: '#2ab27b',
  accentWarning: '#f59e0b',
};

const pageWrapperStyle = {
  minHeight: '100vh',
  width: '100%',
  background: dashboardTheme.backgroundGradient,
  padding: '32px 36px 48px',
  boxSizing: 'border-box',
  margin: 0,
};

const cardBaseStyle = {
  borderRadius: 28,
  border: dashboardTheme.cardBorder,
  background: dashboardTheme.cardBg,
  boxShadow: dashboardTheme.cardShadow,
  backdropFilter: 'blur(18px)',
  padding: 24,
};

const insetCardStyle = {
  borderRadius: 20,
  border: '1px solid rgba(15, 23, 42, 0.05)',
  background: dashboardTheme.insetBg,
  padding: 18,
};

const inputSurfaceStyle = {
  borderRadius: 16,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  background: '#ffffff',
  color: dashboardTheme.accentNavy,
  padding: '12px 16px',
  fontSize: 14,
  boxShadow: '0 5px 20px rgba(15, 23, 42, 0.08)',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
};

const pillStyle = {
  borderRadius: 999,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 600,
  color: dashboardTheme.accentSlate,
  background: 'rgba(15, 23, 42, 0.05)',
};

const checkboxStyle = {
  width: 18,
  height: 18,
  borderRadius: 6,
  border: '2px solid rgba(15, 23, 42, 0.15)',
  background: '#ffffff',
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  position: 'relative',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
  margin: 0,
  padding: 0,
  outline: 'none',
  backgroundImage: 'none',
  backgroundPosition: 'unset',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'unset',
  accentColor: 'transparent',
  flexShrink: 0,
};

const checkboxCheckedStyle = {
  ...checkboxStyle,
  background: dashboardTheme.accentPrimary,
  borderColor: dashboardTheme.accentPrimary,
  boxShadow: '0 2px 8px rgba(88, 102, 242, 0.3)',
  backgroundImage: 'none',
};

const ensureChildren = (node) => ({
  ...node,
  children: Array.isArray(node.children) ? node.children : [],
});

const updateOutlineNode = (nodes, nodeId, updater) =>
  (nodes || []).map((node) => {
    if (node.id === nodeId) {
      return ensureChildren(updater(node));
    }
    if (node.children && node.children.length) {
      return ensureChildren({
        ...node,
        children: updateOutlineNode(node.children, nodeId, updater),
      });
    }
    return ensureChildren(node);
  });

const removeOutlineNode = (nodes, nodeId) =>
  (nodes || [])
    .filter((node) => node.id !== nodeId)
    .map((node) =>
      node.children && node.children.length
        ? ensureChildren({
          ...node,
          children: removeOutlineNode(node.children, nodeId),
        })
        : ensureChildren(node)
    );

const findNodeById = (nodes, nodeId) => {
  for (const node of nodes || []) {
    if (node.id === nodeId) return node;
    if (node.children) {
      const result = findNodeById(node.children, nodeId);
      if (result) return result;
    }
  }
  return null;
};

const FULL_WIDTH_CHILD_SEPARATOR = '-'.repeat(160);
const FULL_WIDTH_TOPIC_SEPARATOR = '='.repeat(160);

const flattenNodes = (nodes) => {
  const all = [];
  const walk = (items) => {
    items.forEach((item) => {
      all.push(item);
      if (item.children && item.children.length) {
        walk(item.children);
      }
    });
  };
  walk(nodes || []);
  return all;
};

// Helper function to escape HTML (shared across functions)
const escapeHtml = (str) => {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const parseMarkdownTables = (text) => {
  const lines = text.split('\n');
  const result = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Check for code blocks first (to catch Program Overview tree diagram)
    if (line.trim().startsWith('```')) {
      // Check if previous lines contain "## Program Overview"
      let isProgramOverview = false;
      for (let checkIdx = Math.max(0, i - 5); checkIdx < i; checkIdx++) {
        if (lines[checkIdx].trim() === '## Program Overview' || lines[checkIdx].includes('## Program Overview')) {
          isProgramOverview = true;
          break;
        }
      }

      const codeBlock = [];
      codeBlock.push(line); // Opening ```
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeBlock.push(lines[i]);
        i++;
      }
      if (i < lines.length) {
        codeBlock.push(lines[i]); // Closing ```
      }
      const codeContent = codeBlock.slice(1, -1).join('\n');

      // Only format as HTML if it's Program Overview, otherwise keep as text
      if (isProgramOverview) {
        // Remove the "## Program Overview" text if it was added to result
        if (result.length > 0 && result[result.length - 1].type === 'text' && result[result.length - 1].content.trim() === '## Program Overview') {
          result.pop();
        }
        // Include the heading with the tree diagram
        const headingHtml = '<h2 style="font-size: 22px; font-weight: 600; margin: 16px 0 8px 0; color: #111827;">Program Overview</h2>';
        const codeHtml = `<pre style="background: #f9fafb; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; overflow-x: auto; font-family: \'Courier New\', monospace; font-size: 13px; line-height: 1.6; color: #111827; white-space: pre-wrap; word-wrap: break-word; margin: 12px 0;"><code style="color: #111827;">${escapeHtml(codeContent)}</code></pre>`;
        result.push({ type: 'html', content: headingHtml + codeHtml });
      } else {
        // Regular code block - keep as text
        result.push({ type: 'text', content: codeBlock.join('\n') });
      }
      i++;
      continue;
    }

    // Check for markdown headings (only handle Program Overview specially, rest stay as text)
    if (line.trim().startsWith('##')) {
      const headingText = line.replace(/^#+\s*/, '').trim();
      if (headingText === 'Program Overview') {
        // Check if next non-empty line is a code block - if so, skip adding this heading
        // as it will be handled by the code block parser
        let nextNonEmptyIdx = i + 1;
        while (nextNonEmptyIdx < lines.length && !lines[nextNonEmptyIdx].trim()) {
          nextNonEmptyIdx++;
        }
        if (nextNonEmptyIdx < lines.length && lines[nextNonEmptyIdx].trim().startsWith('```')) {
          // Code block follows - skip adding heading, it will be handled by code block parser
          i++;
          continue;
        }
        // No code block follows - push as text
        result.push({ type: 'text', content: line });
        i++;
        continue;
      } else {
        // Regular heading - keep as text (don't convert to HTML)
        result.push({ type: 'text', content: line });
        i++;
        continue;
      }
    }

    // Check if this line looks like a table row (contains |)
    if (line.includes('|') && line.trim()) {
      // Check if next line is a separator (contains dashes, colons, and pipes)
      const nextLine = i + 1 < lines.length ? lines[i + 1] : '';
      const isSeparator = /^\s*\|[\s\-:]+\|\s*$/.test(nextLine) || /^\s*\|[\s\-:]+\|/.test(nextLine);

      // Also check if previous line was a table row
      const prevLine = i > 0 ? lines[i - 1] : '';
      const prevWasTable = prevLine.includes('|') && prevLine.trim();

      if (isSeparator || prevWasTable) {
        // Collect table rows
        const tableRows = [];
        let j = i;

        // Get header row (current line)
        const headerCells = line.split('|').map(c => c.trim()).filter(c => c || c === '');
        if (headerCells.length > 0) {
          tableRows.push(headerCells);
        }

        // Skip separator line if present
        if (isSeparator) {
          j = i + 2;
        } else {
          j = i + 1;
        }

        // Collect data rows
        while (j < lines.length && lines[j].includes('|') && lines[j].trim()) {
          // Skip separator lines
          if (/^\s*\|[\s\-:]+\|\s*$/.test(lines[j]) || /^\s*\|[\s\-:]+\|/.test(lines[j])) {
            j++;
            continue;
          }
          const cells = lines[j].split('|').map(c => c.trim()).filter(c => c || c === '');
          if (cells.length > 0 && cells.length === headerCells.length) {
            tableRows.push(cells);
          } else if (cells.length === 0) {
            // Empty line, end of table
            break;
          }
          j++;
        }

        if (tableRows.length > 0) {
          // Convert to HTML table
          let htmlTable = '<table style="width: 100%; border-collapse: collapse; margin: 12px 0; border: 1px solid #e5e7eb; background: #ffffff;">';

          // Header row
          if (tableRows.length > 0) {
            htmlTable += '<thead><tr>';
            tableRows[0].forEach(cell => {
              let cleanCell = escapeHtml(cell);
              cleanCell = cleanCell.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
              cleanCell = cleanCell.replace(/\*/g, '');
              htmlTable += `<th style="border: 1px solid #e5e7eb; padding: 8px 12px; background: #f9fafb; color: #111827; font-weight: 600; text-align: left;">${cleanCell}</th>`;
            });
            htmlTable += '</tr></thead>';
          }

          // Data rows
          if (tableRows.length > 1) {
            htmlTable += '<tbody>';
            for (let k = 1; k < tableRows.length; k++) {
              htmlTable += '<tr>';
              tableRows[k].forEach((cell, cellIdx) => {
                // Ensure we have the same number of cells as header
                let cleanCell = escapeHtml(cell || '');
                // Replace markdown bold **text** with <strong>text</strong>
                cleanCell = cleanCell.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
                cleanCell = cleanCell.replace(/\*/g, '');
                // Convert bullet points (•) and newlines to HTML list
                if (cleanCell.includes('•') || cleanCell.includes('\n')) {
                  const lines = cleanCell.split('\n').filter(l => l.trim());
                  cleanCell = '<ul style="margin: 0; padding-left: 20px; color: #111827;">' +
                    lines.map(line => {
                      const trimmed = line.trim();
                      if (trimmed.startsWith('•')) {
                        return `<li style="margin: 4px 0; color: #111827;">${trimmed.substring(1).trim()}</li>`;
                      }
                      return `<li style="margin: 4px 0; color: #111827;">${trimmed}</li>`;
                    }).join('') +
                    '</ul>';
                }
                htmlTable += `<td style="border: 1px solid #e5e7eb; padding: 8px 12px; color: #111827; vertical-align: top; background: ${k % 2 === 0 ? '#ffffff' : '#f9fafb'};}">${cleanCell}</td>`;
              });
              htmlTable += '</tr>';
            }
            htmlTable += '</tbody>';
          }

          htmlTable += '</table>';
          result.push({ type: 'html', content: htmlTable });
          i = j;
          continue;
        }
      }
    }

    // Regular text line
    result.push({ type: 'text', content: line });
    i++;
  }

  return result;
};

const formatDocumentWithTables = (text) => {
  const segments = parseMarkdownTables(text);
  return segments.map(seg => seg.content).join('\n');
};

const removeMarkdownBold = (text) => {
  if (typeof text !== 'string') return text;
  return text.replace(/\*\*(.+?)\*\*/g, '$1');
};

// Helper function to process inline markdown formatting
const processInlineMarkdown = (text) => {
  if (!text) return '';
  let processed = text;
  // Process bold (must come before italic to avoid conflicts)
  processed = processed.replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>');
  // Process italic (single asterisks, but not if part of bold)
  processed = processed.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em style="font-style: italic;">$1</em>');
  // Process inline code
  processed = processed.replace(/`([^`\n]+)`/g, '<code style="background: #f9fafb; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 13px; border: 1px solid #e5e7eb;">$1</code>');
  // Process links
  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #5865f2; text-decoration: underline;">$1</a>');
  return processed;
};

// Simple markdown renderer for preliminary proposal
const renderMarkdown = (text) => {
  if (!text) return '';
  
  const lines = text.split('\n');
  const result = [];
  let inList = false;
  let listItems = [];
  let inTable = false;
  let tableRows = [];
  let tableHeaders = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    // Horizontal rules
    if (/^---+$/.test(trimmed) && trimmed.length >= 3) {
      if (inList) {
        result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
        listItems = [];
        inList = false;
      }
      if (inTable) {
        // End table before horizontal rule
        if (tableHeaders) {
          result.push('<table style="width: 100%; border-collapse: collapse; margin: 16px 0; border: 1px solid #e5e7eb; table-layout: fixed;">');
          result.push('<thead><tr>');
          tableHeaders.forEach(header => {
            result.push(`<th style="padding: 10px 12px; text-align: left; background: #f9fafb; border-bottom: 2px solid #e5e7eb; font-weight: 600; color: #111827; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal; max-width: 1px;">${processInlineMarkdown(header.trim())}</th>`);
          });
          result.push('</tr></thead><tbody>');
          tableHeaders = null;
        }
        tableRows.forEach(row => {
          result.push('<tr>');
          row.forEach(cell => {
            result.push(`<td style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal; max-width: 1px;">${processInlineMarkdown(cell.trim())}</td>`);
          });
          result.push('</tr>');
        });
        result.push('</tbody></table>');
        tableRows = [];
        inTable = false;
      }
      result.push('<hr style="border: none; border-top: 2px solid #e5e7eb; margin: 24px 0;" />');
      continue;
    }
    
    // Tables - detect lines with pipes
    if (trimmed.includes('|') && trimmed.split('|').length > 2) {
      // Skip separator rows (like | :--- | :--- |)
      if (/^\|[\s\-\:]+(\|[\s\-\:]+)*\|?\s*$/.test(trimmed)) {
        continue; // Skip separator row
      }
      
      if (!inTable) {
        // Close any open list
        if (inList) {
          result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
          listItems = [];
          inList = false;
        }
        inTable = true;
      }
      
      const cells = trimmed.split('|').map(c => c.trim()).filter(c => c !== '');
      if (tableHeaders === null) {
        tableHeaders = cells;
      } else {
        tableRows.push(cells);
      }
      continue;
    } else if (inTable) {
      // End of table
      if (tableHeaders) {
        result.push('<table style="width: 100%; border-collapse: collapse; margin: 16px 0; border: 1px solid #e5e7eb; table-layout: fixed;">');
        result.push('<thead><tr>');
        tableHeaders.forEach(header => {
          result.push(`<th style="padding: 10px 12px; text-align: left; background: #f9fafb; border-bottom: 2px solid #e5e7eb; font-weight: 600; color: #111827; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal; max-width: 1px;">${processInlineMarkdown(header.trim())}</th>`);
        });
        result.push('</tr></thead><tbody>');
        tableHeaders = null;
      }
      tableRows.forEach(row => {
        result.push('<tr>');
        row.forEach(cell => {
          result.push(`<td style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal; max-width: 1px;">${processInlineMarkdown(cell.trim())}</td>`);
        });
        result.push('</tr>');
      });
      result.push('</tbody></table>');
      tableRows = [];
      inTable = false;
    }
    
    // Headers (check in order from most specific to least)
    if (trimmed.startsWith('#### ')) {
      if (inList) {
        result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
        listItems = [];
        inList = false;
      }
      const headerText = processInlineMarkdown(trimmed.substring(5));
      result.push(`<h4 style="font-size: 16px; font-weight: 600; margin: 14px 0 6px 0; color: #111827;">${headerText}</h4>`);
      continue;
    }
    if (trimmed.startsWith('### ')) {
      if (inList) {
        result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
        listItems = [];
        inList = false;
      }
      const headerText = processInlineMarkdown(trimmed.substring(4));
      result.push(`<h3 style="font-size: 18px; font-weight: 600; margin: 16px 0 8px 0; color: #111827;">${headerText}</h3>`);
      continue;
    }
    if (trimmed.startsWith('## ')) {
      if (inList) {
        result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
        listItems = [];
        inList = false;
      }
      const headerText = processInlineMarkdown(trimmed.substring(3));
      result.push(`<h2 style="font-size: 20px; font-weight: 600; margin: 20px 0 10px 0; color: #111827;">${headerText}</h2>`);
      continue;
    }
    if (trimmed.startsWith('# ')) {
      if (inList) {
        result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
        listItems = [];
        inList = false;
      }
      const headerText = processInlineMarkdown(trimmed.substring(2));
      result.push(`<h1 style="font-size: 24px; font-weight: 600; margin: 24px 0 12px 0; color: #111827;">${headerText}</h1>`);
      continue;
    }
    
    // Code blocks
    if (trimmed.startsWith('```')) {
      if (inList) {
        result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
        listItems = [];
        inList = false;
      }
      const codeBlock = [];
      codeBlock.push(line);
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeBlock.push(lines[i]);
        i++;
      }
      if (i < lines.length) codeBlock.push(lines[i]);
      const codeContent = codeBlock.slice(1, -1).join('\n');
      result.push(`<pre style="background: #f9fafb; padding: 12px; border: 1px solid #e5e7eb; border-radius: 4px; overflow-x: auto; font-family: monospace; font-size: 13px; margin: 12px 0; white-space: pre-wrap;"><code>${escapeHtml(codeContent)}</code></pre>`);
      continue;
    }
    
    // List items
    if (/^[\*\-\+] (.+)$/.test(trimmed) || /^\d+\. (.+)$/.test(trimmed)) {
      const content = trimmed.replace(/^[\*\-\d+\.]+\s+/, '');
      const processedContent = processInlineMarkdown(content);
      listItems.push(`<li style="margin: 4px 0; line-height: 1.6; color: #111827;">${processedContent}</li>`);
      inList = true;
      continue;
    }
    
    // End of list
    if (inList && trimmed === '') {
      result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
      listItems = [];
      inList = false;
      result.push('<br />');
      continue;
    }
    
    // Regular text
    if (inList) {
      result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
      listItems = [];
      inList = false;
    }
    
    if (trimmed === '') {
      result.push('<br />');
    } else {
      const processedLine = processInlineMarkdown(line);
      result.push(`<p style="margin: 8px 0; line-height: 1.6; color: #111827;">${processedLine}</p>`);
    }
  }
  
  // Close any remaining list
  if (inList) {
    result.push(`<ul style="margin: 8px 0; padding-left: 24px;">${listItems.join('')}</ul>`);
  }
  
  // Close any remaining table
  if (inTable) {
    if (tableHeaders) {
      result.push('<table style="width: 100%; border-collapse: collapse; margin: 16px 0; border: 1px solid #e5e7eb; table-layout: fixed;">');
      result.push('<thead><tr>');
      tableHeaders.forEach(header => {
        result.push(`<th style="padding: 10px 12px; text-align: left; background: #f9fafb; border-bottom: 2px solid #e5e7eb; font-weight: 600; color: #111827; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal; max-width: 1px;">${processInlineMarkdown(header.trim())}</th>`);
      });
      result.push('</tr></thead><tbody>');
    }
    tableRows.forEach(row => {
      result.push('<tr>');
      row.forEach(cell => {
        result.push(`<td style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal; max-width: 1px;">${processInlineMarkdown(cell.trim())}</td>`);
      });
      result.push('</tr>');
    });
    result.push('</tbody></table>');
  }
  
  return `<div style="line-height: 1.6; color: #111827;">${result.join('')}</div>`;
};

const renderTextWithBold = (text) => {
  if (!text) return null;

  const lines = text.split('\n');
  let boldCounter = 0;

  return lines.map((line, lineIdx) => {
    const segments = [];
    let lastIndex = 0;
    const regex = /\*\*(.+?)\*\*/g;
    let match;

    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: line.slice(lastIndex, match.index),
          key: `text-${lineIdx}-${segments.length}`,
        });
      }
      segments.push({
        type: 'bold',
        content: match[1],
        key: `bold-${lineIdx}-${boldCounter++}`,
      });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
      segments.push({
        type: 'text',
        content: line.slice(lastIndex),
        key: `text-${lineIdx}-${segments.length}`,
      });
    }

    if (segments.length === 0) {
      segments.push({
        type: 'text',
        content: line,
        key: `text-${lineIdx}-0`,
      });
    }

    return (
      <React.Fragment key={`line-${lineIdx}`}>
        {segments.map((segment) =>
          segment.type === 'bold' ? (
            <strong key={segment.key}>{segment.content}</strong>
          ) : (
            <React.Fragment key={segment.key}>{segment.content}</React.Fragment>
          )
        )}
        {lineIdx < lines.length - 1 && <br key={`br-${lineIdx}`} />}
      </React.Fragment>
    );
  });
};

const ResourceSelector = ({
  attachedContext,
  setAttachedContext,
  resourceAttachModal,
  closeResourceAttachModal,
  resourceAttachCategory,
  setResourceAttachCategory,
  resourceAttachSearch,
  setResourceAttachSearch,
  filteredAttachResources,
  handleResourceSelectionForChild,
  handleManualChildCreation,
  resourceAttachLoading,
  resourceAttachPagination,
  handleResourceAttachPageChange,
}) => {
  const modalState = resourceAttachModal || { open: false };
  const closeModal = closeResourceAttachModal || (() => { });
  const updateAttachCategory = setResourceAttachCategory || (() => { });
  const updateAttachSearch = setResourceAttachSearch || (() => { });
  const attachResources = filteredAttachResources || [];
  const selectAttachResource = handleResourceSelectionForChild || (() => { });
  const addManualChild = handleManualChildCreation || (() => { });
  const attachCategory = resourceAttachCategory || 'all';
  const attachSearch = resourceAttachSearch || '';
  const attachPagination = resourceAttachPagination || { page: 1, totalPages: 1 };
  const changeAttachPage = handleResourceAttachPageChange || (() => { });
  const [activeCategory, setActiveCategory] = useState('proposals');
  const [search, setSearch] = useState('');
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const categoryKey = (cat) => RESOURCE_CATEGORY_MAP[cat] || cat;
  const hasSelections = useMemo(() => {
    if (!attachedContext) return false;
    const categorySelected = Object.values(RESOURCE_CATEGORY_MAP).some((key) => (attachedContext[key] || []).length > 0);
    const otherSelected =
      (attachedContext.activities && attachedContext.activities.length > 0) ||
      (attachedContext.transcripts && attachedContext.transcripts.length > 0);
    return categorySelected || otherSelected;
  }, [attachedContext]);

  const handleClearSelection = () => {
    if (!hasSelections) return;
    if (window.confirm('Clear all attached resources? This will remove games, frameworks, tools, and other context.')) {
      setAttachedContext({ ...EMPTY_ATTACHED_CONTEXT });
    }
  };

  const loadList = async (cat, pageNum = 1) => {
    setLoading(true);
    try {
      if (cat === 'proposals') {
        const response = await apiService.getReferenceProposals();
        const all = (response?.proposals || []).map((item) => ({
          id: item.id || item._id || item.uuid || item.title,
          name: item.title || item.name || 'Proposal',
          description: item.summary || item.content || '',
          content: item.content || '',
          raw: item,
        }));
        const query = (search || '').trim().toLowerCase();
        const filtered = query
          ? all.filter((item) => item.name.toLowerCase().includes(query))
          : all;
        setTotal(filtered.length);
        setList(filtered.slice((pageNum - 1) * 25, pageNum * 25));
      } else {
        const response = await apiService.getCatalogItems(cat === 'psychometrics' ? 'psychometrics' : cat, pageNum, 25);
        const query = (search || '').trim().toLowerCase();
        const filtered = (response.items || []).filter((item) => {
          const name = (item.name || '').toLowerCase();
          return !query || name.includes(query);
        });
        setTotal(response.total || filtered.length);
        setList(filtered.map((item) => ({
          id: item.id || item.uuid || item.name,
          name: item.name || 'Resource',
          description: item.detailed_explanation || item.description || item.summary || item.what || '',
          content: item.detailed_explanation || item.description || item.summary || item.what || '',
          raw: item,
        })));
      }
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to load resources', error);
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList(activeCategory, 1);
    // eslint-disable-next-line
  }, [activeCategory]);

  const toggleItem = (category, item) => {
    const key = categoryKey(category);
    setAttachedContext((prev) => {
      const arr = prev[key] || [];
      const exists = arr.find((entry) => entry.id === item.id);
      const next = exists ? arr.filter((entry) => entry.id !== item.id) : [...arr, item.raw || item];
      return { ...prev, [key]: next };
    });
  };

  const isSelected = (category, itemId) => {
    const key = categoryKey(category);
    return (attachedContext[key] || []).some((entry) => entry.id === itemId);
  };

  const renderList = () => {
    if (loading) {
      return <div style={{ color: '#6b7280', padding: 12 }}>Loading…</div>;
    }
    if (!list.length) {
      return <div style={{ color: '#6b7280', padding: 12 }}>No resources found.</div>;
    }
    return list.map((item) => (
      <div
        key={item.id}
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          padding: '12px 16px',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          background: 'rgba(255, 255, 255, 0.75)',
        }}
      >
        <div style={{ position: 'relative', marginTop: 6 }}>
          <input
            type="checkbox"
            checked={isSelected(activeCategory, item.id)}
            onChange={() => toggleItem(activeCategory, item)}
            style={isSelected(activeCategory, item.id) ? checkboxCheckedStyle : checkboxStyle}
          />
          {isSelected(activeCategory, item.id) && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 10,
                height: 10,
                pointerEvents: 'none',
              }}
            >
              <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M2 6L5 9L10 2"
                  stroke="#ffffff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>
        <div
          style={{ flex: 1, cursor: 'pointer' }}
          onClick={() => setDetailItem(item)}
        >
          <div style={{ fontWeight: 600, color: dashboardTheme.accentNavy }}>
            {item.name}
          </div>
          {item.description && (
            <div style={{ fontSize: 12, color: dashboardTheme.accentSlate, marginTop: 4 }}>
              {String(item.description).slice(0, 160)}
              {String(item.description).length > 160 ? '…' : ''}
            </div>
          )}
        </div>
      </div>
    ));
  };

  const pages = Math.max(1, Math.ceil(total / 25));

  return (
    <div style={{ ...cardBaseStyle, padding: 20 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div style={{ ...pillStyle, background: 'rgba(88, 102, 242, 0.15)', color: dashboardTheme.accentPrimary }}>
          Context Attachments
        </div>
        <span style={{ fontSize: 12, color: dashboardTheme.accentSlate }}>
          Attach proposal assets, games, frameworks, and tools for richer outlines.
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        {['proposals', 'games', 'frameworks', 'tools', 'psychometrics'].map((cat) => {
          const isActive = activeCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                ...pillStyle,
                padding: '8px 18px',
                border: isActive ? '1px solid transparent' : '1px solid rgba(15, 23, 42, 0.08)',
                background: isActive ? dashboardTheme.accentPrimary : 'rgba(255, 255, 255, 0.85)',
                color: isActive ? '#ffffff' : dashboardTheme.accentSlate,
                boxShadow: isActive ? '0 12px 30px rgba(88, 102, 242, 0.35)' : 'none',
                cursor: 'pointer',
              }}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${activeCategory}`}
          style={{ ...inputSurfaceStyle, flex: '1 1 220px' }}
          onFocus={(e) => {
            e.target.style.borderColor = dashboardTheme.accentPrimary;
            e.target.style.boxShadow = '0 0 0 4px rgba(88, 102, 242, 0.15)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(15, 23, 42, 0.08)';
            e.target.style.boxShadow = '0 5px 20px rgba(15, 23, 42, 0.08)';
          }}
        />
        <button
          onClick={() => loadList(activeCategory, 1)}
          style={{
            borderRadius: 16,
            border: 'none',
            background: dashboardTheme.accentPrimary,
            color: '#ffffff',
            padding: '12px 24px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 20px 40px rgba(88, 102, 242, 0.3)',
          }}
        >
          Refresh
        </button>
        <button
          onClick={handleClearSelection}
          disabled={!hasSelections}
          style={{
            borderRadius: 16,
            border: '1px solid rgba(15, 23, 42, 0.1)',
            background: hasSelections ? '#ffffff' : 'rgba(255, 255, 255, 0.6)',
            color: hasSelections ? dashboardTheme.accentSlate : 'rgba(107, 114, 128, 0.6)',
            padding: '12px 20px',
            fontSize: 13,
            fontWeight: 600,
            cursor: hasSelections ? 'pointer' : 'not-allowed',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
            transition: 'all 0.2s ease',
          }}
        >
          Clear Selection
        </button>
      </div>
      <div style={{ ...insetCardStyle, padding: 0, maxHeight: 240, overflowY: 'auto' }}>{renderList()}</div>
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => loadList(activeCategory, Math.max(1, page - 1))}
            disabled={page === 1}
            style={{
              borderRadius: 12,
              border: 'none',
              background: page === 1 ? dashboardTheme.accentSoftGray : dashboardTheme.accentSoftBlue,
              color: dashboardTheme.accentSlate,
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: 600,
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              opacity: page === 1 ? 0.6 : 1,
              boxShadow: page === 1 ? 'none' : '0 4px 12px rgba(223, 228, 255, 0.4)',
              transition: 'all 0.2s',
            }}
          >
            Prev
          </button>
          <span style={{ fontSize: 13, color: dashboardTheme.accentSlate, fontWeight: 600 }}>
            Page {page} / {pages}
          </span>
          <button
            onClick={() => loadList(activeCategory, Math.min(pages, page + 1))}
            disabled={page === pages}
            style={{
              borderRadius: 12,
              border: 'none',
              background: page === pages ? dashboardTheme.accentSoftGray : dashboardTheme.accentSoftBlue,
              color: dashboardTheme.accentSlate,
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: 600,
              cursor: page === pages ? 'not-allowed' : 'pointer',
              opacity: page === pages ? 0.6 : 1,
              boxShadow: page === pages ? 'none' : '0 4px 12px rgba(223, 228, 255, 0.4)',
              transition: 'all 0.2s',
            }}
          >
            Next
          </button>
        </div>
      )}
      {detailItem && (
        <div style={{ ...insetCardStyle, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h4 style={{ margin: 0, fontSize: 14, color: dashboardTheme.accentNavy }}>{detailItem.name}</h4>
            <button
              onClick={() => setDetailItem(null)}
              style={{ border: 'none', background: 'transparent', color: dashboardTheme.accentSlate, cursor: 'pointer', fontSize: 16 }}
            >
              ×
            </button>
          </div>
          <div style={{ fontSize: 12, color: dashboardTheme.accentSlate, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {detailItem.content || detailItem.description}
          </div>
        </div>
      )}
      <div style={{ marginTop: 16, fontSize: 12, color: dashboardTheme.accentSlate, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontWeight: 600 }}>Selected</span>
        {Object.entries(RESOURCE_CATEGORY_MAP).map(([cat, key]) => {
          const count = (attachedContext[key] || []).length;
          if (!count) return null;
          return (
            <span
              key={cat}
              style={{
                ...pillStyle,
                background: 'rgba(42, 178, 123, 0.15)',
                color: dashboardTheme.accentSuccess,
              }}
            >
              {cat}: {count}
            </span>
          );
        })}
      </div>
      {modalState.open && typeof document !== 'undefined' &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.55)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 24,
              zIndex: 9999,
            }}
          >
            <div
              style={{
                width: 'min(680px, 95%)',
                background: '#ffffff',
                borderRadius: 24,
                padding: 24,
                boxShadow: '0 45px 90px rgba(15, 23, 42, 0.35)',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 600 }}>
                    Attach Resource
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: '#0f172a' }}>Select a catalog resource for this section</div>
                </div>
                <button
                  onClick={closeModal}
                  style={{
                    border: 'none',
                    background: 'rgba(148, 163, 184, 0.2)',
                    color: '#0f172a',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    fontSize: 18,
                    cursor: 'pointer',
                  }}
                  title="Close"
                >
                  ×
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {['all', 'games', 'frameworks', 'tools', 'psychometrics'].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => updateAttachCategory(cat)}
                    style={{
                      borderRadius: 999,
                      border: '1px solid',
                      borderColor: attachCategory === cat ? dashboardTheme.accentPrimary : 'rgba(15, 23, 42, 0.12)',
                      background: attachCategory === cat ? 'rgba(88, 102, 242, 0.12)' : 'transparent',
                      color: attachCategory === cat ? dashboardTheme.accentPrimary : '#475569',
                      padding: '6px 16px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
              <input
                value={attachSearch}
                onChange={(e) => updateAttachSearch(e.target.value)}
                placeholder="Search resources by name, ID, or reasoning"
                style={{
                  ...inputSurfaceStyle,
                  width: '100%',
                  marginBottom: 14,
                }}
              />
              <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4, borderTop: '1px solid #e2e8f0' }}>
                {resourceAttachLoading && (
                  <div style={{ padding: '12px 8px', color: '#475569', fontSize: 12 }}>
                    Loading catalog resources…
                  </div>
                )}
                {attachResources.length === 0 && (
                  <div style={{ padding: '32px 12px', color: '#94a3b8', textAlign: 'center' }}>
                    No matching resources. Adjust filters or run generation first.
                  </div>
                )}
                {attachResources.map((resource) => (
                  <div
                    key={`${resource.category}-${resource.id}`}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '16px 4px',
                      borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#94a3b8' }}>
                        {resource.category}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>{resource.name}</div>
                      <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
                        {resource.reasoning || resource.description || 'No reasoning provided.'}
                      </div>
                      {resource.placement && (
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Placement: {resource.placement}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => selectAttachResource(resource)}
                      style={{
                        alignSelf: 'center',
                        borderRadius: 12,
                        border: 'none',
                        background: dashboardTheme.accentPrimary,
                        color: '#ffffff',
                        padding: '10px 18px',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 15px 30px rgba(88, 102, 242, 0.25)',
                      }}
                    >
                      Use Resource
                    </button>
                  </div>
                ))}
              </div>
              {attachCategory !== 'all' && attachPagination.totalPages > 1 && (
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center', marginTop: 12 }}>
                  <button
                    onClick={() => changeAttachPage('prev')}
                    disabled={attachPagination.page <= 1 || resourceAttachLoading}
                    style={{
                      borderRadius: 10,
                      border: 'none',
                      padding: '6px 14px',
                      background: attachPagination.page <= 1 || resourceAttachLoading ? 'rgba(148,163,184,0.3)' : dashboardTheme.accentSoftBlue,
                      color: '#1f2a44',
                      cursor: attachPagination.page <= 1 || resourceAttachLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Prev
                  </button>
                  <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
                    Page {attachPagination.page} / {attachPagination.totalPages}
                  </span>
                  <button
                    onClick={() => changeAttachPage('next')}
                    disabled={attachPagination.page >= attachPagination.totalPages || resourceAttachLoading}
                    style={{
                      borderRadius: 10,
                      border: 'none',
                      padding: '6px 14px',
                      background:
                        attachPagination.page >= attachPagination.totalPages || resourceAttachLoading
                          ? 'rgba(148,163,184,0.3)'
                          : dashboardTheme.accentSoftBlue,
                      color: '#1f2a44',
                      cursor:
                        attachPagination.page >= attachPagination.totalPages || resourceAttachLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
              <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between' }}>
                <button
                  onClick={addManualChild}
                  style={{
                    borderRadius: 12,
                    border: '1px dashed rgba(148, 163, 184, 0.8)',
                    background: 'rgba(248, 250, 255, 0.9)',
                    color: '#1f2a44',
                    padding: '8px 18px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Skip catalog & add blank ingredient
                </button>
                <button
                  onClick={closeModal}
                  style={{
                    borderRadius: 12,
                    border: '1px solid rgba(148, 163, 184, 0.6)',
                    background: 'transparent',
                    color: '#475569',
                    padding: '8px 18px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

const ProposalOutlineBuilder = () => {
  const { planId: urlPlanId } = useParams();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const selectedNodeIdRef = useRef(null);

  // Use planId from URL if available, otherwise check localStorage for last worked proposal, otherwise use default
  // Decode URL-encoded planId
  const decodedUrlPlanId = urlPlanId ? decodeURIComponent(urlPlanId) : null;

  // Check localStorage for last worked proposal if no URL parameter
  const getLastWorkedProposal = () => {
    try {
      const lastProposal = localStorage.getItem('propulse_last_worked_proposal');
      return lastProposal || null;
    } catch (e) {
      return null;
    }
  };

  const initialPlanId = decodedUrlPlanId || getLastWorkedProposal() || DEFAULT_PLAN_ID;
  const [planId, setPlanId] = useState(initialPlanId);
  const [planIdInput, setPlanIdInput] = useState(initialPlanId);
  const [outline, setOutline] = useState([]);
  const [conceptDocument, setConceptDocument] = useState('');
  const [showConceptDoc, setShowConceptDoc] = useState(false);
  const [loadingOutline, setLoadingOutline] = useState(false);
  const [savingOutline, setSavingOutline] = useState(false);
  const [copiedOutline, setCopiedOutline] = useState(false);

  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [nodeTitle, setNodeTitle] = useState('');
  const [nodeType, setNodeType] = useState('custom_ingredient');
  const [resourceType, setResourceType] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [nodeContent, setNodeContent] = useState('');
  const [savingNode, setSavingNode] = useState(false);

  const [attachedContext, setAttachedContext] = useState(EMPTY_ATTACHED_CONTEXT);
  const [selectedResourcesData, setSelectedResourcesData] = useState({
    selected_resources: {},
    placement_strategy: {},
  });
  const [resourceAttachModal, setResourceAttachModal] = useState({ open: false, parentId: null });
  const [resourceAttachSearch, setResourceAttachSearch] = useState('');
  const [resourceAttachCategory, setResourceAttachCategory] = useState('all');
  const [addMissingChildModal, setAddMissingChildModal] = useState({ open: false, parentId: null });
  const [resourceAttachCatalogState, setResourceAttachCatalogState] = useState({});
  const resourceAttachCatalogStateRef = useRef({});
  const [resourceAttachLoadingCategory, setResourceAttachLoadingCategory] = useState(null);
  const [clientName, setClientName] = useState('');
  const [autoUserCommand, setAutoUserCommand] = useState('');
  // Context fields (optional)
  const [cohortSize, setCohortSize] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [rolesFunctions, setRolesFunctions] = useState('');
  const [language, setLanguage] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [geographicRegion, setGeographicRegion] = useState('');
  const [timelineUrgency, setTimelineUrgency] = useState('');
  const [autoTemplate, setAutoTemplate] = useState('generic');
  // Context items (unified: files, text, past proposals)
  const [contextItems, setContextItems] = useState([]); // Array of {id, type: 'file'|'text'|'proposal', content, description, fileName?, extracting?, proposalId?}
  const contextItemIdCounter = useRef(0);
  const [contextTab, setContextTab] = useState('file'); // 'file', 'text', 'proposal'
  const [referenceProposals, setReferenceProposals] = useState([]); // For past proposals tab
  const [loadingReferenceProposals, setLoadingReferenceProposals] = useState(false);
  const [proposalSearchQuery, setProposalSearchQuery] = useState(''); // Search query for proposals
  const [selectedProposalIds, setSelectedProposalIds] = useState(new Set()); // Set of selected proposal IDs for multi-select
  const [sharedProposalComment, setSharedProposalComment] = useState(''); // Shared comment for all proposals
  const [selectedProposalPreview, setSelectedProposalPreview] = useState(null); // {id, title, content} for preview
  const reasoningContainerRef = useRef(null);
  const [reasoningLog, setReasoningLog] = useState([]);
  const [progressMessage, setProgressMessage] = useState('');
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [showPreliminaryProposal, setShowPreliminaryProposal] = useState(false);
  const [preliminaryProposal, setPreliminaryProposal] = useState('');
  const [preliminaryProposalText, setPreliminaryProposalText] = useState('');
  const [refinementInstructions, setRefinementInstructions] = useState('');
  const [isRefiningProposal, setIsRefiningProposal] = useState(false);
  const [preliminaryProposalViewMode, setPreliminaryProposalViewMode] = useState('view'); // 'view' or 'edit'
  const [downloadingPreliminaryPdf, setDownloadingPreliminaryPdf] = useState(false);
  const pendingProposalPayloadRef = useRef(null);

  // Update proposal text when preliminary proposal changes
  useEffect(() => {
    if (preliminaryProposal) {
      setPreliminaryProposalText(preliminaryProposal);
      // Auto-show card if preliminary proposal exists
      if (preliminaryProposal.trim()) {
        setShowPreliminaryProposal(true);
      }
    }
  }, [preliminaryProposal]);
  const [connecting, setConnecting] = useState(false);
  const [generatingNodeContent, setGeneratingNodeContent] = useState(false);
  const [generatingAllContent, setGeneratingAllContent] = useState(false);
  const [outlineBusy, setOutlineBusy] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, currentNode: '' });
  const [deckSummaries, setDeckSummaries] = useState({});
  const [deckGenerating, setDeckGenerating] = useState(false);
  const [deckStatus, setDeckStatus] = useState('');
  const [exportProgress, setExportProgress] = useState({ show: false, type: '', current: 0, total: 0, message: '' });
  const [nodeAiInstruction, setNodeAiInstruction] = useState('');
  const [nodeAiProcessing, setNodeAiProcessing] = useState(false);
  const [nodeAiPlan, setNodeAiPlan] = useState([]);
  const [nodeAiStatus, setNodeAiStatus] = useState('');
  const [nodeDiffs, setNodeDiffs] = useState([]);
  const [nodeDiffView, setNodeDiffView] = useState(false);
  const [nodeCurrentDiffIndex, setNodeCurrentDiffIndex] = useState(0);
  const [showNewProposalModal, setShowNewProposalModal] = useState(false);
  const [showDeleteProposalModal, setShowDeleteProposalModal] = useState(false);
  const nodeDiffRefs = useRef([]);
  const [nodeMemoryMap, setNodeMemoryMap] = useState(null);
  const [nodeOriginalContent, setNodeOriginalContent] = useState('');
  const [nodeApplyingDiffs, setNodeApplyingDiffs] = useState(false);
  const [showModelSettings, setShowModelSettings] = useState(false);
  const [plannerModelOverride, setPlannerModelOverride] = useState('');
  const [plannerFallbackOverride, setPlannerFallbackOverride] = useState('');
  const [generatorModelOverride, setGeneratorModelOverride] = useState('');
  const [generatorFallbackOverride, setGeneratorFallbackOverride] = useState('');
  const [useResourceContentDirectly, setUseResourceContentDirectly] = useState(false);
  const [resourceScriptApplying, setResourceScriptApplying] = useState(false);
  const [resourceScriptStatus, setResourceScriptStatus] = useState('');
  const resourceContentCacheRef = useRef({});
  const [fullDocumentOverride, setFullDocumentOverride] = useState(null);
  const deckRequestResolveRef = useRef(null);
  const deckRequestRejectRef = useRef(null);
  const deckRequestPromiseRef = useRef(null);
  const deckForceNextRef = useRef(false);
  const outlineLocked = outlineBusy || resourceScriptApplying;

  const lastPointsTotalRef = useRef(null);
  const { setProposalOutlineCritical } = useNavGuard();

  // Expose critical state for in-app nav guard (navbar tab switch warning)
  useEffect(() => {
    const critical = autoGenerating || outlineBusy || deckGenerating;
    setProposalOutlineCritical(critical);
    return () => setProposalOutlineCritical(false);
  }, [autoGenerating, outlineBusy, deckGenerating, setProposalOutlineCritical]);

  const refreshPointsAndNotify = useCallback(async (reason) => {
    console.log(`[Points] refreshPointsAndNotify called for: ${reason}`);
    try {
      const response = await api.get('/gamification/points/');
      const total = response?.data?.total_points ?? 0;
      console.log(`[Points] API response - total_points: ${total}`);
      let last = lastPointsTotalRef.current;
      if (last == null && typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem('propulse_last_points');
          if (stored != null) {
            last = parseInt(stored, 10);
            console.log(`[Points] Loaded last from localStorage: ${last}`);
          }
        } catch (e) {
          // ignore storage issues
        }
      }
      if (last == null) {
        last = total;
        console.log(`[Points] No previous value, setting last = total: ${last}`);
      }
      const delta = total - last;
      console.log(`[Points] Calculated delta: ${delta} (total: ${total}, last: ${last})`);
      lastPointsTotalRef.current = total;
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('propulse_last_points', String(total));
        } catch (e) {
          // ignore
        }
        if (delta !== 0) {
          console.log(`[Points] ✅ Dispatching event: ${delta} points (total: ${total}) for: ${reason}`);
          window.dispatchEvent(
            new CustomEvent('propulse_points_update', {
              detail: { delta, total, reason },
            })
          );
        } else {
          console.log(`[Points] ⚠️ No points change (total: ${total}, last: ${last}) for: ${reason} - modal will NOT show`);
        }
      }
    } catch (error) {
      console.error('[Points] ❌ Failed to refresh points:', error);
    }
  }, []);

  // --- Intro Video Modal (YouTube) ---
  // Only show on first visit (check localStorage)
  const [showIntroVideo, setShowIntroVideo] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const hasSeenIntro = localStorage.getItem('propulse_intro_video_seen');
      return !hasSeenIntro; // Show if not seen before
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = () => {
      setShowIntroVideo(true);
    };

    window.addEventListener('propulse_show_intro_video', handler);
    return () => {
      window.removeEventListener('propulse_show_intro_video', handler);
    };
  }, []);

  const closeIntroVideo = () => {
    // Mark as seen in localStorage so it doesn't show again automatically
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('propulse_intro_video_seen', 'true');
      } catch (e) {
        // ignore storage errors
      }
    }
    setShowIntroVideo(false);
  };

  const resetNodeDiffState = () => {
    setNodeDiffs([]);
    setNodeDiffView(false);
    setNodeCurrentDiffIndex(0);
    setNodeMemoryMap(null);
    setNodeOriginalContent('');
    setNodeAiStatus('');
    setNodeAiPlan([]);
  };

  useEffect(() => {
    setDeckSummaries({});
    setDeckGenerating(false);
    setDeckStatus('');
    deckRequestResolveRef.current = null;
    deckRequestRejectRef.current = null;
    deckRequestPromiseRef.current = null;
  }, [planId]);

  const fetchResourceAttachCategory = useCallback(
    async (category, page = 1, options = {}) => {
      const { force = false, showLoader = false, searchQuery = '' } = options;
      if (!category || category === 'all') return;

      const query = (searchQuery || '').trim();
      const existing = resourceAttachCatalogStateRef.current[category];
      // Check if we need to refetch (different page, different search, or force)
      const needsRefetch = force ||
        !existing ||
        existing.page !== page ||
        (existing.searchQuery || '') !== query;

      if (!needsRefetch && (existing.items || []).length) {
        return;
      }

      if (showLoader) {
        setResourceAttachLoadingCategory(category);
      }

      try {
        let response;
        // Use backend search API when there's a search query to search full database
        // Otherwise use paginated fetch for normal browsing
        if (query) {
          response = await apiService.searchCatalogs(query, category, page, RESOURCE_ATTACH_PAGE_SIZE);
        } else {
          response = await apiService.getCatalogItems(category, page, RESOURCE_ATTACH_PAGE_SIZE);
        }

        const payload = {
          items: Array.isArray(response?.items) ? response.items : [],
          total: response?.total || 0,
          page,
          perPage: RESOURCE_ATTACH_PAGE_SIZE,
          totalPages: response?.total_pages || (response?.total ? Math.max(1, Math.ceil(response.total / RESOURCE_ATTACH_PAGE_SIZE)) : 1),
          searchQuery: query, // Store the search query used for this fetch
        };
        setResourceAttachCatalogState((prev) => {
          const next = { ...prev, [category]: payload };
          resourceAttachCatalogStateRef.current = next;
          return next;
        });
      } catch (error) {
        console.error('Failed to load catalog resources for attach modal', error);
      } finally {
        if (showLoader) {
          setResourceAttachLoadingCategory((prev) => (prev === category ? null : prev));
        }
      }
    },
    []
  );

  useEffect(() => {
    resourceAttachCatalogStateRef.current = resourceAttachCatalogState;
  }, [resourceAttachCatalogState]);

  useEffect(() => {
    setResourceAttachModal({ open: false, parentId: null });
    setResourceAttachSearch('');
    setResourceAttachCategory('all');
  }, [planId]);

  useEffect(() => {
    if (!resourceAttachModal.open) return;
    RESOURCE_ATTACH_CATEGORIES.forEach((cat) => {
      fetchResourceAttachCategory(cat, 1);
    });
  }, [resourceAttachModal.open, fetchResourceAttachCategory]);

  useEffect(() => {
    if (!resourceAttachModal.open) return;
    if (resourceAttachCategory === 'all') return;
    fetchResourceAttachCategory(resourceAttachCategory, 1, {
      showLoader: true,
      searchQuery: resourceAttachSearch
    });
  }, [resourceAttachCategory, resourceAttachModal.open, fetchResourceAttachCategory, resourceAttachSearch]);

  useEffect(() => {
    // When search changes, reload the current category to trigger backend search
    if (!resourceAttachModal.open) return;
    if (resourceAttachCategory === 'all') return;
    fetchResourceAttachCategory(resourceAttachCategory, 1, {
      force: true,
      showLoader: true,
      searchQuery: resourceAttachSearch
    });
  }, [resourceAttachSearch, resourceAttachModal.open, resourceAttachCategory, fetchResourceAttachCategory]);

  // Set body, html, and main-content background to match the grey container
  useEffect(() => {
    const originalBodyBg = document.body.style.background;
    const originalHtmlBg = document.documentElement.style.background;
    const originalBodyMargin = document.body.style.margin;
    const mainContent = document.querySelector('.main-content');
    const originalMainContentBg = mainContent ? mainContent.style.background : null;
    const originalMainContentPadding = mainContent ? mainContent.style.padding : null;
    const app = document.querySelector('.app');
    const originalAppBg = app ? app.style.background : null;

    document.body.style.background = dashboardTheme.backgroundGradient;
    document.body.style.margin = '0';
    document.documentElement.style.background = dashboardTheme.backgroundGradient;

    if (mainContent) {
      mainContent.style.background = dashboardTheme.backgroundGradient;
      mainContent.style.padding = '0';
    }

    if (app) {
      app.style.background = dashboardTheme.backgroundGradient;
    }

    return () => {
      document.body.style.background = originalBodyBg;
      document.body.style.margin = originalBodyMargin;
      document.documentElement.style.background = originalHtmlBg;
      if (mainContent) {
        mainContent.style.background = originalMainContentBg;
        mainContent.style.padding = originalMainContentPadding;
      }
      if (app) {
        app.style.background = originalAppBg;
      }
    };
  }, []);

  // Inject style to hide default checkbox checkmark
  useEffect(() => {
    const styleId = 'custom-checkbox-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        input[type="checkbox"] {
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
          background-image: none !important;
        }
        input[type="checkbox"]:checked {
          background-image: none !important;
        }
        input[type="checkbox"]::before,
        input[type="checkbox"]::after {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const styleEl = document.getElementById(styleId);
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, []);

  const selectedNode = useMemo(() => findNodeById(outline, selectedNodeId), [outline, selectedNodeId]);

  useEffect(() => {
    nodeDiffRefs.current = nodeDiffRefs.current.slice(0, nodeDiffs.length);
  }, [nodeDiffs]);

  useEffect(() => {
    if (!nodeDiffView || nodeDiffs.length === 0) return;
    const target = nodeDiffRefs.current[nodeCurrentDiffIndex];
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [nodeCurrentDiffIndex, nodeDiffs.length, nodeDiffView]);

  const getWsUrl = () => {
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
    const wsBase = base.replace(/^https/, 'wss').replace(/^http/, 'ws');

    // Get JWT token from localStorage
    const token = localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN_KEY || 'draftjam_access_token');

    // Append token to query string if available
    if (token) {
      return `${wsBase}/ws/lp?token=${encodeURIComponent(token)}`;
    }

    return `${wsBase}/ws/lp`;
  };

  const closeSocket = () => {
    if (wsRef.current) {
      try {
        wsRef.current.onopen = null;
        wsRef.current.onclose = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
      } catch (error) {
        console.error('Error closing socket', error);
      }
    }
    wsRef.current = null;
  };

  const ensureConnection = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return true;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      return false;
    }
    try {
      setConnecting(true);
      const socket = new WebSocket(getWsUrl());
      wsRef.current = socket;
      socket.onopen = () => {
        setConnecting(false);
      };
      socket.onclose = () => {
        if (!autoGenerating) {
          setConnecting(false);
        }
        if (!reconnectTimerRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            ensureConnection();
          }, 1000);
        }
      };
      socket.onerror = (event) => {
        console.error('WS error', event);
      };
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (!data || typeof data !== 'object') return;
          // Debug log for preliminary_proposal_ready
          if (data.type === 'preliminary_proposal_ready') {
            console.log('LP_WS: Received preliminary_proposal_ready', { 
              hasProposal: !!data.preliminary_proposal, 
              planId: data.plan_id,
              proposalLength: data.preliminary_proposal?.length,
              fullData: data
            });
          }
          switch (data.type) {
            case 'reasoning':
              if (data.content) {
                const cleanedChunk = String(data.content).replace(/\s+/g, ' ');
                setReasoningLog((prev) => {
                  if (prev.length === 0) {
                    return [cleanedChunk];
                  }
                  const next = [...prev];
                  next[next.length - 1] = `${next[next.length - 1]} ${cleanedChunk}`.replace(/\s{2,}/g, ' ');
                  return next;
                });
                queueMicrotask(() => {
                  const el = reasoningContainerRef.current;
                  if (el) {
                    el.scrollTop = el.scrollHeight;
                  }
                });
              }
              break;
            case 'concept_document':
              if (data.content) {
                setConceptDocument(data.content);
                setShowConceptDoc(true); // Auto-show when received
              }
              break;
            case 'missing_child_added':
              if (Array.isArray(data.outline)) {
                const nextOutline = data.outline.map(ensureChildren);
                setOutline(nextOutline);
                setAddMissingChildModal({ open: false, parentId: null });
              }
              break;
            case 'outline_update':
              if (Array.isArray(data.outline)) {
                const nextOutline = data.outline.map(ensureChildren);
                setOutline(nextOutline);
                if (!selectedNodeId && nextOutline.length > 0) {
                  const firstNode = nextOutline[0];
                  selectNode(firstNode.id, firstNode);
                }
              }
              break;
            case 'proposal_node_content':
              // Node content is persisted via REST; we refresh after completion.
              setGeneratingNodeContent(false); // Always reset generating state
              const currentSelectedNodeId = selectedNodeIdRef.current;
              console.log('Received proposal_node_content:', { node_id: data.node_id, currentSelectedNodeId, content_length: data.content?.length });
              if (data.node_id === currentSelectedNodeId) {
                // Set content directly from WebSocket message (backend sends it after saving)
                setNodeContent(data.content || '');
                console.log('Updated node content for selected node');
              } else {
                console.log('Node ID mismatch - not updating content');
              }
              // If generating all content, resolve the promise for this node
              if (generatingAllContentRef.current && data.node_id) {
                const resolver = pendingNodeResolversRef.current.get(data.node_id);
                if (resolver) {
                  resolver();
                  pendingNodeResolversRef.current.delete(data.node_id);
                }
              }
              break;
            case 'proposal_content_update':
              // Stream incremental content updates
              const currentSelectedNodeIdForUpdate = selectedNodeIdRef.current;
              if (data.node_id === currentSelectedNodeIdForUpdate) {
                setNodeContent(data.content || '');
              }
              break;
            case 'progress':
              if (data.stage === 'concept_document') {
                setOutlineBusy(true);
                setProgressMessage(data.message || 'Generating concept document...');
              } else if (data.stage === 'building_node') {
                setOutlineBusy(true);
                setProgressMessage(`Building node ${data.index || 0}: ${data.node_title || '...'}`);
              } else {
                setProgressMessage(
                  `${data.stage || 'working'} ${data.index || 0}/${data.total || 0}`
                );
              }
              break;
            case 'deck_progress':
              setDeckGenerating(true);
              setDeckStatus(
                `Deck ${data.index || 0}/${data.total || 0}: ${data.node_title || 'Working…'}`
              );
              break;
            case 'deck_summary':
              if (data.node_id && data.deck_summary) {
                setDeckSummaries((prev) => ({
                  ...prev,
                  [data.node_id]: data.deck_summary,
                }));
              }
              break;
            case 'preliminary_proposal_ready':
              if (data.preliminary_proposal && data.plan_id) {
                setPreliminaryProposal(data.preliminary_proposal);
                setShowPreliminaryProposal(true); // Auto-show card like concept doc
                setAutoGenerating(false); // Pause generation
                setOutlineBusy(false);
              }
              break;
            case 'preliminary_proposal_refined':
              if (data.refined_proposal) {
                setPreliminaryProposal(data.refined_proposal);
                setIsRefiningProposal(false);
              }
              break;
            case 'deck_complete': {
              setDeckGenerating(false);
              setDeckStatus('');
              let mappedResult = deckSummaries;
              if (Array.isArray(data.summaries)) {
                const mapped = { ...deckSummaries };
                data.summaries.forEach((item) => {
                  if (item?.node_id && item.deck_summary) {
                    mapped[item.node_id] = item.deck_summary;
                  }
                });
                setDeckSummaries(mapped);
                mappedResult = mapped;
              }
              if (deckRequestResolveRef.current) {
                deckRequestResolveRef.current(mappedResult);
                deckRequestResolveRef.current = null;
              }
              deckRequestRejectRef.current = null;
              deckRequestPromiseRef.current = null;
              // Awarded on backend; refresh and show points
              refreshPointsAndNotify('Deck summary generated');
              break;
            }
            case 'deck_error':
              setDeckGenerating(false);
              if (deckRequestRejectRef.current) {
                deckRequestRejectRef.current(new Error(data.error || 'Deck generation failed'));
              } else if (data.error) {
                alert(`Deck generation failed: ${data.error}`);
              }
              deckRequestResolveRef.current = null;
              deckRequestRejectRef.current = null;
              deckRequestPromiseRef.current = null;
              setDeckStatus('');
              break;
            case 'error':
              setAutoGenerating(false);
              setOutlineBusy(false);
              setGeneratingNodeContent(false);
              if (data.error) {
                setReasoningLog((prev) => [...prev, `Error: ${data.error}`]);
              }
              setProgressMessage('');
              break;
            case 'complete':
              setAutoGenerating(false);
              setOutlineBusy(false);
              setProgressMessage('Complete');
              if (planId) {
                loadOutline(planId, { selectFirstNode: false });
              }
              refreshPointsAndNotify('Outline generated');
              break;
            default:
              break;
          }
        } catch (error) {
          console.error('WS message parse error', error);
        }
      };
    } catch (error) {
      console.error('Failed to open WebSocket', error);
      setConnecting(false);
    }
    return false;
  };

  useEffect(() => {
    ensureConnection();
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      closeSocket();
    };
    // eslint-disable-next-line
  }, []);

  // Save planId to localStorage whenever it changes (unless it's the default)
  useEffect(() => {
    if (planId && planId !== DEFAULT_PLAN_ID) {
      try {
        localStorage.setItem('propulse_last_worked_proposal', planId);
      } catch (e) {
        console.warn('Failed to save last worked proposal to localStorage:', e);
      }
    }
  }, [planId]);

  useEffect(() => {
    setSelectedNodeId(null);
    setNodeTitle('');
    setNodeContent('');
    setCustomDescription('');
    setCustomNotes('');
    setResourceId('');
    setResourceType('');
    setNodeType('custom_ingredient');
    if (!planId) {
      setOutline([]);
      return;
    }
    loadOutline(planId, { selectFirstNode: true });
    loadPreliminaryProposal(planId); // Load preliminary proposal if it exists
    // eslint-disable-next-line
  }, [planId]);

  // Handle URL parameter changes
  useEffect(() => {
    if (decodedUrlPlanId && decodedUrlPlanId !== planId) {
      setPlanId(decodedUrlPlanId);
      setPlanIdInput(decodedUrlPlanId);
    } else if (!decodedUrlPlanId && planId === DEFAULT_PLAN_ID) {
      // If no URL parameter and we're on default, check if we should load last worked proposal
      try {
        const lastProposal = localStorage.getItem('propulse_last_worked_proposal');
        if (lastProposal && lastProposal !== DEFAULT_PLAN_ID) {
          // Load last worked proposal without redirect to avoid breaking navigation
          setPlanId(lastProposal);
          setPlanIdInput(lastProposal);
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [decodedUrlPlanId, planId]);

  useEffect(() => {
    // Reset generating state when selected node changes
    setGeneratingNodeContent(false);
    // Update ref with latest selectedNodeId
    selectedNodeIdRef.current = selectedNodeId;
  }, [selectedNodeId]);

  const loadOutline = async (targetPlanId, options = { selectFirstNode: true }) => {
    if (!targetPlanId) return;
    try {
      setLoadingOutline(true);
      const data = await apiService.getProposalOutline(targetPlanId);
      const nextOutline = Array.isArray(data?.outline) ? data.outline.map(ensureChildren) : [];
      setOutline(nextOutline);
      setConceptDocument(data?.concept_document || '');
      setClientName(data?.client_name || '');
      const resourcesPayload = data?.selected_resources || {};
      setSelectedResourcesData({
        selected_resources: resourcesPayload.selected_resources || {},
        placement_strategy: resourcesPayload.placement_strategy || {},
      });
      if (options.selectFirstNode && nextOutline.length > 0) {
        const firstNode = nextOutline[0];
        selectNode(firstNode.id, firstNode);
      }
    } catch (error) {
      console.error('Failed to load proposal outline', error);
      setOutline([]);
      setConceptDocument('');
      setClientName('');
      setSelectedResourcesData({ selected_resources: {}, placement_strategy: {} });
      // Clear deck summaries when outline fails to load
      setDeckSummaries({});
      setDeckGenerating(false);
      setDeckStatus('');
      deckRequestResolveRef.current = null;
      deckRequestRejectRef.current = null;
      deckRequestPromiseRef.current = null;
    } finally {
      setLoadingOutline(false);
    }
  };


  // Load preliminary proposal if it exists (from export artifacts)
  const loadPreliminaryProposal = async (targetPlanId) => {
    if (!targetPlanId) return;
    try {
      const data = await apiService.getPreliminaryProposal(targetPlanId);
      if (data?.success && data?.preliminary_proposal?.trim()) {
        setPreliminaryProposal(data.preliminary_proposal);
        setShowPreliminaryProposal(true);
      } else if (preliminaryProposal && preliminaryProposal.trim()) {
        setShowPreliminaryProposal(true);
      }
    } catch (error) {
      console.error('Error loading preliminary proposal:', error);
    }
  };

  // --- Lesson Plan (LP) helpers ---

  const generateProgramOverview = (outline) => {
    if (!outline || outline.length === 0) return '';

    // Filter out title, problem_statement, and commercials
    const programNodes = outline.filter(node => {
      const type = node.type || '';
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      return type !== 'title' && type !== 'problem_statement' && type !== 'commercials' && hasChildren;
    });

    if (programNodes.length === 0) return '';

    const formatDuration = (minutes) => {
      if (!minutes) return '';
      if (minutes < 60) return `${minutes} min`;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    };

    const formatTopicDuration = (node) => {
      // First, check for day/week/month indicators in title or custom_description
      const title = (node.title || '').toLowerCase();
      const desc = (node.custom_description || '').toLowerCase();
      const text = title + ' ' + desc;

      if (text.includes('day') || text.includes('week') || text.includes('month')) {
        // Try to extract number
        const dayMatch = text.match(/(\d+)\s*day/i);
        const weekMatch = text.match(/(\d+)\s*week/i);
        const monthMatch = text.match(/(\d+)\s*month/i);

        if (monthMatch) return `${monthMatch[1]} Month${monthMatch[1] !== '1' ? 's' : ''}`;
        if (weekMatch) return `${weekMatch[1]} Week${weekMatch[1] !== '1' ? 's' : ''}`;
        if (dayMatch) return `${dayMatch[1]} Day${dayMatch[1] !== '1' ? 's' : ''}`;
      }

      // Second, calculate from children durations
      const children = node.children || [];
      if (children.length > 0) {
        const totalMinutes = children.reduce((sum, child) => {
          return sum + (child.time_minutes || 0);
        }, 0);
        if (totalMinutes > 0) {
          // Convert to days/weeks if significant
          const totalHours = totalMinutes / 60;
          if (totalHours >= 24) {
            const days = Math.round(totalHours / 8); // Assuming 8-hour work day
            return `${days} Day${days !== '1' ? 's' : ''}`;
          } else if (totalHours >= 40) {
            const weeks = Math.round(totalHours / 40); // Assuming 40-hour work week
            return `${weeks} Week${weeks !== '1' ? 's' : ''}`;
          } else {
            return formatDuration(totalMinutes);
          }
        }
      }

      // Third, fallback to node's own time_minutes if available
      if (node.time_minutes) {
        return formatDuration(node.time_minutes);
      }

      return '';
    };

    // Build tree structure with numbering
    const buildTreeNode = (node, depth = 0, isLast = false, moduleNum = null) => {
      const children = Array.isArray(node.children) ? node.children : [];
      if (children.length === 0) return '';

      const indent = '  '.repeat(depth);
      // Use ASCII tree chars to avoid UTF-8 encoding issues in PPTX
      const connector = depth === 0 ? '' : (isLast ? '\\- ' : '+- ');
      const title = node.title || 'Untitled';
      const nodeType = (node.type || '').toLowerCase();
      const titleLower = title.toLowerCase();
      const duration = formatTopicDuration(node);
      const durationText = duration ? ` (${duration})` : '';

      // Add temporal tag if applicable
      const temporalTag = getTemporalTag(node);
      const tagText = temporalTag ? ` ${temporalTag}` : '';

      // Add module number prefix for parent nodes (not temporal)
      let numberedTitle = title;
      if (depth === 0 && moduleNum !== null && !temporalTag) {
        // Avoid "Module 1: Module 1: ..." if title already has it
        const alreadyHasModule = /^Module\s+\d+:\s+/i.test(title.trim());
        numberedTitle = alreadyHasModule ? title : `Module ${moduleNum}: ${title}`;
      }

      let treeText = `${indent}${connector}**${numberedTitle}**${tagText}${durationText}\n`;

      if (children.length > 0) {
        // Group children by type for better organization
        const byType = {};
        children.forEach((child, childIdx) => {
          const childType = child.type || 'other';
          if (!byType[childType]) byType[childType] = [];
          const childTemporalTag = getTemporalTag(child);
          const childNum = moduleNum !== null ? `${moduleNum}.${childIdx + 1}` : null;
          byType[childType].push({
            title: child.title || child.id,
            duration: child.time_minutes ? formatDuration(child.time_minutes) : '',
            type: childType,
            temporalTag: childTemporalTag,
            number: childNum
          });
        });

        // Build children tree
        const typeKeys = Object.keys(byType);
        typeKeys.forEach((typeKey, typeIdx) => {
          const items = byType[typeKey];
          const isLastType = typeIdx === typeKeys.length - 1;
          const typeLabel = typeKey.charAt(0).toUpperCase() + typeKey.slice(1).replace(/_/g, ' ');

          // Type header (if multiple types)
          if (typeKeys.length > 1) {
            const typeConnector = isLastType ? '\\- ' : '+- ';
            treeText += `${indent}${depth === 0 ? '|  ' : '   '}${typeConnector}*${typeLabel}*\n`;
          }

          // Items under this type
          items.forEach((item, itemIdx) => {
            const isLastItem = itemIdx === items.length - 1 && isLastType;
            const itemConnector = isLastItem ? '\\- ' : '+- ';
            const itemIndent = typeKeys.length > 1 ? `${indent}${depth === 0 ? '|  ' : '   '}   ` : `${indent}${depth === 0 ? '|  ' : '   '}`;
            const itemDuration = item.duration ? ` (${item.duration})` : '';
            const itemTag = item.temporalTag ? ` ${item.temporalTag}` : '';
            const itemNumber = item.number ? `${item.number} ` : '';
            treeText += `${itemIndent}${itemConnector}${itemNumber}${item.title}${itemTag}${itemDuration}\n`;
          });
        });
      }

      return treeText;
    };

    // Build the tree
    let overview = '## Program Overview\n\n';
    overview += '```\n';

    programNodes.forEach((node, idx) => {
      const isLast = idx === programNodes.length - 1;
      const moduleNum = idx + 1; // Module numbers start from 1
      const nodeTree = buildTreeNode(node, 0, isLast, moduleNum);
      if (nodeTree) {
        overview += nodeTree;
      }
    });

    overview += '```\n\n';
    return overview;
  };

  const getProgramOverviewTreeText = () => {
    const overviewMarkdown = generateProgramOverview(outline);
    if (!overviewMarkdown) return '';
    return overviewMarkdown
      .replace(/^##\s*Program Overview\s*/i, '')
      .replace(/```/g, '')
      .trim();
  };

  const isClientBriefNode = (node) => {
    if (!node) return false;
    const type = (node.type || '').toLowerCase();
    const titleLower = (node.title || '').toLowerCase();
    return type === 'problem_statement' || titleLower.includes('client brief');
  };

  const addProgramOverviewSlide = (pptxInstance, treeText, options = {}) => {
    if (!pptxInstance || !treeText) return false;
    const slide = pptxInstance.addSlide();
    const slideWidth = options.slideWidth || 10;
    const slideHeight = options.slideHeight || 5.625;
    const marginX = options.marginX ?? 0.5;
    const marginY = options.marginY ?? 0.4;
    const contentWidth = slideWidth - marginX * 2;
    let currentY = marginY;
    slide.addText('Program Overview', {
      x: marginX,
      y: currentY,
      w: contentWidth,
      h: 0.9,
      fontSize: 24,
      bold: true,
      color: '363636',
      fontFace: 'Arial',
      valign: 'top',
      shrinkText: true,
    });
    currentY += 1.0;
    const availableHeight = slideHeight - currentY - 0.2;
    if (availableHeight > 0) {
      slide.addText(treeText, {
        x: marginX,
        y: currentY,
        w: contentWidth,
        h: availableHeight,
        fontSize: 12,
        fontFace: 'Courier New',
        color: '363636',
        lineSpacing: 18,
        valign: 'top',
        shrinkText: true,
      });
    }
    return true;
  };

  const compileFullDocument = async () => {
    if (fullDocumentOverride) {
      return fullDocumentOverride.trim();
    }
    if (!planId || outline.length === 0) return '';

    let fullDoc = '';

    let programOverviewInserted = false;

    // Compile outline with content
    const compileNode = async (node, depth = 0, parentPath = []) => {
      const indent = '  '.repeat(depth);
      const nodeType = node.type || '';
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      const isParentNode = hasChildren;
      const isTopicNode = nodeType === 'custom_ingredient';
      const isChildLeaf = !isParentNode && parentPath.length > 0;
      const parentNode = parentPath.length ? parentPath[parentPath.length - 1] : null;
      const parentChildren = parentNode?.children || [];
      const isLastSibling =
        parentNode && parentChildren.length
          ? parentChildren[parentChildren.length - 1]?.id === node.id
          : false;
      const skipChildSeparator =
        isChildLeaf && parentNode?.type === 'custom_ingredient' && isLastSibling;

      const titleLower = (node.title || '').toLowerCase();
      const isClientBriefNode = nodeType === 'problem_statement' || titleLower.includes('client brief');

      // Build title with hierarchical path and numbering
      let nodeTitle = node.title || 'Untitled';

      // Check if this is a parent module node
      const isTemporal = getTemporalTag(node) !== null;
      const isModuleNode = isParentNode && !isTemporal && nodeType !== 'title' && nodeType !== 'problem_statement' && nodeType !== 'commercials';

      if (isModuleNode) {
        // For parent module nodes, show "Module X: Title"
        const moduleNum = getModuleNumber(node, outline);
        if (moduleNum) {
          nodeTitle = `Module ${moduleNum}: ${nodeTitle}`;
        }
      } else if (!isParentNode && depth > 0) {
        // For child nodes, add hierarchical path prefix with numbering
        const hierarchicalPath = buildHierarchicalPath(node, outline);
        if (hierarchicalPath) {
          nodeTitle = hierarchicalPath + nodeTitle;
        }
      }

      fullDoc += `${indent}# ${nodeTitle}\n\n`;

      // Fetch node content if available, otherwise use custom_description
      let nodeContent = '';
      if (planId && node.id) {
        try {
          const data = await apiService.getProposalNode(planId, node.id);
          if (data?.content && data.content.trim()) {
            nodeContent = data.content.trim();
          }
        } catch (error) {
          console.error(`Failed to load content for node ${node.id}:`, error);
        }
      }

      // Use fetched content if available, otherwise fall back to custom_description
      let wroteLeafContent = false;
      if (!isParentNode) {
        if (nodeContent) {
          // Temporarily omit parent node content from exports
          fullDoc += `${indent}${nodeContent}\n\n`;
          wroteLeafContent = true;
        } else if (node.custom_description) {
          // Temporarily omit parent node content from exports
          fullDoc += `${indent}${node.custom_description}\n\n`;
          wroteLeafContent = true;
        }
      }

      if (wroteLeafContent && isChildLeaf && !skipChildSeparator) {
        fullDoc += `${FULL_WIDTH_CHILD_SEPARATOR}\n\n`;
      }

      // Insert Program Overview after problem statement content
      if (!programOverviewInserted && isClientBriefNode) {
        const overviewMarkdown = generateProgramOverview(outline);
        if (overviewMarkdown.trim()) {
          fullDoc += '\n' + overviewMarkdown + '\n\n';
          programOverviewInserted = true;
        }
      }

      // Recursively compile children
      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          await compileNode(child, depth + 1, [...parentPath, node]);
        }
      }

      if (isParentNode && isTopicNode) {
        fullDoc += `${FULL_WIDTH_TOPIC_SEPARATOR}\n\n`;
      }

      fullDoc += '\n';
    };

    for (const node of outline) {
      await compileNode(node);
    }

    // If problem statement wasn't found, insert overview at the beginning
    if (!programOverviewInserted) {
      const overviewMarkdown = generateProgramOverview(outline);
      if (overviewMarkdown.trim()) {
        fullDoc = overviewMarkdown + '\n\n' + fullDoc;
      }
    }

    return fullDoc.trim();
  };

  const compileDeckDocument = (summaryMap = deckSummaries) => {
    const nodes = flattenNodes(outline);
    if (!nodes.length) return '';

    const sections = [];
    const overviewMarkdown = generateProgramOverview(outline);
    let overviewInserted = false;
    const parentMap = new Map();

    const buildParentMap = (items, parent = null) => {
      (items || []).forEach((item) => {
        if (parent && item?.id) {
          parentMap.set(item.id, parent);
        }
        if (item.children && item.children.length > 0) {
          buildParentMap(item.children, item);
        }
      });
    };

    buildParentMap(outline);
    let lastTopicId = null;

    nodes.forEach((node) => {
      // Skip parent nodes (nodes with children) - only include leaf nodes
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      if (hasChildren) return;

      const summary = summaryMap[node.id];
      if (!summary) return;

      const parentNode = node.id ? parentMap.get(node.id) : null;
      const parentType = (parentNode?.type || '').toLowerCase();
      const currentTopicId = parentType === 'custom_ingredient' ? parentNode.id : null;
      const parentChildren = parentNode?.children || [];
      const isLastChild =
        parentChildren.length > 0
          ? parentChildren[parentChildren.length - 1]?.id === node.id
          : false;
      const skipChildSeparator = currentTopicId && isLastChild;

      if (lastTopicId && currentTopicId !== lastTopicId) {
        sections.push(FULL_WIDTH_TOPIC_SEPARATOR, '');
      }

      // Build title with hierarchical path prefix
      let slideTitle = summary.slide_title || node.title || 'Slide';

      // Since we've already filtered out nodes with children (line 1945), 
      // this is always a child/leaf node, so use hierarchical path with numbering
      const hierarchicalPath = buildHierarchicalPath(node, outline);
      if (hierarchicalPath) {
        slideTitle = hierarchicalPath + slideTitle;
      }

      sections.push(`## ${slideTitle}`);
      if (summary.subtitle) {
        sections.push(`_${summary.subtitle}_`);
      }
      (summary.bullets || []).forEach((bullet) => {
        if (bullet) sections.push(`- ${bullet}`);
      });
      if (skipChildSeparator) {
        sections.push('');
      } else {
        sections.push('', FULL_WIDTH_CHILD_SEPARATOR, '');
      }

      if (
        !overviewInserted &&
        overviewMarkdown &&
        (node.type === 'problem_statement' || (node.title || '').toLowerCase().includes('client brief'))
      ) {
        sections.push(overviewMarkdown.trim(), '');
        overviewInserted = true;
      }

      lastTopicId = currentTopicId || null;
    });

    if (!overviewInserted && overviewMarkdown) {
      sections.unshift('', overviewMarkdown.trim());
    }

    if (lastTopicId) {
      sections.push(FULL_WIDTH_TOPIC_SEPARATOR);
    }

    return sections.join('\n').trim();
  };

  const buildHtmlPartsFromDocument = (docText) => {
    const segments = parseMarkdownTables(docText);
    return segments.map((seg) => {
      if (seg.type === 'html') return seg.content;
      let content = escapeHtml(seg.content);
      if (!content.trim().startsWith('```')) {
        content = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      }
      content = content.replace(/^(#+)\s*(.+)$/gm, (match, hashes, text) => {
        const level = hashes.length;
        const tag = `h${Math.min(level, 6)}`;
        const fontSize = 24 - (level - 1) * 2;
        return `<${tag} style="font-size: ${fontSize}px; font-weight: 600; margin: 16px 0 8px 0; color: #1e293b;">${text.trim()}</${tag}>`;
      });
      content = content.replace(/\n/g, '<br>');
      return `<div style="white-space: pre-wrap; word-wrap: break-word; font-family: 'Times New Roman', serif; margin: 8px 0; color: #1e293b; line-height: 1.6;">${content}</div>`;
    });
  };

  const openDocumentAsPdf = (htmlParts, titleText) => {
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <title>${escapeHtml(titleText)}</title>
          <style>
            @media print {
              @page { margin: 2cm; }
            }
            body {
              font-family: 'Times New Roman', serif;
              padding: 40px;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
            }
            pre {
              white-space: pre-wrap;
              word-wrap: break-word;
              font-family: 'Courier New', monospace;
              margin: 16px 0;
              padding: 16px;
              background: transparent;
              border: none;
              font-size: 13px;
              line-height: 1.6;
              color: #000000;
              overflow-x: auto;
            }
            code {
              font-family: 'Courier New', monospace;
              color: #000000;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 16px 0;
              border: 1px solid #333;
              page-break-inside: avoid;
            }
            th {
              border: 1px solid #333;
              padding: 10px 12px;
              background: #f0f0f0;
              font-weight: 600;
              text-align: left;
              color: #1e293b;
            }
            td {
              border: 1px solid #333;
              padding: 10px 12px;
              color: #1e293b;
              vertical-align: top;
            }
            td ul {
              margin: 0;
              padding-left: 20px;
              color: #1e293b;
            }
            td li {
              margin: 4px 0;
              color: #1e293b;
            }
            tr:nth-child(even) { background: #f9f9f9; }
          </style>
        </head>
        <body>
          ${htmlParts.join('')}
        </body>
      </html>
    `;

    // Create a Blob with the HTML content
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);

    // Open the Blob URL in a new window - this is less likely to be blocked
    const printWindow = window.open(blobUrl, '_blank');

    if (!printWindow) {
      // Fallback: If pop-up is still blocked, try to download the file instead
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${titleText.replace(/[^a-z0-9]/gi, '_')}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Clean up the blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      throw new Error('Unable to open PDF window. The file has been downloaded instead. Please open it and use Print to PDF.');
    }

    // Wait for the window to load, then trigger print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        // Clean up the blob URL after printing
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }, 250);
    };

    // Fallback: if onload doesn't fire, try after a delay
    setTimeout(() => {
      if (printWindow && !printWindow.closed) {
        try {
          printWindow.print();
        } catch (e) {
          console.warn('Could not trigger print:', e);
        }
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
    }, 1000);
  };

  const renderDocumentAsPdf = (docText, titleText) => {
    try {
      const htmlParts = buildHtmlPartsFromDocument(docText);
      openDocumentAsPdf(htmlParts, titleText);
    } catch (error) {
      console.error('Error rendering PDF:', error);
      // Show user-friendly error message
      alert(error.message || 'Failed to generate PDF. Please check your browser settings and allow pop-ups for this site, or try downloading the file instead.');
      throw error; // Re-throw so calling code can handle it
    }
  };

  const requestDeckSummaries = ({ force = false } = {}) => {
    if (!planId) {
      return Promise.reject(new Error('Plan ID is required.'));
    }
    const shouldForce = force || deckForceNextRef.current;
    if (!shouldForce && Object.keys(deckSummaries).length && !deckGenerating) {
      return Promise.resolve(deckSummaries);
    }
    if (deckGenerating && deckRequestPromiseRef.current) {
      return deckRequestPromiseRef.current;
    }

    const promise = new Promise((resolve, reject) => {
      deckRequestResolveRef.current = resolve;
      deckRequestRejectRef.current = reject;

      const sendCommand = () => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          try {
            setDeckGenerating(true);
            setDeckStatus('Starting deck generation…');
            wsRef.current.send(
              JSON.stringify({
                command: 'generate_deck_summary',
                plan_id: planId,
                force: shouldForce,
              })
            );
            deckForceNextRef.current = false;
          } catch (error) {
            reject(error);
            deckRequestResolveRef.current = null;
            deckRequestRejectRef.current = null;
            deckRequestPromiseRef.current = null;
            setDeckGenerating(false);
            setDeckStatus('');
          }
        } else {
          setTimeout(sendCommand, 250);
        }
      };

      ensureConnection();
      sendCommand();
    });

    deckRequestPromiseRef.current = promise;
    return promise;
  };

  const mapResourceCategory = (value = '') => {
    const normalized = String(value || '').toLowerCase();
    if (!normalized) return '';
    if (normalized.includes('game') || normalized.includes('activity')) return 'games';
    if (normalized.includes('framework')) return 'frameworks';
    if (normalized.includes('tool')) return 'tools';
    if (normalized.includes('psychometric')) return 'psychometrics';
    return '';
  };

  const mapCategoryToNodeType = (category = '') => {
    const normalized = String(category || '').toLowerCase();
    if (normalized.includes('game')) return 'game';
    if (normalized.includes('framework')) return 'framework';
    if (normalized.includes('psychometric')) return 'training';
    if (normalized.includes('tool')) return 'activity';
    return 'custom_ingredient';
  };

  const mapCategoryToResourceType = (category = '') => {
    const normalized = String(category || '').toLowerCase();
    if (normalized.includes('game')) return 'game';
    if (normalized.includes('framework')) return 'framework';
    if (normalized.includes('tool')) return 'tool';
    if (normalized.includes('psychometric')) return 'psychometric';
    return '';
  };

  const normalizeResourceCategory = (value = '') => {
    const normalized = String(value || '').toLowerCase();
    if (normalized.startsWith('game')) return 'games';
    if (normalized.startsWith('framework')) return 'frameworks';
    if (normalized.startsWith('tool')) return 'tools';
    if (normalized.startsWith('psychometric')) return 'psychometrics';
    return value || '';
  };



  const attachCatalogResources = useMemo(() => {
    const registry = new Map();

    const register = (category, resource) => {
      if (!resource) return;
      const normalizedCategory = normalizeResourceCategory(category);
      if (!normalizedCategory) return;
      const raw = resource.raw && typeof resource.raw === 'object' ? resource.raw : resource;
      const id =
        resource.id ||
        raw.id ||
        raw._id ||
        raw.uuid ||
        raw.resource_id ||
        raw.name ||
        raw.title;
      if (!id) return;
      const key = `${normalizedCategory}::${id}`;
      const existing = registry.get(key) || {};
      registry.set(key, {
        category: normalizedCategory,
        id: String(id),
        name: resource.name || raw.name || raw.title || existing.name || String(id),
        description:
          resource.description ||
          existing.description ||
          raw.detailed_explanation ||
          raw.description ||
          raw.summary ||
          raw.what ||
          '',
        reasoning: resource.reasoning || existing.reasoning || '',
        placement: resource.placement || existing.placement || raw.placement_hint || '',
        raw,
      });
    };

    RESOURCE_ATTACH_CATEGORIES.forEach((category) => {
      const state = resourceAttachCatalogState[category];
      (state?.items || []).forEach((item) => register(category, item));
    });

    const selected = selectedResourcesData?.selected_resources || {};
    Object.entries(selected).forEach(([category, resources]) => {
      (resources || []).forEach((res) => register(category, res));
    });

    Object.entries(RESOURCE_CATEGORY_MAP).forEach(([category, ctxKey]) => {
      (attachedContext?.[ctxKey] || []).forEach((entry) => register(category, entry));
    });

    return Array.from(registry.values());
  }, [resourceAttachCatalogState, selectedResourcesData, attachedContext]);

  const hasCatalogResources = attachCatalogResources.length > 0;

  const filteredAttachResources = useMemo(() => {
    const query = resourceAttachSearch.trim().toLowerCase();
    // When a specific category is selected and there's a search query, 
    // the backend search already filtered the results, so we only need to filter by category
    // When category is 'all', we need client-side filtering for both category and search
    const categoryState = resourceAttachCategory !== 'all' ? resourceAttachCatalogState[resourceAttachCategory] : null;
    const isBackendSearch = query && categoryState && categoryState.searchQuery === query;

    return attachCatalogResources.filter((item) => {
      const matchCategory = resourceAttachCategory === 'all' || item.category === resourceAttachCategory;
      // Skip client-side search filtering if backend search was used
      if (isBackendSearch) {
        return matchCategory;
      }
      // Otherwise, do client-side filtering
      const haystack = `${item.name || ''} ${item.description || ''} ${item.reasoning || ''} ${item.id || ''}`.toLowerCase();
      const matchSearch = !query || haystack.includes(query);
      return matchCategory && matchSearch;
    });
  }, [attachCatalogResources, resourceAttachCategory, resourceAttachSearch, resourceAttachCatalogState]);

  const resourceAttachPagination = resourceAttachCategory === 'all' ? null : resourceAttachCatalogState[resourceAttachCategory];
  const resourceAttachLoading = resourceAttachLoadingCategory
    ? resourceAttachCategory === 'all' || resourceAttachLoadingCategory === resourceAttachCategory
    : false;

  const handleResourceAttachPageChange = (direction) => {
    if (resourceAttachCategory === 'all') return;
    const current = resourceAttachCatalogStateRef.current[resourceAttachCategory];
    if (!current) return;
    let nextPage = current.page || 1;
    if (direction === 'prev') {
      nextPage = Math.max(1, (current.page || 1) - 1);
    } else if (direction === 'next') {
      nextPage = Math.min(current.totalPages || 1, (current.page || 1) + 1);
    } else if (typeof direction === 'number') {
      nextPage = Math.min(current.totalPages || 1, Math.max(1, direction));
    }
    if (nextPage === current.page) return;
    fetchResourceAttachCategory(resourceAttachCategory, nextPage, {
      force: true,
      showLoader: true,
      searchQuery: resourceAttachSearch
    });
  };

  const logResourceStats = (nodes = []) => {
    if (!Array.isArray(nodes) || !nodes.length) {
      console.info('Resource assignments summary: no nodes provided');
      return;
    }
    const summary = { total: 0, withResources: 0, missingResources: 0, nodeDetails: [] };
    nodes.forEach((node) => {
      if (!node) return;
      summary.total += 1;
      const refs = buildResourceRefsFromNode(node);
      if (refs.length) {
        summary.withResources += 1;
        summary.nodeDetails.push({
          id: node.id,
          title: node.title,
          type: node.type,
          resourceRefs: refs,
        });
      } else {
        summary.missingResources += 1;
      }
    });
    try {
      console.groupCollapsed('Resource assignments summary');
      console.table(summary.nodeDetails);
      console.log(summary);
      console.groupEnd();
    } catch (err) {
      console.log('Resource assignments summary', summary);
    }
  };

  const composeResourceContent = (item = {}) => {
    if (!item || typeof item !== 'object') return '';
    const lines = [];
    const normalized = { ...item };
    const handledKeys = new Set();

    const ignoredKeys = new Set([
      'id',
      'name',
      'aliases',
      'alias',
      'category',
      'occurrences',
      'resource_id',
      'resource_ids',
      'resource_type',
      'resource_types',
      'created_at',
      'updated_at',
    ]);

    const toTitleCase = (key = '') =>
      key
        .split('_')
        .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
        .join(' ')
        .replace(/\bId\b/gi, 'ID')
        .trim();

    const appendLine = (label, value, key) => {
      if (value == null || value === '') return;
      if (key) handledKeys.add(key);
      if (typeof value === 'string') {
        const text = value.trim();
        if (!text) return;
        lines.push(label ? `${label}: ${text}` : text);
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        lines.push(label ? `${label}: ${value}` : String(value));
      } else if (Array.isArray(value)) {
        const entries = value.map((entry) => (entry == null ? '' : String(entry).trim())).filter(Boolean);
        if (!entries.length) return;
        if (label) {
          lines.push(`${label}:`);
        }
        entries.forEach((entry) => lines.push(`- ${entry}`));
      } else if (typeof value === 'object') {
        const serialized = JSON.stringify(value, null, 2);
        if (!serialized) return;
        if (label) {
          lines.push(`${label}:`);
        }
        lines.push(serialized);
      }
    };

    const appendField = (key, label) => appendLine(label || toTitleCase(key), normalized[key], key);

    const preferredOrder = [
      'what',
      'why',
      'objective',
      'purpose',
      'summary',
      'outcomes',
      'results',
      'benefits',
      'duration',
      'participants',
      'how_to',
      'steps',
      'process',
      'conduct',
      'flow',
      'agenda',
      'problem_solved',
      'pain_points',
      'context',
      'industry',
      'usage_context',
      'applications',
      'components',
      'pillars',
      'phases',
      'modules',
      'rules',
      'constraints',
      'tips',
      'notes',
      'materials',
      'tools_required',
      'support_needed',
      'debrief_points',
      'evaluation',
      'success_metrics',
      'example_instructions',
      'case_study',
      'best_practices',
      'detailed_explanation',
    ];

    preferredOrder.forEach((field) => {
      if (field in normalized) {
        appendField(field);
      }
    });

    Object.keys(normalized).forEach((key) => {
      if (handledKeys.has(key) || ignoredKeys.has(key)) return;
      appendField(key);
    });

    return lines.join('\n').trim();
  };

  const isLeafNode = (node) => {
    if (!node) return false;
    return !(Array.isArray(node.children) && node.children.length > 0);
  };

  const buildResourceRefsFromNode = (node) => {
    if (!node || !isLeafNode(node)) return [];
    const ids = node.resource_ids || [];
    if (!ids.length) {
      const legacyId = node.resource_id;
      const legacyType = node.resource_type;
      return legacyId ? [{ id: legacyId, type: legacyType || '' }] : [];
    }
    const types = node.resource_types || [];
    return ids
      .map((id, index) => ({
        id,
        type: types[index] || node.resource_type || '',
      }))
      .filter((ref) => !!ref.id);
  };

  const fetchResourceContentByRef = async (ref) => {
    if (!ref || !ref.id) return null;
    const category = mapResourceCategory(ref.type);
    if (!category) return null;
    const cacheKey = `${category}:${ref.id}`;
    if (resourceContentCacheRef.current[cacheKey]) {
      return resourceContentCacheRef.current[cacheKey];
    }
    try {
      const response = await apiService.getCatalogItem(category, ref.id);
      const item = response?.item;
      if (!item) return null;
      const content = composeResourceContent(item);
      if (!content) return null;
      const payload = {
        id: ref.id,
        name: item.name || ref.id,
        category,
        content,
      };
      resourceContentCacheRef.current[cacheKey] = payload;
      return payload;
    } catch (error) {
      console.error(`Failed to load catalog item ${ref.id}:`, error);
      return null;
    }
  };

  const buildResourceScriptFromNode = async (node) => {
    const refs = buildResourceRefsFromNode(node);
    if (!refs.length) return '';
    const sections = [];
    for (const ref of refs) {
      const resource = await fetchResourceContentByRef(ref);
      if (resource?.content) {
        sections.push(`### ${resource.name}\n${resource.content}`);
      }
    }
    return sections.join('\n\n').trim();
  };

  const applyResourceScriptToNode = async (nodeId, node, script) => {
    if (!planId || !nodeId) return;
    const metadataPayload = {
      type: node?.type || '',
      resourceType: node?.resource_type || '',
      resourceId: node?.resource_id || '',
      customDescription: node?.custom_description || '',
      customNotes: node?.custom_notes || '',
      resourceIds: node?.resource_ids || [],
      resourceTypes: node?.resource_types || [],
    };
    const resourceRefs = buildResourceRefsFromNode(node);
    await apiService.saveProposalNode(planId, nodeId, {
      content: script || '',
      metadata: metadataPayload,
      resource_refs: resourceRefs,
    });
    if (selectedNodeId === nodeId) {
      setNodeContent(script || '');
    }
  };

  const tryApplyResourceScriptForNode = async (node, force = false) => {
    if (
      !planId ||
      (!useResourceContentDirectly && !force) ||
      !node ||
      !node.id ||
      !isLeafNode(node)
    ) {
      return false;
    }
    try {
      const resourceScript = await buildResourceScriptFromNode(node);
      if (!resourceScript) return false;
      await applyResourceScriptToNode(node.id, node, resourceScript);
      return true;
    } catch (error) {
      console.error('Failed to apply resource script', error);
      return false;
    }
  };

  const applyResourceScriptsAcrossOutline = async () => {
    if (resourceScriptApplying) return;
    if (!planId || !outline.length) {
      setResourceScriptStatus('Load an outline before applying resource scripts.');
      setTimeout(() => setResourceScriptStatus(''), 5000);
      return;
    }
    if (generatingAllContent || generatingNodeContent || autoGenerating) {
      setResourceScriptStatus('Please wait for ongoing generation to finish before inserting resource scripts.');
      setTimeout(() => setResourceScriptStatus(''), 6000);
      return;
    }
    resourceContentCacheRef.current = {};
    setResourceScriptApplying(true);
    setResourceScriptStatus('Applying resource scripts to existing sections…');
    try {
      const nodes = flattenNodes(outline);
      logResourceStats(nodes);
      let appliedCount = 0;
      for (const node of nodes) {
        if (!node || !node.id) continue;
        const refs = buildResourceRefsFromNode(node);
        if (!refs.length) continue;
        const applied = await tryApplyResourceScriptForNode(node, true);
        if (applied) {
          appliedCount += 1;
        }
      }
      setResourceScriptStatus(
        appliedCount
          ? `Inserted resource scripts into ${appliedCount} section${appliedCount === 1 ? '' : 's'}.`
          : 'No resource-backed sections required updates.'
      );
    } catch (error) {
      console.error('Failed to apply resource scripts across outline', error);
      setResourceScriptStatus('Could not apply resource scripts automatically. Please try again.');
    } finally {
      setTimeout(() => setResourceScriptStatus(''), 6500);
      setResourceScriptApplying(false);
    }
  };

  const handleResourceContentToggle = (event) => {
    const checked = event.target.checked;
    setUseResourceContentDirectly(checked);
    if (checked) {
      applyResourceScriptsAcrossOutline();
    } else {
      const nodes = flattenNodes(outline);
      logResourceStats(nodes);
      setResourceScriptStatus('');
    }
  };

  const handleDownloadTXT = async () => {
    try {
      const fullDoc = await compileFullDocument();
      if (!fullDoc) {
        alert('No content to download. Please generate an outline first.');
        return;
      }

      const sanitized = removeMarkdownBold(fullDoc);
      const blob = new Blob([sanitized], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${planId || 'proposal'}_full_document.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download TXT:', error);
      alert('Failed to download TXT file: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // Show progress modal
      setExportProgress({ show: true, type: 'pdf', current: 0, total: 100, message: 'Compiling full document...' });

      const fullDoc = await compileFullDocument();
      if (!fullDoc) {
        setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
        alert('No content to download. Please generate an outline first.');
        return;
      }

      setExportProgress({ show: true, type: 'pdf', current: 90, total: 100, message: 'Generating PDF...' });

      // Small delay to show progress, then generate PDF
      setTimeout(() => {
        renderDocumentAsPdf(fullDoc, `Proposal Document - ${planId || 'Proposal'}`);
        setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
      }, 300);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
      alert('Failed to download PDF file: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDownloadDeckPDF = async () => {
    if (!planId) {
      alert('Set a plan identifier before generating the deck.');
      return;
    }
    try {
      // Show progress modal
      setExportProgress({ show: true, type: 'pdf', current: 0, total: 3, message: 'Preparing deck summaries...' });

      const summaryMap = await requestDeckSummaries();
      setExportProgress({ show: true, type: 'pdf', current: 1, total: 3, message: 'Compiling document...' });

      const deckDoc = compileDeckDocument(summaryMap);
      if (!deckDoc) {
        setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
        alert('Deck summaries are not available yet.');
        return;
      }

      setExportProgress({ show: true, type: 'pdf', current: 2, total: 3, message: 'Generating PDF...' });

      // Small delay to show progress, then generate PDF
      setTimeout(() => {
        renderDocumentAsPdf(deckDoc, `Deck Summary - ${planId}`);
        setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
        // Backend awards points for deck generation; refresh and show
        // Add delay to ensure backend has processed the points
        setTimeout(() => {
          refreshPointsAndNotify('Deck PDF generated');
        }, 2000);
      }, 300);
    } catch (error) {
      console.error('Failed to generate deck PDF:', error);
      setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
      alert('Failed to generate deck PDF: ' + (error.message || 'Unknown error'));
    }
  };

  const handleClearDeckSummaries = () => {
    if (deckGenerating) {
      alert('Deck generation in progress. Please wait until it completes before clearing.');
      return;
    }
    setDeckSummaries({});
    setDeckStatus('');
    deckRequestResolveRef.current = null;
    deckRequestRejectRef.current = null;
    deckRequestPromiseRef.current = null;
    deckForceNextRef.current = true;
  };

  const handleDownloadPPT = async () => {
    if (!planId) {
      alert('Set a plan identifier before generating the PPT.');
      return;
    }

    try {
      setExportProgress({
        show: true,
        type: 'ppt',
        current: 5,
        total: 100,
        message: 'Requesting PPT from backend...',
      });

      // Build download URL (backend v2 endpoint)
      const apiBase = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000').replace(/\/$/, '');
      const filename = `${planId}_Training_Proposal.pptx`;
      const cacheBust = `_ts=${Date.now()}`;
      const url = `${apiBase}/api/proposals/download-v2/${encodeURIComponent(filename)}?format=pptx&${cacheBust}`;

      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      setExportProgress({
        show: true,
        type: 'ppt',
        current: 60,
        total: 100,
        message: 'Downloading PPT...',
      });

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${planId}_Training_Proposal_v2.pptx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      setExportProgress({
        show: true,
        type: 'ppt',
        current: 100,
        total: 100,
        message: 'PPT download complete.',
      });

      // Backend awards points; refresh and show in UI
      // Add delay to ensure backend has processed the points
      setTimeout(() => {
        refreshPointsAndNotify('PPT downloaded');
      }, 1500);

      setTimeout(() => {
        setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
      }, 600);
    } catch (error) {
      console.error('Failed to generate PPT:', error);
      setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
      alert('Failed to generate PPT file: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDownloadFullDocPPT = async () => {
    if (!planId) {
      alert('Set a plan identifier before generating the PPT.');
      return;
    }
    if (!outline.length) {
      alert('Generate or load an outline before exporting.');
      return;
    }

    const extractBulletPoints = (text, maxBullets = 10) => {
      if (!text) return [];
      const normalized = text
        .replace(/\r/g, '\n')
        .split(/\n+/)
        .map((line) => line.replace(/^[\s>*-]+/, '').trim())
        .filter(Boolean);
      if (normalized.length >= 3) {
        return normalized.slice(0, maxBullets);
      }
      const sentences = text
        .split(/(?<=[.!?])\s+/)
        .map((sentence) => sentence.trim())
        .filter(Boolean);
      return sentences.slice(0, maxBullets);
    };

    try {
      setExportProgress({
        show: true,
        type: 'ppt',
        current: 0,
        total: 100,
        message: 'Gathering outline content...',
      });

      const nodes = flattenNodes(outline);
      const leafNodes = nodes.filter((node) => !(Array.isArray(node.children) && node.children.length > 0));

      if (!leafNodes.length) {
        setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
        alert('No leaf sections available to export.');
        return;
      }

      const slidesWithContent = [];
      for (let i = 0; i < leafNodes.length; i++) {
        const node = leafNodes[i];
        setExportProgress({
          show: true,
          type: 'ppt',
          current: Math.min(5 + Math.floor((i / leafNodes.length) * 25), 30),
          total: 100,
          message: `Fetching content for "${node.title || 'Section'}"...`,
        });
        let nodeContent = '';
        if (planId && node.id) {
          try {
            const data = await apiService.getProposalNode(planId, node.id);
            if (data?.content && data.content.trim()) {
              nodeContent = data.content.trim();
            }
          } catch (error) {
            console.error(`Failed to load content for node ${node.id}:`, error);
          }
        }
        if (!nodeContent && node.custom_description) {
          nodeContent = node.custom_description.trim();
        }
        if (nodeContent) {
          slidesWithContent.push({ node, content: nodeContent });
        }
      }

      if (!slidesWithContent.length) {
        setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
        alert('Leaf sections do not have generated content yet. Please generate node content first.');
        return;
      }

      setExportProgress({
        show: true,
        type: 'ppt',
        current: 35,
        total: 100,
        message: 'Building PowerPoint slides...',
      });

      const pptx = new PptxGenJS();
      pptx.layout = 'LAYOUT_16x9';

      const programOverviewTree = getProgramOverviewTreeText();
      const includeProgramOverview = !!programOverviewTree;
      const totalSlides = slidesWithContent.length + (includeProgramOverview ? 1 : 0);
      let slideCount = 0;
      let programOverviewInserted = false;

      const slideWidth = 10;
      const slideHeight = 5.625;
      const marginX = 0.5;
      const marginY = 0.4;
      const contentWidth = slideWidth - marginX * 2;

      const insertProgramOverviewSlide = (message = 'Adding Program Overview slide...') => {
        if (!includeProgramOverview || programOverviewInserted) return;
        addProgramOverviewSlide(pptx, programOverviewTree, { slideWidth, slideHeight, marginX, marginY });
        programOverviewInserted = true;
        slideCount++;
        const progress = 35 + Math.floor((slideCount / totalSlides) * 55);
        setExportProgress({
          show: true,
          type: 'ppt',
          current: Math.min(progress, 95),
          total: 100,
          message,
        });
      };

      slidesWithContent.forEach(({ node, content }, idx) => {
        const slide = pptx.addSlide();
        let slideTitle = node.title || 'Slide';

        // Check if this is a parent module node
        const nodeType = (node.type || '').toLowerCase();
        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        const isTemporal = getTemporalTag(node) !== null;
        const isModuleNode = hasChildren && !isTemporal && nodeType !== 'title' && nodeType !== 'problem_statement' && nodeType !== 'commercials';

        if (isModuleNode) {
          // For parent module nodes, show "Module X: Title"
          const moduleNum = getModuleNumber(node, outline);
          if (moduleNum) {
            slideTitle = `Module ${moduleNum}: ${slideTitle}`;
          }
        } else {
          // For child nodes, use hierarchical path with numbering
          const hierarchicalPath = buildHierarchicalPath(node, outline);
          if (hierarchicalPath) {
            slideTitle = hierarchicalPath + slideTitle;
          }
        }

        let currentY = marginY;
        slide.addText(slideTitle, {
          x: marginX,
          y: currentY,
          w: contentWidth,
          h: 0.9,
          fontSize: 24,
          bold: true,
          color: '363636',
          fontFace: 'Arial',
          valign: 'top',
          shrinkText: true,
        });
        currentY += 1.0;

        const bullets = extractBulletPoints(content);
        if (bullets.length) {
          const availableHeight = slideHeight - currentY - 0.2;
          if (availableHeight > 0) {
            slide.addText(bullets.join('\n'), {
              x: marginX,
              y: currentY,
              w: contentWidth,
              h: availableHeight,
              fontSize: 12,
              bullet: true,
              color: '363636',
              fontFace: 'Arial',
              lineSpacing: 20,
              valign: 'top',
              shrinkText: true,
            });
          }
        } else {
          // Fallback: add trimmed paragraph
          const availableHeight = slideHeight - currentY - 0.2;
          if (availableHeight > 0) {
            slide.addText(content.slice(0, 800), {
              x: marginX,
              y: currentY,
              w: contentWidth,
              h: availableHeight,
              fontSize: 12,
              color: '363636',
              fontFace: 'Arial',
              valign: 'top',
              shrinkText: true,
            });
          }
        }

        slideCount++;
        const progress = 35 + Math.floor((slideCount / totalSlides) * 55);
        setExportProgress({
          show: true,
          type: 'ppt',
          current: Math.min(progress, 95),
          total: 100,
          message: `Creating slide ${Math.min(slideCount, totalSlides)}/${totalSlides}...`,
        });

        if (!programOverviewInserted && includeProgramOverview && isClientBriefNode(node)) {
          insertProgramOverviewSlide('Adding Program Overview slide after Client Brief...');
        }
      });

      if (includeProgramOverview && !programOverviewInserted) {
        insertProgramOverviewSlide();
      }

      const fileName = `${planId || 'Proposal'}_FullDoc.pptx`;
      await pptx.writeFile({ fileName });

      setExportProgress({
        show: true,
        type: 'ppt',
        current: 100,
        total: 100,
        message: 'Download ready…',
      });
      setTimeout(() => {
        setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
      }, 600);
    } catch (error) {
      console.error('Failed to generate full document PPT:', error);
      setExportProgress({ show: false, type: '', current: 0, total: 0, message: '' });
      alert('Failed to generate full document PPT: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteOutlineClick = () => {
    if (!planId || outline.length === 0) return;
    setShowDeleteProposalModal(true);
  };

  const handleDeleteOutlineConfirm = async () => {
    if (!planId) return;
    setShowDeleteProposalModal(false);
    try {
      await apiService.deleteProposalOutline(planId);
      // Clear all state
      setOutline([]);
      setConceptDocument('');
      setSelectedResourcesData({ selected_resources: {}, placement_strategy: {} });
      setResourceAttachModal({ open: false, parentId: null });
      setResourceAttachSearch('');
      setResourceAttachCategory('all');
      setSelectedNodeId(null);
      setNodeContent('');
      setNodeTitle('');
      setCustomDescription('');
      setCustomNotes('');
      setShowConceptDoc(false);
      setDeckSummaries({});
      setDeckGenerating(false);
      setDeckStatus('');
      deckRequestResolveRef.current = null;
      deckRequestRejectRef.current = null;
      deckRequestPromiseRef.current = null;
      // Navigate to management page
      window.location.href = '/management';
    } catch (error) {
      console.error('Failed to delete proposal', error);
      alert('Failed to delete proposal: ' + (error.message || 'Unknown error'));
    }
  };

  const handleNewProposalClick = () => {
    setShowNewProposalModal(true);
  };

  const handleNewProposalConfirm = async () => {
    setShowNewProposalModal(false);
    // Always save current proposal before switching so it persists and shows in Management list
    if (planId) {
      try {
        await apiService.saveProposalOutline(planId, outline.length > 0 ? outline : []);
      } catch (error) {
        console.error('Failed to save current proposal before switching:', error);
        toast.error('Could not save current proposal. It may not appear in Management.');
        // Continue anyway - user might want to switch even if save fails
      }
    }

    // Generate a unique plan ID with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const newPlanId = `proposal_${timestamp}`;

    // Reset all state for new proposal
    setPlanId(newPlanId);
    setPlanIdInput(newPlanId);
    setOutline([]);
    setConceptDocument('');
    setSelectedResourcesData({ selected_resources: {}, placement_strategy: {} });
    setResourceAttachModal({ open: false, parentId: null });
    setResourceAttachSearch('');
    setResourceAttachCategory('all');
    setSelectedNodeId(null);
    setNodeContent('');
    setNodeTitle('');
    setCustomDescription('');
    setCustomNotes('');
    setShowConceptDoc(false);
    setDeckSummaries({});
    setDeckGenerating(false);
    setDeckStatus('');
    deckRequestResolveRef.current = null;
    deckRequestRejectRef.current = null;
    deckRequestPromiseRef.current = null;

    // Navigate to new proposal URL
    navigate(`/proposal-outline/${encodeURIComponent(newPlanId)}`);
  };

  const persistOutline = async (nextOutline) => {
    if (!planId) return;
    try {
      setSavingOutline(true);
      await apiService.saveProposalOutline(planId, nextOutline);
      setOutline(nextOutline.map(ensureChildren));
    } catch (error) {
      console.error('Failed to save proposal outline', error);
    } finally {
      setSavingOutline(false);
    }
  };

  const selectNode = async (nodeId, nodeOverride = null) => {
    resetNodeDiffState();
    setSelectedNodeId(nodeId);
    const node = nodeOverride || findNodeById(outline, nodeId) || {};
    setNodeTitle(node.title || '');
    setNodeType(node.type || 'custom_ingredient');
    setResourceType(node.resource_type || '');
    setResourceId(node.resource_id || '');
    setCustomDescription(node.custom_description || '');
    setCustomNotes(node.custom_notes || '');

    if (!planId || !nodeId) {
      setNodeContent('');
      return;
    }

    try {
      const data = await apiService.getProposalNode(planId, nodeId);
      setNodeContent(data?.content || '');
      const metadata = data?.metadata || {};
      if (metadata.type) setNodeType(metadata.type);
      if (metadata.resourceType ?? metadata.resource_type) {
        setResourceType(metadata.resourceType ?? metadata.resource_type);
      }
      if (metadata.resourceId ?? metadata.resource_id) {
        setResourceId(metadata.resourceId ?? metadata.resource_id);
      }
      if (metadata.customDescription ?? metadata.custom_description) {
        setCustomDescription(metadata.customDescription ?? metadata.custom_description);
      }
      if (metadata.customNotes ?? metadata.custom_notes) {
        setCustomNotes(metadata.customNotes ?? metadata.custom_notes);
      }
    } catch (error) {
      console.error('Failed to load proposal node', error);
      setNodeContent('');
    }
  };

  const handleAddRoot = async () => {
    const id = `ingredient_${Date.now()}`;
    const newNode = ensureChildren({
      id,
      title: 'New Ingredient',
      type: 'custom_ingredient',
      resource_type: '',
      resource_id: '',
      custom_description: '',
      custom_notes: '',
    });
    const nextOutline = [...outline, newNode];
    await persistOutline(nextOutline);
    await selectNode(id, newNode);
  };

  const handleAddChild = async (parentId) => {
    // If preliminary proposal exists, offer to add missing child from it
    if (preliminaryProposal && preliminaryProposal.trim()) {
      const parentNode = findNodeInOutline(outline, parentId);
      if (parentNode) {
        setAddMissingChildModal({ open: true, parentId, parentNode });
        return;
      }
    }
    
    // Fallback to resource attach modal
    if (!hasCatalogResources) {
      alert('No catalog resources available. Please generate or load resources first.');
      return;
    }
    setResourceAttachModal({ open: true, parentId });
    setResourceAttachSearch('');
    setResourceAttachCategory('all');
  };
  
  const findNodeInOutline = (nodes, targetId) => {
    for (const node of nodes || []) {
      if (node.id === targetId) {
        return node;
      }
      if (node.children) {
        const found = findNodeInOutline(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };
  
  const handleMissingChildAdded = (newOutline) => {
    setOutline(newOutline);
    setAddMissingChildModal({ open: false, parentId: null });
  };

  const closeResourceAttachModal = () => {
    setResourceAttachModal({ open: false, parentId: null });
    setResourceAttachSearch('');
    setResourceAttachCategory('all');
  };

  const handleResourceSelectionForChild = async (resourceItem) => {
    const parentId = resourceAttachModal.parentId;
    closeResourceAttachModal();
    if (!parentId || !resourceItem) return;
    await createChildWithResource(parentId, resourceItem);
  };

  const createManualChild = async (parentId) => {
    try {
      const id = `ingredient_${Date.now()}`;
      const newChild = ensureChildren({
        id,
        title: 'New Ingredient',
        type: 'custom_ingredient',
        resource_type: '',
        resource_id: '',
        resource_ids: [],
        resource_types: [],
        custom_description: '',
        custom_notes: '',
      });
      const nextOutline = updateOutlineNode(outline, parentId, (node) => ({
        ...node,
        children: [...(node.children || []), newChild],
      }));
      await persistOutline(nextOutline);
      await selectNode(id, newChild);
      setNodeContent('');
    } catch (error) {
      console.error('Failed to add manual child ingredient', error);
      alert('Failed to add ingredient. Please try again.');
    }
  };

  const createChildWithResource = async (parentId, resourceItem) => {
    try {
      const resourceCategory = resourceItem.category || '';
      const nodeResourceType = mapCategoryToResourceType(resourceCategory);
      const nodeType = mapCategoryToNodeType(resourceCategory) || 'custom_ingredient';
      const id = `ingredient_${Date.now()}`;
      const newChild = ensureChildren({
        id,
        title: resourceItem.name || resourceItem.id || 'New Ingredient',
        type: nodeType,
        resource_type: nodeResourceType,
        resource_id: resourceItem.id || '',
        resource_ids: resourceItem.id ? [resourceItem.id] : [],
        resource_types: nodeResourceType ? [nodeResourceType] : [],
        custom_description: '',
        custom_notes: '',
      });
      const nextOutline = updateOutlineNode(outline, parentId, (node) => ({
        ...node,
        children: [...(node.children || []), newChild],
      }));
      await persistOutline(nextOutline);
      await selectNode(id, newChild);
      if (resourceItem.id && nodeResourceType) {
        const resourcePayload = await fetchResourceContentByRef({ id: resourceItem.id, type: nodeResourceType });
        const script = resourcePayload?.content || '';
        await applyResourceScriptToNode(id, newChild, script);
        setNodeContent(script);
      } else {
        await applyResourceScriptToNode(id, newChild, '');
      }
    } catch (error) {
      console.error('Failed to attach catalog resource', error);
      alert('Failed to attach resource. Please try again.');
    }
  };

  const handleManualChildCreation = async () => {
    const parentId = resourceAttachModal.parentId;
    closeResourceAttachModal();
    if (!parentId) return;
    await createManualChild(parentId);
  };

  const handleDeleteNode = async (nodeId) => {
    if (!window.confirm('Delete this ingredient and its children?')) return;
    const nextOutline = removeOutlineNode(outline, nodeId);
    await persistOutline(nextOutline);
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
      setNodeTitle('');
      setNodeContent('');
      setCustomDescription('');
      setCustomNotes('');
      setResourceId('');
      setResourceType('');
      setNodeType('custom_ingredient');
    }
  };

  const handleNodeFieldChange = (updates) => {
    if (!selectedNodeId) return;
    const nextOutline = updateOutlineNode(outline, selectedNodeId, (node) => ({
      ...node,
      ...updates,
    }));
    setOutline(nextOutline);
  };

  const handleSaveOutlineClick = () => persistOutline(outline);

  const handleSaveNodeContent = async () => {
    if (!planId || !selectedNodeId) return;
    try {
      setSavingNode(true);
      const metadataPayload = {
        type: nodeType,
        resourceType,
        resourceId,
        customDescription,
        customNotes,
      };
      const resourceRefs = resourceType && resourceId ? [{ type: resourceType, id: resourceId }] : [];
      await apiService.saveProposalNode(planId, selectedNodeId, {
        content: nodeContent,
        metadata: metadataPayload,
        resource_refs: resourceRefs,
      });
    } catch (error) {
      console.error('Failed to save proposal node content', error);
    } finally {
      setSavingNode(false);
    }
  };

  const handleAiEditSelectedNode = async () => {
    if (!selectedNodeId) {
      alert('Select a node to edit.');
      return;
    }

    if (!nodeContent || !nodeContent.trim()) {
      alert('Node content is empty. Generate or paste content before editing with AI.');
      return;
    }

    if (!nodeAiInstruction.trim()) {
      alert('Enter an instruction for the AI edit.');
      return;
    }

    setNodeAiProcessing(true);
    setNodeAiStatus('');
    try {
      const original = nodeContent;
      const overridesPayload = {};
      if (plannerModelOverride && plannerModelOverride.trim()) {
        overridesPayload.planner_model = plannerModelOverride.trim();
      }
      if (plannerFallbackOverride && plannerFallbackOverride.trim()) {
        overridesPayload.planner_fallback_model = plannerFallbackOverride.trim();
      }
      if (generatorModelOverride && generatorModelOverride.trim()) {
        overridesPayload.generator_model = generatorModelOverride.trim();
      }
      if (generatorFallbackOverride && generatorFallbackOverride.trim()) {
        overridesPayload.generator_fallback_model = generatorFallbackOverride.trim();
      }
      const requestOptions = {
        scope: 'node',
        ...(Object.keys(overridesPayload).length ? { model_overrides: overridesPayload } : {}),
      };
      const result = await apiService.brainDiscoverDiffs(original, nodeAiInstruction, requestOptions);
      setNodeOriginalContent(original);
      setNodeMemoryMap(result?.memory_map || null);
      const operations = result?.plan?.operations || [];
      setNodeAiPlan(operations);

      const discoveredDiffs = (result?.diff_set?.diffs || []).map((diff, idx) => {
        const originalText = diff?.original_text || '';
        const editedText = diff?.new_text || '';
        const startLine = diff?.start_line ?? 0;
        const endLine = diff?.end_line ?? 0;

        return {
          id: `${diff?.operation?.type || diff?.type || 'op'}-${diff?.start_line}-${diff?.end_line}-${idx}-${Date.now()}`,
          originalText,
          editedText,
          startLine,
          endLine,
          type: diff?.operation?.type || diff?.type || 'replace',
          rationale: diff?.operation?.rationale || diff?.rationale || '',
          sectionName: diff?.section_name || '',
          status: diff?.status || 'pending',
          error: diff?.error || null,
          startChar: diff?.start_char ?? 0,
          endChar: diff?.end_char ?? 0,
          matchScore: diff?.match_score ?? 1.0,
          beforeContext: diff?.before_context || '',
          afterContext: diff?.after_context || '',
          initialStartLine: startLine,
          initialEndLine: endLine,
          fullOriginalText: originalText,
          fullEditedText: editedText,
          operation: diff?.operation
            ? diff.operation
            : {
              type: diff?.type || 'replace',
              section_name: diff?.section_name || '',
              start_line: startLine ?? 1,
              end_line: endLine ?? 1,
              rationale: diff?.rationale || '',
              metadata: {},
            },
        }
      });

      setNodeDiffs(discoveredDiffs);

      if (discoveredDiffs.length > 0) {
        setNodeDiffView(true);
        setNodeCurrentDiffIndex(0);
        const errorCount = discoveredDiffs.filter((d) => d.status === 'error').length;
        const pendingCount = discoveredDiffs.filter((d) => d.status === 'pending').length;
        if (result?.diff_set?.has_overlaps) {
          setNodeAiStatus(
            `⚠️ Found ${discoveredDiffs.length} diff(s) (${pendingCount} pending, ${errorCount} error). ` +
            `${result.diff_set.overlap_errors.length} overlap(s) detected. Review before applying.`
          );
        } else {
          setNodeAiStatus(
            `✅ Found ${pendingCount} diff(s) ready for review.` +
            (errorCount ? ` ${errorCount} diff(s) reported errors.` : '')
          );
        }
      } else if (result?.diff_set) {
        setNodeDiffView(false);
        setNodeAiStatus('AI edit completed but no changes detected.');
      } else {
        setNodeDiffs([]);
        setNodeDiffView(false);
        setNodeAiStatus('No diffs returned from AI edit.');
      }
    } catch (error) {
      console.error('AI node edit failed:', error);
      alert(error?.message || 'Failed to edit content with AI.');
      setNodeAiStatus('Error: ' + (error?.message || 'Failed to edit content'));
    } finally {
      setNodeAiProcessing(false);
    }
  };

  const handleNodeAcceptDiff = (diffId) => {
    setNodeDiffs((prev) =>
      prev.map((diff) =>
        diff.id === diffId && diff.status !== 'error' ? { ...diff, status: 'accepted' } : diff
      )
    );
  };

  const handleNodeRejectDiff = (diffId) => {
    setNodeDiffs((prev) =>
      prev.map((diff) =>
        diff.id === diffId && diff.status !== 'error' ? { ...diff, status: 'rejected' } : diff
      )
    );
  };

  const handleNodeAcceptAllDiffs = () => {
    setNodeDiffs((prev) =>
      prev.map((diff) =>
        diff.status === 'pending' ? { ...diff, status: 'accepted' } : diff
      )
    );
  };

  const handleNodeRejectAllDiffs = () => {
    setNodeDiffs((prev) =>
      prev.map((diff) =>
        diff.status === 'pending' || diff.status === 'accepted' ? { ...diff, status: 'rejected' } : diff
      )
    );
  };

  const handleDiffEditedTextChange = (diffId, newValue) => {
    setNodeDiffs((prev) =>
      prev.map((diff) => (diff.id === diffId ? { ...diff, editedText: newValue } : diff))
    );
  };

  const handleDiffLineChange = (diffId, field, newValue) => {
    setNodeDiffs((prev) =>
      prev.map((diff) => {
        if (diff.id !== diffId) return diff;
        const initialStart = diff.initialStartLine ?? diff.startLine ?? 0;
        const initialEnd = diff.initialEndLine ?? diff.endLine ?? initialStart;
        const baseText = diff.fullOriginalText ?? diff.originalText ?? '';
        const baseLines = baseText ? baseText.split('\n') : [];
        if (!baseLines.length) {
          return diff;
        }

        const parsedValue = Number(newValue);
        if (Number.isNaN(parsedValue)) return diff;

        let newStart = diff.startLine ?? initialStart;
        let newEnd = diff.endLine ?? initialEnd;

        if (field === 'start') {
          newStart = Math.min(Math.max(parsedValue, initialStart), newEnd);
        } else {
          newEnd = Math.max(Math.min(parsedValue, initialEnd), newStart);
        }

        if (newStart < initialStart) newStart = initialStart;
        if (newEnd > initialEnd) newEnd = initialEnd;
        if (newStart > newEnd) {
          if (field === 'start') {
            newEnd = newStart;
          } else {
            newStart = newEnd;
          }
        }

        const startOffset = Math.max(0, newStart - initialStart);
        const endOffset = Math.max(startOffset, newEnd - initialStart + 1);
        const newOriginalLines = baseLines.slice(startOffset, endOffset);

        return {
          ...diff,
          startLine: newStart,
          endLine: newEnd,
          originalText: newOriginalLines.join('\n'),
          operation: diff.operation
            ? { ...diff.operation, start_line: newStart, end_line: newEnd }
            : diff.operation,
        };
      })
    );
  };

  const handleDiffSpanReset = (diffId) => {
    setNodeDiffs((prev) =>
      prev.map((diff) => {
        if (diff.id !== diffId) return diff;
        const initialStart = diff.initialStartLine ?? diff.startLine ?? 0;
        const initialEnd = diff.initialEndLine ?? diff.endLine ?? initialStart;
        const baseText = diff.fullOriginalText ?? diff.originalText ?? '';
        return {
          ...diff,
          startLine: initialStart,
          endLine: initialEnd,
          originalText: baseText,
          operation: diff.operation
            ? { ...diff.operation, start_line: initialStart, end_line: initialEnd }
            : diff.operation,
        };
      })
    );
  };

  const handleResetEditedText = (diffId) => {
    setNodeDiffs((prev) =>
      prev.map((diff) =>
        diff.id === diffId
          ? { ...diff, editedText: diff.fullEditedText ?? diff.editedText }
          : diff
      )
    );
  };

  const handleJumpToDiff = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= nodeDiffs.length) return;
    setNodeCurrentDiffIndex(nextIndex);
  };

  const handleApplyNodeDiffs = async () => {
    const acceptedDiffs = nodeDiffs.filter((diff) => diff.status === 'accepted' && !diff.error);
    if (acceptedDiffs.length === 0) {
      alert('No diffs accepted. Please accept at least one diff before applying.');
      return;
    }
    if (!nodeOriginalContent || !nodeMemoryMap) {
      alert('Missing original content or memory map. Please run the AI edit again.');
      return;
    }

    setNodeApplyingDiffs(true);
    setNodeAiStatus('Applying accepted diffs…');
    try {
      const backendDiffs = acceptedDiffs.map((diff) => ({
        operation: diff.operation,
        original_text: diff.originalText,
        new_text: diff.editedText,
        before_context: diff.beforeContext,
        after_context: diff.afterContext,
        section_name: diff.sectionName,
        start_char: diff.startChar,
        end_char: diff.endChar,
        start_line: diff.startLine,
        end_line: diff.endLine,
        match_score: diff.matchScore,
        status: 'accepted',
        error: diff.error,
      }));

      const result = await apiService.brainApplyDiffs(nodeOriginalContent, nodeMemoryMap, backendDiffs);

      if (result?.edited_content) {
        setNodeContent(result.edited_content);
        resetNodeDiffState();
        setNodeAiStatus(`✅ Applied ${acceptedDiffs.length} diff(s).`);
      } else {
        setNodeAiStatus('Error: No edited content returned.');
      }
    } catch (error) {
      console.error('Apply node diffs failed:', error);
      alert(error?.message || 'Failed to apply diffs.');
      setNodeAiStatus('Error: ' + (error?.message || 'Failed to apply diffs'));
    } finally {
      setNodeApplyingDiffs(false);
    }
  };

  const handleCancelNodeDiffs = () => {
    resetNodeDiffState();
  };

  const renderNodeDiffView = () => {
    if (!nodeDiffs.length) {
      return <div style={{ color: '#6b7280', fontSize: 13 }}>No diffs to display.</div>;
    }

    const statusCounts = nodeDiffs.reduce(
      (acc, diff) => {
        const key = diff.status || 'pending';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      { pending: 0, accepted: 0, rejected: 0, error: 0 }
    );

    return (
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 12,
            background: '#f9fafb',
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            marginBottom: 16,
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontSize: 12, color: '#111827' }}>
            Showing {nodeDiffs.length} change{nodeDiffs.length > 1 ? 's' : ''} • Pending{' '}
            {statusCounts.pending || 0} • Accepted {statusCounts.accepted || 0} • Rejected{' '}
            {statusCounts.rejected || 0} • Errors {statusCounts.error || 0}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleJumpToDiff(nodeCurrentDiffIndex - 1)}
              disabled={nodeCurrentDiffIndex === 0}
              style={{
                borderRadius: 6,
                border: '1px solid #2563eb',
                background:
                  nodeCurrentDiffIndex === 0 ? '#f3f4f6' : '#dbeafe',
                color: nodeCurrentDiffIndex === 0 ? '#9ca3af' : '#1e40af',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 500,
                cursor: nodeCurrentDiffIndex === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              ← Prev
            </button>
            <div style={{ fontSize: 12, color: '#6b7280', minWidth: 120, textAlign: 'center' }}>
              Jump {nodeCurrentDiffIndex + 1}/{nodeDiffs.length}
            </div>
            <button
              onClick={() => handleJumpToDiff(nodeCurrentDiffIndex + 1)}
              disabled={nodeCurrentDiffIndex === nodeDiffs.length - 1}
              style={{
                borderRadius: 6,
                border: '1px solid #2563eb',
                background:
                  nodeCurrentDiffIndex === nodeDiffs.length - 1
                    ? '#f3f4f6'
                    : '#dbeafe',
                color: nodeCurrentDiffIndex === nodeDiffs.length - 1 ? '#9ca3af' : '#1e40af',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 500,
                cursor: nodeCurrentDiffIndex === nodeDiffs.length - 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Next →
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {nodeDiffs.map((diff, idx) => {
            const isActive = idx === nodeCurrentDiffIndex;
            return (
              <div
                key={diff.id}
                ref={(el) => {
                  nodeDiffRefs.current[idx] = el;
                }}
                style={{
                  border: `1px solid ${isActive ? '#3b82f6' : '#e5e7eb'}`,
                  boxShadow: isActive ? '0 0 0 2px rgba(59,130,246,0.2)' : 'none',
                  borderRadius: 10,
                  padding: 16,
                  background: isActive ? '#eff6ff' : '#ffffff',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, color: '#111827', fontWeight: 500 }}>
                      Change {idx + 1} of {nodeDiffs.length}{' '}
                      <span
                        style={{
                          display: 'inline-block',
                          marginLeft: 6,
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          color: '#ffffff',
                          background:
                            diff.status === 'accepted'
                              ? '#22c55e'
                              : diff.status === 'rejected'
                                ? '#ef4444'
                                : diff.status === 'error'
                                  ? '#f97316'
                                  : '#6366f1',
                        }}
                      >
                        {diff.status?.toUpperCase() || 'PENDING'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                      Lines {diff.startLine}-{diff.endLine} • {diff.type?.toUpperCase() || 'CHANGE'} •{' '}
                      {diff.sectionName || 'Node'}
                      {diff.matchScore !== undefined && diff.matchScore < 1.0 && (
                        <> • Match {(diff.matchScore * 100).toFixed(0)}%</>
                      )}
                    </div>
                    {diff.rationale && (
                      <div style={{ fontSize: 11, color: '#4f46e5', marginTop: 6 }}>
                        {diff.rationale}
                      </div>
                    )}
                    {diff.error && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 6 }}>
                        Error: {diff.error}
                      </div>
                    )}
                  </div>
                  {diff.status !== 'error' && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleNodeAcceptDiff(diff.id)}
                        disabled={diff.status === 'accepted'}
                        style={{
                          borderRadius: 6,
                          border: '1px solid #16a34a',
                          background:
                            diff.status === 'accepted'
                              ? '#dcfce7'
                              : '#dcfce7',
                          color: '#166534',
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: diff.status === 'accepted' ? 'not-allowed' : 'pointer',
                          opacity: diff.status === 'accepted' ? 0.6 : 1,
                        }}
                      >
                        {diff.status === 'accepted' ? 'Accepted' : 'Accept'}
                      </button>
                      <button
                        onClick={() => handleNodeRejectDiff(diff.id)}
                        disabled={diff.status === 'rejected'}
                        style={{
                          borderRadius: 6,
                          border: '1px solid #dc2626',
                          background:
                            diff.status === 'rejected'
                              ? '#fee2e2'
                              : '#fee2e2',
                          color: '#991b1b',
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: diff.status === 'rejected' ? 'not-allowed' : 'pointer',
                          opacity: diff.status === 'rejected' ? 0.6 : 1,
                        }}
                      >
                        {diff.status === 'rejected' ? 'Rejected' : 'Reject'}
                      </button>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                    marginTop: 12,
                  }}
                >
                  <div
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: 12,
                      background: '#fef2f2',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 10,
                        fontSize: 11,
                        color: '#dc2626',
                        fontWeight: 600,
                        marginBottom: 8,
                        paddingBottom: 8,
                        borderBottom: '1px solid #e5e7eb',
                      }}
                    >
                      <span>ORIGINAL</span>
                      <span style={{ color: '#d97706', fontWeight: 500 }}>
                        Initial {diff.initialStartLine}-{diff.initialEndLine}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 10,
                        fontSize: 11,
                        color: '#4f46e5',
                      }}
                    >
                      <span>Span:</span>
                      <input
                        type="number"
                        value={diff.startLine}
                        min={diff.initialStartLine}
                        max={diff.endLine}
                        onChange={(e) => handleDiffLineChange(diff.id, 'start', e.target.value)}
                        style={{
                          width: 70,
                          padding: '2px 6px',
                          borderRadius: 4,
                          border: '1px solid #d1d5db',
                          background: '#ffffff',
                          color: '#111827',
                        }}
                      />
                      <span>to</span>
                      <input
                        type="number"
                        value={diff.endLine}
                        min={diff.startLine}
                        max={diff.initialEndLine}
                        onChange={(e) => handleDiffLineChange(diff.id, 'end', e.target.value)}
                        style={{
                          width: 70,
                          padding: '2px 6px',
                          borderRadius: 4,
                          border: '1px solid #d1d5db',
                          background: '#ffffff',
                          color: '#111827',
                        }}
                      />
                      {(diff.startLine !== diff.initialStartLine || diff.endLine !== diff.initialEndLine) && (
                        <button
                          onClick={() => handleDiffSpanReset(diff.id)}
                          style={{
                            borderRadius: 4,
                            border: '1px solid #d1d5db',
                            background: '#f3f4f6',
                            color: '#374151',
                            padding: '2px 8px',
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          Reset span
                        </button>
                      )}
                    </div>

                    <div
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: '#111827',
                        fontSize: 12,
                        lineHeight: 1.6,
                        fontFamily: 'monospace',
                      }}
                    >
                      {diff.originalText || (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>(empty)</span>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: 12,
                      background: '#f0fdf4',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 11,
                        color: '#16a34a',
                        fontWeight: 600,
                        marginBottom: 8,
                        paddingBottom: 8,
                        borderBottom: '1px solid #e5e7eb',
                      }}
                    >
                      <span>EDITED ({diff.type?.toUpperCase() || 'CHANGE'})</span>
                      {diff.fullEditedText && diff.editedText !== diff.fullEditedText && (
                        <button
                          onClick={() => handleResetEditedText(diff.id)}
                          style={{
                            borderRadius: 4,
                            border: '1px solid #16a34a',
                            background: '#dcfce7',
                            color: '#166534',
                            padding: '2px 8px',
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          Reset text
                        </button>
                      )}
                    </div>
                    <textarea
                      value={diff.editedText}
                      onChange={(e) => handleDiffEditedTextChange(diff.id, e.target.value)}
                      placeholder="Edit the replacement content before accepting."
                      rows={Math.min(10, (diff.editedText || '').split('\n').length + 1)}
                      style={{
                        width: '100%',
                        borderRadius: 6,
                        border: '1px solid #d1d5db',
                        background: '#ffffff',
                        color: '#111827',
                        fontSize: 12,
                        lineHeight: 1.6,
                        fontFamily: 'monospace',
                        padding: 8,
                        resize: 'vertical',
                        minHeight: 120,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const generatingAllContentRef = useRef(false);
  const pendingNodeResolversRef = useRef(new Map());

  const handleGenerateAllContent = async () => {
    if (!planId || outline.length === 0) {
      alert('No outline available. Please generate an outline first.');
      return;
    }
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      alert('WebSocket not connected. Please wait and try again.');
      return;
    }

    // Flatten all nodes
    const allNodes = flattenNodes(outline);
    if (allNodes.length === 0) {
      alert('No nodes found in outline.');
      return;
    }

    if (!window.confirm(`Generate content for all ${allNodes.length} sections? This may take several minutes.`)) {
      return;
    }

    setGeneratingAllContent(true);
    generatingAllContentRef.current = true;
    setGenerationProgress({ current: 0, total: allNodes.length, currentNode: '' });

    const enriched = {
      proposals: attachedContext.proposals || [],
      games: attachedContext.games || [],
      activities: attachedContext.activities || [],
      frameworks: attachedContext.frameworks || [],
      tools: attachedContext.tools || [],
      psychometricTools: attachedContext.psychometricTools || [],
    };

    // Generate content for each node sequentially
    for (let i = 0; i < allNodes.length; i++) {
      const node = allNodes[i];
      setGenerationProgress({
        current: i + 1,
        total: allNodes.length,
        currentNode: node.title || node.id,
      });

      if (useResourceContentDirectly) {
        const applied = await tryApplyResourceScriptForNode(node);
        if (applied) {
          continue;
        }
      }

      // Wait for WebSocket to be ready
      if (wsRef.current.readyState !== WebSocket.OPEN) {
        alert(`WebSocket disconnected. Generated ${i} of ${allNodes.length} sections.`);
        break;
      }

      // Create a promise that resolves when this node's content is generated
      const nodePromise = new Promise((resolve) => {
        pendingNodeResolversRef.current.set(node.id, resolve);
      });

      // Send generation request
      wsRef.current.send(JSON.stringify({
        command: 'generate_proposal_section',
        plan_id: planId,
        node_id: node.id,
        current_outline: outline,
        attached_context: enriched,
      }));

      // Wait for this node to complete (with timeout)
      try {
        await Promise.race([
          nodePromise,
          new Promise((resolve) => setTimeout(resolve, 90000)), // 90 second timeout per node
        ]);
      } catch (error) {
        console.error(`Error generating content for node ${node.id}:`, error);
      }

      // Small delay between nodes
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setGeneratingAllContent(false);
    generatingAllContentRef.current = false;
    pendingNodeResolversRef.current.clear();
    setGenerationProgress({ current: 0, total: 0, currentNode: '' });

    // Backend awards points when all nodes have content; refresh and show
    // Add delay to ensure backend has processed all sections and awarded points
    setTimeout(() => {
      refreshPointsAndNotify('All content generated');
    }, 3000);
  };

  const triggerNodeGenerationViaAi = () => {
    if (!planId || !selectedNodeId) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      alert('WebSocket not connected. Please wait and try again.');
      return;
    }
    setGeneratingNodeContent(true);
    setNodeContent('Generating content...');

    const enriched = {
      proposals: attachedContext.proposals || [],
      games: attachedContext.games || [],
      activities: attachedContext.activities || [],
      frameworks: attachedContext.frameworks || [],
      tools: attachedContext.tools || [],
      psychometricTools: attachedContext.psychometricTools || [],
    };

    wsRef.current.send(JSON.stringify({
      command: 'generate_proposal_section',
      plan_id: planId,
      node_id: selectedNodeId,
      current_outline: outline,
      attached_context: enriched,
    }));
  };

  const handleGenerateNodeContent = () => {
    if (!planId || !selectedNodeId) {
      alert('Please select a node to generate content for.');
      return;
    }

    const runDirectResourceInsert = async () => {
      if (!selectedNode) return false;
      setGeneratingNodeContent(true);
      try {
        const applied = await tryApplyResourceScriptForNode(selectedNode);
        setGeneratingNodeContent(false);
        return applied;
      } catch (error) {
        console.error('Resource script insertion failed', error);
        setGeneratingNodeContent(false);
        return false;
      }
    };

    if (useResourceContentDirectly) {
      runDirectResourceInsert().then((applied) => {
        if (!applied) {
          triggerNodeGenerationViaAi();
        }
      });
    } else {
      triggerNodeGenerationViaAi();
    }
  };

  const handleCopyOutline = async () => {
    if (!outline || outline.length === 0) {
      return;
    }
    try {
      const outlineJson = JSON.stringify(outline, null, 2);
      await navigator.clipboard.writeText(outlineJson);
      setCopiedOutline(true);
      setTimeout(() => setCopiedOutline(false), 2000);
    } catch (error) {
      console.error('Failed to copy outline:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = JSON.stringify(outline, null, 2);
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedOutline(true);
        setTimeout(() => setCopiedOutline(false), 2000);
      } catch (err) {
        alert('Failed to copy outline. Please try selecting and copying manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleCopyNodeContent = async () => {
    if (!nodeContent) return;
    try {
      await navigator.clipboard.writeText(nodeContent);
      // Show temporary feedback
      const btn = document.getElementById('copy-content-btn');
      if (btn) {
        const originalText = btn.textContent;
        btn.textContent = 'Copied!';
        btn.style.color = '#48bb78';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.style.color = '#94a3b8';
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to copy content:', error);
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = nodeContent;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        const btn = document.getElementById('copy-content-btn');
        if (btn) {
          const originalText = btn.textContent;
          btn.textContent = 'Copied!';
          btn.style.color = '#48bb78';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.style.color = '#94a3b8';
          }, 2000);
        }
      } catch (err) {
        console.error('Fallback copy failed:', err);
        alert('Failed to copy content. Please select and copy manually.');
      }
      document.body.removeChild(textarea);
    }
  };

  // Load reference proposals for past proposals tab
  useEffect(() => {
    if (contextTab === 'proposal' && referenceProposals.length === 0 && !loadingReferenceProposals) {
      setLoadingReferenceProposals(true);
      apiService.getReferenceProposals()
        .then(data => {
          setReferenceProposals(data.proposals || []);
        })
        .catch(error => {
          console.error('Error loading reference proposals:', error);
        })
        .finally(() => {
          setLoadingReferenceProposals(false);
        });
    }
  }, [contextTab]);

  // Extract text from uploaded file
  const extractTextFromFile = async (file, itemId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/proposals/extract-file-text/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success && response.data.extracted_text) {
        setContextItems(prev =>
          prev.map(item =>
            item.id === itemId
              ? { ...item, content: response.data.extracted_text, extracting: false }
              : item
          )
        );
      } else {
        setContextItems(prev =>
          prev.map(item =>
            item.id === itemId ? { ...item, extracting: false } : item
          )
        );
        alert(`Failed to extract text from ${file.name}`);
      }
    } catch (error) {
      console.error('Error extracting text from file:', error);
      setContextItems(prev =>
        prev.map(item =>
          item.id === itemId ? { ...item, extracting: false } : item
        )
      );
      alert(`Error extracting text from ${file.name}: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleAutoGenerate = () => {
    if (!planId) {
      alert('Please set a plan identifier before generating.');
      return;
    }
    
    // Build context items (files, text, proposals) - filter out items without content
    const contextItemsForLLM = contextItems
      .filter(item => item.content && item.content.trim())
      .map(item => ({
        type: item.type,
        fileName: item.fileName || undefined,
        proposalId: item.proposalId || undefined,
        text: item.content,
        description: item.description || ''
      }));
    
    // Debug: Log context items to verify comments are included
    if (contextItemsForLLM.length > 0) {
      console.log('LP_DEBUG: Context items being sent to LLM:', contextItemsForLLM.map(item => ({
        type: item.type,
        name: item.fileName || item.proposalId || 'Unknown',
        hasDescription: !!item.description,
        description: item.description
      })));
    }
    
    const payload = {
      command: 'auto_generate_proposal',
      plan_id: planId,
      current_outline: outline,
      attached_context: attachedContext,
      user_command: autoUserCommand,
      client_name: clientName.trim(),
      template: autoTemplate,
      // Optional context fields
      cohort_size: cohortSize || undefined,
      age_range: ageRange || undefined,
      roles_functions: rolesFunctions || undefined,
      language: language || undefined,
      delivery_mode: deliveryMode || undefined,
      industry: industry || undefined,
      company_size: companySize || undefined,
      geographic_region: geographicRegion || undefined,
      timeline_urgency: timelineUrgency || undefined,
      // Context items (files, text, past proposals)
      context_items: contextItemsForLLM.length > 0 ? contextItemsForLLM : undefined,
    };

    // Store payload for continuation after preliminary proposal acceptance
    pendingProposalPayloadRef.current = payload;

    const sendPayload = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        setTimeout(sendPayload, 200);
        return;
      }
      setAutoGenerating(true);
      setOutlineBusy(true);
      setReasoningLog([]);
      setProgressMessage('Starting…');
      wsRef.current.send(JSON.stringify(payload));
    };

    const connected = ensureConnection();
    if (connected) {
      sendPayload();
    } else {
      setTimeout(sendPayload, 400);
    }
  };

  const handleDownloadPreliminaryProposalPdf = () => {
    if (!preliminaryProposalText || !preliminaryProposalText.trim()) {
      alert('No preliminary proposal to download.');
      return;
    }
    setDownloadingPreliminaryPdf(true);
    try {
      const htmlContent = renderMarkdown(preliminaryProposalText);
      const sourceDiv = document.createElement('div');
      sourceDiv.style.cssText = 'padding: 24px; font-size: 14px; line-height: 1.6; color: #111827; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; max-width: 800px; overflow: hidden;';
      sourceDiv.innerHTML = '<style>table { table-layout: fixed !important; width: 100% !important; } th, td { overflow-wrap: break-word !important; word-break: break-word !important; word-wrap: break-word !important; white-space: normal !important; box-sizing: border-box !important; }</style>' + htmlContent;
      sourceDiv.innerHTML = htmlContent;
      document.body.appendChild(sourceDiv);

      const opt = {
        margin: [15, 15, 15, 15],
        filename: `Preliminary_Proposal_${planId || 'proposal'}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };
      html2pdf().set(opt).from(sourceDiv).save().then(() => {
        document.body.removeChild(sourceDiv);
        setDownloadingPreliminaryPdf(false);
      }).catch((err) => {
        document.body.removeChild(sourceDiv);
        setDownloadingPreliminaryPdf(false);
        console.error('PDF download error:', err);
        alert('Failed to generate PDF. Please try again.');
      });
    } catch (err) {
      setDownloadingPreliminaryPdf(false);
      console.error('PDF download error:', err);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleRefinePreliminaryProposal = () => {
    if (!refinementInstructions.trim()) {
      alert('Please enter refinement instructions');
      return;
    }
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      alert('WebSocket not connected. Please wait and try again.');
      return;
    }
    
    setIsRefiningProposal(true);
    wsRef.current.send(JSON.stringify({
      command: 'refine_preliminary_proposal',
      plan_id: planId,
      instructions: refinementInstructions,
      current_proposal: preliminaryProposalText,
    }));
  };

  const handleAcceptPreliminaryProposal = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      alert('WebSocket not connected. Please wait and try again.');
      return;
    }
    
    setShowPreliminaryProposal(false);
    setAutoGenerating(true);
    setOutlineBusy(true);
    
    // Get the stored payload and send accept command with continuation
    const payload = pendingProposalPayloadRef.current || {};
    wsRef.current.send(JSON.stringify({
      command: 'accept_preliminary_proposal',
      plan_id: planId,
      final_proposal: preliminaryProposalText,
      attached_context: payload.attached_context || attachedContext,
      user_command: payload.user_command || autoUserCommand,
      client_name: payload.client_name || clientName.trim(),
      template: payload.template || autoTemplate,
      current_outline: payload.current_outline || outline,
      cohort_size: payload.cohort_size || cohortSize || undefined,
      age_range: payload.age_range || ageRange || undefined,
      roles_functions: payload.roles_functions || rolesFunctions || undefined,
      language: payload.language || language || undefined,
      delivery_mode: payload.delivery_mode || deliveryMode || undefined,
      industry: payload.industry || industry || undefined,
      company_size: payload.company_size || companySize || undefined,
      geographic_region: payload.geographic_region || geographicRegion || undefined,
      timeline_urgency: payload.timeline_urgency || timelineUrgency || undefined,
      context_items: payload.context_items || (contextItems.filter(item => item.content && item.content.trim()).map(item => ({
        type: item.type,
        fileName: item.fileName || undefined,
        proposalId: item.proposalId || undefined,
        text: item.content,
        description: item.description || ''
      }))),
      use_past_resources: payload.use_past_resources || false,
      use_only_past_resources: payload.use_only_past_resources || false,
    }));
  };

  const renderNode = (node, depth = 0) => {
    const isSelected = node.id === selectedNodeId;
    return (
      <div key={node.id} style={{ marginLeft: depth * 14, marginBottom: 6 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            padding: depth === 0 ? '12px 14px' : '10px 12px',
            borderRadius: depth === 0 ? 16 : 12,
            border: isSelected ? '1px solid rgba(88, 102, 242, 0.45)' : '1px solid rgba(15, 23, 42, 0.08)',
            background: isSelected ? 'rgba(88, 102, 242, 0.12)' : 'rgba(255, 255, 255, 0.75)',
            boxShadow: isSelected ? '0 15px 35px rgba(88, 102, 242, 0.25)' : '0 10px 30px rgba(15, 23, 42, 0.08)',
            cursor: 'pointer',
            transition: 'background 0.2s ease, box-shadow 0.2s ease',
            backdropFilter: 'blur(6px)',
          }}
          onClick={() => selectNode(node.id)}
        >
          <span style={{ flex: 1, fontSize: '12px', color: dashboardTheme.accentNavy }}>{node.title || '(untitled ingredient)'}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAddChild(node.id);
            }}
            style={{
              background: 'rgba(42, 178, 123, 0.18)',
              border: '1px solid rgba(42, 178, 123, 0.4)',
              color: '#1b7b56',
              borderRadius: 8,
              padding: '3px 8px',
              fontSize: 11,
              fontWeight: 600,
            }}
            title="Add child ingredient"
          >
            +
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteNode(node.id);
            }}
            style={{
              background: 'rgba(255, 123, 106, 0.18)',
              border: '1px solid rgba(255, 123, 106, 0.4)',
              color: dashboardTheme.accentCoral,
              borderRadius: 8,
              padding: '3px 8px',
              fontSize: 11,
              fontWeight: 600,
            }}
            title="Delete ingredient"
          >
            ×
          </button>
        </div>
        {node.children && node.children.length > 0 && (
          <div style={{ marginTop: 4 }}>{node.children.map((child) => renderNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div style={pageWrapperStyle}>
      {/* Intro Video Modal on Login / First Visit */}
      {showIntroVideo && typeof document !== 'undefined' &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.75)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 24,
              zIndex: 10000,
            }}
          >
            <div
              style={{
                width: 'min(900px, 95%)',
                background: '#0b1120',
                borderRadius: 24,
                padding: 16,
                boxShadow: '0 45px 90px rgba(15, 23, 42, 0.6)',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid rgba(148, 163, 184, 0.4)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                  paddingInline: 8,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: '#64748b',
                      fontWeight: 600,
                    }}
                  >
                    Welcome to Propulse
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: '#e2e8f0',
                    }}
                  >
                    Quick walkthrough of the Proposal Generator
                  </div>
                  <p
                    style={{
                      marginTop: 6,
                      fontSize: 13,
                      color: '#cbd5f5',
                      maxWidth: 520,
                    }}
                  >
                    Please watch this short video to understand how to use the new Propulse outline experience effectively.
                  </p>
                </div>
                <button
                  onClick={closeIntroVideo}
                  style={{
                    border: 'none',
                    background: 'rgba(15, 23, 42, 0.9)',
                    color: '#e2e8f0',
                    width: 36,
                    height: 36,
                    borderRadius: '999px',
                    fontSize: 18,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 10px 25px rgba(15, 23, 42, 0.7)',
                  }}
                  title="Close video"
                >
                  ×
                </button>
              </div>
              <div
                style={{
                  position: 'relative',
                  paddingBottom: '56.25%', // 16:9
                  height: 0,
                  overflow: 'hidden',
                  borderRadius: 18,
                  marginTop: 8,
                  background: '#020617',
                }}
              >
                <iframe
                  src="https://www.youtube.com/embed/0cEJ5nGZ34c?rel=0"
                  title="Propulse Overview"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </div>,
          document.body
        )}

      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 12px 60px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 24,
            background: 'rgba(255, 255, 255, 0.92)',
            borderRadius: 32,
            padding: '22px 28px',
            boxShadow: '0 35px 70px rgba(15, 23, 42, 0.12)',
          }}
        >
          <div>
            <h2 style={{ fontSize: 28, fontWeight: 600, margin: 0, color: dashboardTheme.accentNavy }}>
              Proposal Generator
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={planIdInput}
              onChange={(e) => setPlanIdInput(e.target.value.trim())}
              placeholder="Plan identifier"
              style={{ ...inputSurfaceStyle, minWidth: 220 }}
              onFocus={(e) => {
                e.target.style.borderColor = dashboardTheme.accentPrimary;
                e.target.style.boxShadow = '0 0 0 4px rgba(88, 102, 242, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(15, 23, 42, 0.08)';
                e.target.style.boxShadow = '0 5px 20px rgba(15, 23, 42, 0.08)';
              }}
            />
            <button
              onClick={() => {
                if (planId === planIdInput) {
                  loadOutline(planId, { selectFirstNode: true });
                } else {
                  setPlanId(planIdInput || DEFAULT_PLAN_ID);
                }
              }}
              style={{
                borderRadius: 18,
                border: 'none',
                background: dashboardTheme.accentPrimary,
                color: '#ffffff',
                padding: '12px 24px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 20px 40px rgba(88, 102, 242, 0.3)',
              }}
            >
              Load Plan
            </button>
            {planId && planId !== DEFAULT_PLAN_ID && (
              <button
                onClick={() => navigate(`/lesson-plan/${encodeURIComponent(planId)}`)}
                style={{
                  borderRadius: 18,
                  border: '1px solid rgba(34, 197, 94, 0.6)',
                  background: 'rgba(34, 197, 94, 0.12)',
                  color: '#166534',
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Open Lesson Plan →
              </button>
            )}
          </div>
        </div>

        {/* Preliminary Proposal Card */}
        {preliminaryProposal && (
          <div style={{ ...cardBaseStyle, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: dashboardTheme.accentNavy }}>Preliminary Proposal</h3>
              <button
                onClick={() => setShowPreliminaryProposal(!showPreliminaryProposal)}
                style={{
                  borderRadius: 20,
                  border: 'none',
                  background: showPreliminaryProposal ? dashboardTheme.accentPrimary : 'rgba(88, 102, 242, 0.12)',
                  color: showPreliminaryProposal ? '#ffffff' : dashboardTheme.accentPrimary,
                  padding: '8px 20px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: showPreliminaryProposal ? '0 15px 35px rgba(88, 102, 242, 0.3)' : 'none',
                }}
              >
                {showPreliminaryProposal ? 'Hide' : 'Show'}
              </button>
            </div>
            {showPreliminaryProposal && (
              <div style={{ marginTop: 12 }}>
                {/* Refinement Instructions */}
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: dashboardTheme.accentNavy,
                    }}
                  >
                    How would you like to refine this proposal?
                  </label>
                  <textarea
                    value={refinementInstructions}
                    onChange={(e) => setRefinementInstructions(e.target.value)}
                    placeholder="e.g., Make it more concise, add more detail to Phase 2, adjust the tone to be more executive-focused..."
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                  <button
                    onClick={handleRefinePreliminaryProposal}
                    disabled={isRefiningProposal || !refinementInstructions.trim()}
                    style={{
                      marginTop: '10px',
                      padding: '10px 20px',
                      backgroundColor: isRefiningProposal || !refinementInstructions.trim() ? '#ccc' : dashboardTheme.accentPrimary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isRefiningProposal || !refinementInstructions.trim() ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {isRefiningProposal ? 'Refining...' : 'Refine with AI'}
                  </button>
                </div>

                {/* Proposal Text Editor */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label
                      style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: dashboardTheme.accentNavy,
                      }}
                    >
                      Preliminary Proposal
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={handleDownloadPreliminaryProposalPdf}
                        disabled={downloadingPreliminaryPdf || !preliminaryProposalText?.trim()}
                        title="Download preliminary proposal as PDF (same formatting as view)"
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f3f4f6',
                          color: '#111827',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: downloadingPreliminaryPdf ? 'wait' : 'pointer',
                          fontSize: '13px',
                          fontWeight: 500,
                          opacity: !preliminaryProposalText?.trim() ? 0.5 : 1,
                        }}
                      >
                        {downloadingPreliminaryPdf ? 'Generating…' : 'Download PDF'}
                      </button>
                      <button
                        onClick={() => setPreliminaryProposalViewMode('view')}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: preliminaryProposalViewMode === 'view' ? dashboardTheme.accentPrimary : '#f3f4f6',
                          color: preliminaryProposalViewMode === 'view' ? 'white' : '#111827',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => setPreliminaryProposalViewMode('edit')}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: preliminaryProposalViewMode === 'edit' ? dashboardTheme.accentPrimary : '#f3f4f6',
                          color: preliminaryProposalViewMode === 'edit' ? 'white' : '#111827',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                  {preliminaryProposalViewMode === 'view' ? (
                    <div
                      style={{
                        width: '100%',
                        minHeight: '400px',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        backgroundColor: '#ffffff',
                        overflowY: 'auto',
                        ...insetCardStyle,
                      }}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(preliminaryProposalText) }}
                    />
                  ) : (
                    <textarea
                      value={preliminaryProposalText}
                      onChange={(e) => setPreliminaryProposalText(e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '400px',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        resize: 'vertical',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        ...insetCardStyle,
                      }}
                    />
                  )}
                  <div
                    style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#666',
                    }}
                  >
                    {preliminaryProposalText.length} characters
                  </div>
                </div>

                {/* Accept Button - only when waiting for acceptance (outline not yet building/built) */}
                {!outlineBusy && !autoGenerating && (!outline || outline.length === 0) && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button
                      onClick={handleAcceptPreliminaryProposal}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: dashboardTheme.accentPrimary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      Accept & Continue
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Concept Document Display */}
        {conceptDocument && (
          <div style={{ ...cardBaseStyle, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: dashboardTheme.accentNavy }}>Concept Document</h3>
              <button
                onClick={() => setShowConceptDoc(!showConceptDoc)}
                style={{
                  borderRadius: 20,
                  border: 'none',
                  background: showConceptDoc ? dashboardTheme.accentPrimary : 'rgba(88, 102, 242, 0.12)',
                  color: showConceptDoc ? '#ffffff' : dashboardTheme.accentPrimary,
                  padding: '8px 20px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: showConceptDoc ? '0 15px 35px rgba(88, 102, 242, 0.3)' : 'none',
                }}
              >
                {showConceptDoc ? 'Hide' : 'Show'}
              </button>
            </div>
            {showConceptDoc && (
              <div
                style={{
                  ...insetCardStyle,
                  marginTop: 12,
                  color: dashboardTheme.accentNavy,
                  fontSize: 13,
                  lineHeight: 1.8,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 500,
                  overflowY: 'auto',
                }}
              >
                {conceptDocument}
              </div>
            )}
          </div>
        )}

        <div style={{ ...cardBaseStyle, marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 18, color: dashboardTheme.accentNavy }}>Automatic Outline Generation</h3>
          <p style={{ fontSize: 12, color: dashboardTheme.accentSlate, marginTop: 4 }}>
            Provide a brief and attach resources. The generator produces a structured outline without generic fallbacks.
          </p>
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: dashboardTheme.accentSlate, marginBottom: 6 }}>Client Name</label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name (e.g., Ashok Leyland)"
              style={{
                ...inputSurfaceStyle,
                width: '100%',
                padding: '10px 14px',
                fontSize: 14,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = dashboardTheme.accentPrimary;
                e.target.style.boxShadow = '0 0 0 4px rgba(88, 102, 242, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(15, 23, 42, 0.08)';
                e.target.style.boxShadow = '0 5px 20px rgba(15, 23, 42, 0.08)';
              }}
            />
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: dashboardTheme.accentSlate, marginBottom: 6 }}>Client Brief</label>
            <textarea
              value={autoUserCommand}
              onChange={(e) => setAutoUserCommand(e.target.value)}
              placeholder="Client brief, objectives, constraints, tone…"
              rows={4}
              style={{
                ...inputSurfaceStyle,
                width: '100%',
                minHeight: 140,
                resize: 'vertical',
                lineHeight: 1.6,
                fontFamily: 'inherit',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = dashboardTheme.accentPrimary;
                e.target.style.boxShadow = '0 0 0 4px rgba(88, 102, 242, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(15, 23, 42, 0.08)';
                e.target.style.boxShadow = '0 5px 20px rgba(15, 23, 42, 0.08)';
              }}
            />
          </div>
          
          {/* Context Fields Section */}
          <div style={{ marginTop: 16, padding: '16px', background: 'rgba(15, 23, 42, 0.02)', borderRadius: 12, border: '1px solid rgba(15, 23, 42, 0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ margin: 0, fontSize: 14, color: dashboardTheme.accentNavy, fontWeight: 600 }}>Additional Context (Optional)</h4>
              <span style={{ marginLeft: 8, fontSize: 11, color: dashboardTheme.accentSlate }}>Help generate more contextualized proposals</span>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              {/* Essential Fields */}
              <div>
                <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>Cohort Size</label>
                <input
                  type="number"
                  value={cohortSize}
                  onChange={(e) => setCohortSize(e.target.value)}
                  placeholder="e.g., 25, 50, 100"
                  min="1"
                  style={{ ...inputSurfaceStyle, width: '100%', padding: '8px 10px', fontSize: 13 }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>Age Range</label>
                <select
                  value={ageRange}
                  onChange={(e) => setAgeRange(e.target.value)}
                  style={{ ...inputSurfaceStyle, width: '100%', padding: '8px 10px', fontSize: 13, cursor: 'pointer' }}
                >
                  <option value="">Select age range</option>
                  <option value="18-25">18-25</option>
                  <option value="26-35">26-35</option>
                  <option value="36-45">36-45</option>
                  <option value="46-55">46-55</option>
                  <option value="55+">55+</option>
                  <option value="Mixed">Mixed</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>Roles/Functions</label>
                <input
                  type="text"
                  value={rolesFunctions}
                  onChange={(e) => setRolesFunctions(e.target.value)}
                  placeholder="e.g., Managers, Frontline Staff"
                  style={{ ...inputSurfaceStyle, width: '100%', padding: '8px 10px', fontSize: 13 }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>Language</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  style={{ ...inputSurfaceStyle, width: '100%', padding: '8px 10px', fontSize: 13, cursor: 'pointer' }}
                >
                  <option value="">Select language</option>
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Tamil">Tamil</option>
                  <option value="Telugu">Telugu</option>
                  <option value="Mixed">Mixed</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>Delivery Mode</label>
                <select
                  value={deliveryMode}
                  onChange={(e) => setDeliveryMode(e.target.value)}
                  style={{ ...inputSurfaceStyle, width: '100%', padding: '8px 10px', fontSize: 13, cursor: 'pointer' }}
                >
                  <option value="">Select delivery mode</option>
                  <option value="In-Person">In-Person</option>
                  <option value="Virtual">Virtual</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Not Specified">Not Specified</option>
                </select>
              </div>
              
              {/* Optional Fields */}
              <div>
                <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>Industry</label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  style={{ ...inputSurfaceStyle, width: '100%', padding: '8px 10px', fontSize: 13, cursor: 'pointer' }}
                >
                  <option value="">Select industry</option>
                  <option value="Retail">Retail</option>
                  <option value="Food Service">Food Service</option>
                  <option value="Technology">Technology</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Financial Services">Financial Services</option>
                  <option value="Education">Education</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>Company Size</label>
                <select
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                  style={{ ...inputSurfaceStyle, width: '100%', padding: '8px 10px', fontSize: 13, cursor: 'pointer' }}
                >
                  <option value="">Select company size</option>
                  <option value="1-50">1-50</option>
                  <option value="51-200">51-200</option>
                  <option value="201-1000">201-1000</option>
                  <option value="1000-5000">1000-5000</option>
                  <option value="5000+">5000+</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>Geographic Region</label>
                <input
                  type="text"
                  value={geographicRegion}
                  onChange={(e) => setGeographicRegion(e.target.value)}
                  placeholder="e.g., Mumbai, South India"
                  style={{ ...inputSurfaceStyle, width: '100%', padding: '8px 10px', fontSize: 13 }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>Timeline/Urgency</label>
                <select
                  value={timelineUrgency}
                  onChange={(e) => setTimelineUrgency(e.target.value)}
                  style={{ ...inputSurfaceStyle, width: '100%', padding: '8px 10px', fontSize: 13, cursor: 'pointer' }}
                >
                  <option value="">Select timeline</option>
                  <option value="ASAP">ASAP</option>
                  <option value="1-2 months">1-2 months</option>
                  <option value="3-6 months">3-6 months</option>
                  <option value="6+ months">6+ months</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Unified Context Section with Tabs */}
          <div style={{ marginTop: 16, padding: '16px', background: 'rgba(15, 23, 42, 0.02)', borderRadius: 12, border: '1px solid rgba(15, 23, 42, 0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ margin: 0, fontSize: 14, color: dashboardTheme.accentNavy, fontWeight: 600 }}>Add Context</h4>
              <span style={{ marginLeft: 8, fontSize: 11, color: dashboardTheme.accentSlate }}>Files, text, or past proposals with instructions</span>
            </div>
            
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid rgba(15, 23, 42, 0.1)' }}>
              <button
                onClick={() => setContextTab('file')}
                style={{
                  padding: '8px 16px',
                  background: contextTab === 'file' ? dashboardTheme.accentPrimary : 'transparent',
                  color: contextTab === 'file' ? '#ffffff' : dashboardTheme.accentSlate,
                  border: 'none',
                  borderBottom: contextTab === 'file' ? `2px solid ${dashboardTheme.accentPrimary}` : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: contextTab === 'file' ? 600 : 400,
                  borderRadius: '4px 4px 0 0'
                }}
              >
                File
              </button>
              <button
                onClick={() => setContextTab('text')}
                style={{
                  padding: '8px 16px',
                  background: contextTab === 'text' ? dashboardTheme.accentPrimary : 'transparent',
                  color: contextTab === 'text' ? '#ffffff' : dashboardTheme.accentSlate,
                  border: 'none',
                  borderBottom: contextTab === 'text' ? `2px solid ${dashboardTheme.accentPrimary}` : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: contextTab === 'text' ? 600 : 400,
                  borderRadius: '4px 4px 0 0'
                }}
              >
                Text
              </button>
              <button
                onClick={() => setContextTab('proposal')}
                style={{
                  padding: '8px 16px',
                  background: contextTab === 'proposal' ? dashboardTheme.accentPrimary : 'transparent',
                  color: contextTab === 'proposal' ? '#ffffff' : dashboardTheme.accentSlate,
                  border: 'none',
                  borderBottom: contextTab === 'proposal' ? `2px solid ${dashboardTheme.accentPrimary}` : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: contextTab === 'proposal' ? 600 : 400,
                  borderRadius: '4px 4px 0 0'
                }}
              >
                Past Proposal
              </button>
            </div>
            
            {/* Tab Content */}
            {contextTab === 'file' && (
              <div>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.pptx,.xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const itemId = ++contextItemIdCounter.current;
                      const newItem = {
                        id: itemId,
                        type: 'file',
                        fileName: file.name,
                        content: '',
                        description: '',
                        extracting: true
                      };
                      setContextItems(prev => [...prev, newItem]);
                      extractTextFromFile(file, itemId);
                    }
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                  id="file-upload-input"
                />
                <label
                  htmlFor="file-upload-input"
                  style={{
                    display: 'inline-block',
                    padding: '8px 16px',
                    background: dashboardTheme.accentPrimary,
                    color: '#ffffff',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                    marginBottom: 12
                  }}
                >
                  + Upload File (PDF, DOC, DOCX, TXT, PPTX, Excel)
                </label>
              </div>
            )}
            
            {contextTab === 'text' && (
              <div>
                {/* List of Text Entries */}
                {contextItems
                  .filter(item => item.type === 'text')
                  .map((textItem) => (
                    <div
                      key={textItem.id}
                      style={{
                        padding: '12px',
                        background: '#ffffff',
                        borderRadius: 8,
                        border: '1px solid rgba(15, 23, 42, 0.1)',
                        marginBottom: 12
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ 
                          fontSize: 10, 
                          padding: '2px 6px', 
                          background: dashboardTheme.accentSoftBlue, 
                          color: dashboardTheme.accentPrimary,
                          borderRadius: 4,
                          fontWeight: 500
                        }}>
                          TEXT CONTEXT
                        </span>
                        <button
                          onClick={() => {
                            setContextItems(prev => prev.filter(item => item.id !== textItem.id));
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: dashboardTheme.accentCoral,
                            cursor: 'pointer',
                            fontSize: 12,
                            padding: '4px 8px',
                            fontWeight: 500
                          }}
                        >
                          Remove
                        </button>
                      </div>
                      
                      {/* Text Content */}
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>
                          Text Content:
                        </label>
                        <textarea
                          value={textItem.content || ''}
                          onChange={(e) => {
                            setContextItems(prev =>
                              prev.map(item =>
                                item.id === textItem.id ? { ...item, content: e.target.value } : item
                              )
                            );
                          }}
                          placeholder="Paste or type text here..."
                          style={{
                            ...inputSurfaceStyle,
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: 13,
                            minHeight: '100px',
                            resize: 'vertical',
                            fontFamily: 'inherit'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                
                {/* Add New Text Entry Button */}
                <button
                  onClick={() => {
                    const itemId = ++contextItemIdCounter.current;
                    setContextItems(prev => [...prev, {
                      id: itemId,
                      type: 'text',
                      content: '',
                      description: '' // Empty for text entries
                    }]);
                  }}
                  style={{
                    padding: '10px 16px',
                    background: dashboardTheme.accentPrimary,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <span>+</span>
                  <span>Add Text Context</span>
                </button>
                
                {contextItems.filter(item => item.type === 'text').length === 0 && (
                  <div style={{ fontSize: 12, color: dashboardTheme.accentSlate, marginTop: 12, fontStyle: 'italic', textAlign: 'center' }}>
                    No text entries yet. Click "+ Add Text Context" to add one.
                  </div>
                )}
              </div>
            )}
            
            {contextTab === 'proposal' && (
              <div>
                {loadingReferenceProposals ? (
                  <div style={{ fontSize: 12, color: dashboardTheme.accentSlate, fontStyle: 'italic', marginBottom: 12 }}>
                    Loading past proposals...
                  </div>
                ) : (
                  <>
                    {/* Shared Comment Field for All Proposals */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: 12, 
                        fontWeight: 500,
                        color: dashboardTheme.accentNavy, 
                        marginBottom: 8 
                      }}>
                        Comments / Instructions for All Proposals (Optional):
                      </label>
                      <textarea
                        value={sharedProposalComment}
                        onChange={(e) => setSharedProposalComment(e.target.value)}
                        placeholder="e.g., 'Use these proposals as the only source of topics', 'Reference these for structure but adapt content', etc."
                        style={{
                          ...inputSurfaceStyle,
                          width: '100%',
                          padding: '10px 12px',
                          fontSize: 12,
                          minHeight: '70px',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          marginBottom: 8
                        }}
                      />
                      <div style={{ fontSize: 11, color: dashboardTheme.accentSlate }}>
                        This comment will apply to all selected proposals when you add them.
                      </div>
                    </div>
                    
                    {/* Search Bar */}
                    <input
                      type="text"
                      value={proposalSearchQuery}
                      onChange={(e) => setProposalSearchQuery(e.target.value)}
                      placeholder="Search past proposals..."
                      style={{
                        ...inputSurfaceStyle,
                        width: '100%',
                        padding: '8px 10px',
                        fontSize: 13,
                        marginBottom: 12
                      }}
                    />
                    
                    {/* Filtered Proposals List with Checkboxes */}
                    {proposalSearchQuery.trim() && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          border: '1px solid rgba(15, 23, 42, 0.1)',
                          borderRadius: 8,
                          background: '#ffffff',
                          marginBottom: 12
                        }}>
                          {referenceProposals
                            .filter(p => 
                              p.title.toLowerCase().includes(proposalSearchQuery.toLowerCase()) ||
                              (p.content && p.content.toLowerCase().includes(proposalSearchQuery.toLowerCase()))
                            )
                            .slice(0, 10) // Limit to 10 results
                            .map(proposal => (
                              <div
                                key={proposal.id}
                                style={{
                                  padding: '10px 12px',
                                  borderBottom: '1px solid rgba(15, 23, 42, 0.05)',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 10,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.03)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                onClick={(e) => {
                                  // Don't toggle if clicking on preview link
                                  if (e.target.tagName === 'A' || e.target.closest('a')) {
                                    return;
                                  }
                                  const newSelected = new Set(selectedProposalIds);
                                  if (newSelected.has(proposal.id)) {
                                    newSelected.delete(proposal.id);
                                  } else {
                                    newSelected.add(proposal.id);
                                  }
                                  setSelectedProposalIds(newSelected);
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedProposalIds.has(proposal.id)}
                                  onChange={() => {
                                    const newSelected = new Set(selectedProposalIds);
                                    if (newSelected.has(proposal.id)) {
                                      newSelected.delete(proposal.id);
                                    } else {
                                      newSelected.add(proposal.id);
                                    }
                                    setSelectedProposalIds(newSelected);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ marginTop: 2, cursor: 'pointer' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 500, color: dashboardTheme.accentNavy }}>
                                    {proposal.title}
                                  </div>
                                  {proposal.content && (
                                    <div style={{ 
                                      fontSize: 11, 
                                      color: dashboardTheme.accentSlate, 
                                      marginTop: 4,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {proposal.content.substring(0, 100)}...
                                    </div>
                                  )}
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setSelectedProposalPreview({
                                        id: proposal.id,
                                        title: proposal.title,
                                        content: proposal.content || ''
                                      });
                                    }}
                                    style={{
                                      fontSize: 11,
                                      color: dashboardTheme.accentPrimary,
                                      textDecoration: 'none',
                                      marginTop: 4,
                                      display: 'inline-block'
                                    }}
                                  >
                                    Preview content
                                  </a>
                                </div>
                              </div>
                            ))}
                          {referenceProposals.filter(p => 
                            p.title.toLowerCase().includes(proposalSearchQuery.toLowerCase()) ||
                            (p.content && p.content.toLowerCase().includes(proposalSearchQuery.toLowerCase()))
                          ).length === 0 && (
                            <div style={{ padding: '12px', fontSize: 12, color: dashboardTheme.accentSlate, textAlign: 'center' }}>
                              No proposals found matching "{proposalSearchQuery}"
                            </div>
                          )}
                        </div>
                        
                        {/* Add Selected Button */}
                        {selectedProposalIds.size > 0 && (
                          <button
                            onClick={() => {
                              const selectedProposals = referenceProposals.filter(p => selectedProposalIds.has(p.id));
                              selectedProposals.forEach(proposal => {
                                const itemId = ++contextItemIdCounter.current;
                                setContextItems(prev => [...prev, {
                                  id: itemId,
                                  type: 'proposal',
                                  proposalId: proposal.id,
                                  fileName: proposal.title,
                                  content: proposal.content || '',
                                  description: sharedProposalComment || '' // Use shared comment
                                }]);
                              });
                              setSelectedProposalIds(new Set()); // Clear selection
                              setProposalSearchQuery(''); // Clear search
                            }}
                            style={{
                              padding: '8px 16px',
                              background: dashboardTheme.accentPrimary,
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 500
                            }}
                          >
                            Add {selectedProposalIds.size} Selected Proposal{selectedProposalIds.size > 1 ? 's' : ''}
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* Proposal Preview Modal */}
                    {selectedProposalPreview && (
                      <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                        padding: '20px'
                      }}
                      onClick={() => setSelectedProposalPreview(null)}
                      >
                        <div
                          style={{
                            background: '#ffffff',
                            borderRadius: 12,
                            maxWidth: '800px',
                            width: '100%',
                            maxHeight: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Header */}
                          <div style={{
                            padding: '16px 20px',
                            borderBottom: '1px solid rgba(15, 23, 42, 0.1)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: dashboardTheme.accentNavy }}>
                              {selectedProposalPreview.title}
                            </h3>
                            <button
                              onClick={() => setSelectedProposalPreview(null)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                fontSize: 20,
                                color: dashboardTheme.accentSlate,
                                cursor: 'pointer',
                                padding: '0 8px'
                              }}
                            >
                              ×
                            </button>
                          </div>
                          
                          {/* Content Preview */}
                          <div style={{
                            padding: '20px',
                            overflowY: 'auto',
                            flex: 1,
                            fontSize: 13,
                            lineHeight: 1.6,
                            color: dashboardTheme.accentNavy
                          }}>
                            <pre style={{
                              whiteSpace: 'pre-wrap',
                              wordWrap: 'break-word',
                              fontFamily: 'inherit',
                              margin: 0
                            }}>
                              {selectedProposalPreview.content || 'No content available'}
                            </pre>
                          </div>
                          
                          {/* Footer with Actions */}
                          <div style={{
                            padding: '16px 20px',
                            borderTop: '1px solid rgba(15, 23, 42, 0.1)',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 12
                          }}>
                            <button
                              onClick={() => setSelectedProposalPreview(null)}
                              style={{
                                padding: '8px 16px',
                                background: 'transparent',
                                border: `1px solid ${dashboardTheme.accentSlate}`,
                                color: dashboardTheme.accentSlate,
                                borderRadius: 8,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 500
                              }}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            
            {/* Context Items List (all types) */}
            {contextItems.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                {contextItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      padding: '12px',
                      background: '#ffffff',
                      borderRadius: 8,
                      border: '1px solid rgba(15, 23, 42, 0.1)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ 
                          fontSize: 10, 
                          padding: '2px 6px', 
                          background: dashboardTheme.accentSoftBlue, 
                          color: dashboardTheme.accentPrimary,
                          borderRadius: 4,
                          fontWeight: 500
                        }}>
                          {item.type === 'file' ? '📄 FILE' : item.type === 'text' ? '📝 TEXT' : '📋 PROPOSAL'}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 500, color: dashboardTheme.accentNavy }}>
                          {item.fileName || 'Text Content'}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setContextItems(prev => prev.filter(i => i.id !== item.id));
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: dashboardTheme.accentCoral,
                          cursor: 'pointer',
                          fontSize: 12,
                          padding: '4px 8px'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    
                    {/* Description/Instructions textbox - always visible */}
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ display: 'block', fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 4 }}>
                        Description / Instructions (What is this? How should the LLM use it?):
                      </label>
                      <textarea
                        value={item.description}
                        onChange={(e) => {
                          setContextItems(prev =>
                            prev.map(i =>
                              i.id === item.id ? { ...i, description: e.target.value } : i
                            )
                          );
                        }}
                        placeholder="e.g., 'This contains client requirements. Use it to understand their specific needs when generating the proposal.'"
                        style={{
                          ...inputSurfaceStyle,
                          width: '100%',
                          padding: '8px 10px',
                          fontSize: 12,
                          minHeight: '60px',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                    
                    {/* Status/Content preview */}
                    {item.type === 'file' && (
                      <>
                        {item.extracting ? (
                          <div style={{ fontSize: 12, color: dashboardTheme.accentSlate, fontStyle: 'italic' }}>
                            Extracting text...
                          </div>
                        ) : item.content ? (
                          <div style={{ fontSize: 11, color: dashboardTheme.accentSlate }}>
                            ✅ Extracted {item.content.length} characters
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: dashboardTheme.accentCoral }}>
                            ⚠️ Failed to extract text
                          </div>
                        )}
                      </>
                    )}
                    {item.type === 'text' && item.content && (
                      <div style={{ fontSize: 11, color: dashboardTheme.accentSlate }}>
                            ✅ {item.content.length} characters
                          </div>
                    )}
                    {item.type === 'proposal' && item.content && (
                      <div style={{ fontSize: 11, color: dashboardTheme.accentSlate }}>
                            ✅ {item.content.length} characters
                          </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 220px' }}>
              <label style={{ display: 'block', fontSize: 12, color: dashboardTheme.accentSlate, marginBottom: 6 }}>Template</label>
              <select
                value={autoTemplate}
                onChange={(e) => setAutoTemplate(e.target.value)}
                style={{ ...inputSurfaceStyle, width: '100%', cursor: 'pointer' }}
                onFocus={(e) => {
                  e.target.style.borderColor = dashboardTheme.accentPrimary;
                  e.target.style.boxShadow = '0 0 0 4px rgba(88, 102, 242, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(15, 23, 42, 0.08)';
                  e.target.style.boxShadow = '0 5px 20px rgba(15, 23, 42, 0.08)';
                }}
              >
                <option value="generic">Generic Proposal</option>
                <option value="half_day">½ Day Workshop</option>
                <option value="journey_1">Journey (2 Days)</option>
                <option value="journey_2">Journey (Multi-Month)</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <ResourceSelector
                attachedContext={attachedContext}
                setAttachedContext={setAttachedContext}
                resourceAttachModal={resourceAttachModal}
                closeResourceAttachModal={closeResourceAttachModal}
                resourceAttachCategory={resourceAttachCategory}
                setResourceAttachCategory={setResourceAttachCategory}
                resourceAttachSearch={resourceAttachSearch}
                setResourceAttachSearch={setResourceAttachSearch}
                filteredAttachResources={filteredAttachResources}
                handleResourceSelectionForChild={handleResourceSelectionForChild}
                handleManualChildCreation={handleManualChildCreation}
                resourceAttachLoading={resourceAttachLoading}
                resourceAttachPagination={resourceAttachPagination}
                handleResourceAttachPageChange={handleResourceAttachPageChange}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
            <button
              onClick={handleAutoGenerate}
              disabled={autoGenerating || connecting}
              style={{
                borderRadius: 20,
                border: 'none',
                background: autoGenerating || connecting ? 'rgba(88, 102, 242, 0.35)' : dashboardTheme.accentPrimary,
                color: '#ffffff',
                padding: '12px 28px',
                fontSize: 14,
                fontWeight: 600,
                cursor: (autoGenerating || connecting) ? 'not-allowed' : 'pointer',
                opacity: (autoGenerating || connecting) ? 0.7 : 1,
                boxShadow: autoGenerating || connecting ? 'none' : '0 20px 40px rgba(88, 102, 242, 0.3)',
              }}
            >
              {autoGenerating ? 'Generating Outline…' : connecting ? 'Connecting…' : (outline && outline.length > 0 ? 'Regenerate Outline' : 'Generate Outline')}
            </button>
            {outline && outline.length > 0 && !autoGenerating && !connecting && (
              <button
                onClick={handleAutoGenerate}
                disabled={autoGenerating || connecting}
                style={{
                  borderRadius: 20,
                  border: `2px solid ${dashboardTheme.accentPrimary}`,
                  background: 'transparent',
                  color: dashboardTheme.accentPrimary,
                  padding: '12px 28px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: (autoGenerating || connecting) ? 'not-allowed' : 'pointer',
                  opacity: (autoGenerating || connecting) ? 0.7 : 1,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!autoGenerating && !connecting) {
                    e.target.style.background = 'rgba(88, 102, 242, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!autoGenerating && !connecting) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                Continue Building
              </button>
            )}
            {progressMessage && <span style={{ fontSize: 12, color: dashboardTheme.accentSlate }}>{progressMessage}</span>}
          </div>
          {reasoningLog.length > 0 && (
            <div
              style={{
                ...insetCardStyle,
                marginTop: 14,
                maxHeight: 160,
                overflowY: 'auto',
                border: '1px dashed rgba(88, 102, 242, 0.25)',
                width: '100%',
                boxSizing: 'border-box',
              }}
              ref={reasoningContainerRef}
            >
              {reasoningLog.map((entry, idx) => (
                <div
                  key={idx}
                  style={{
                    fontSize: 12,
                    color: dashboardTheme.accentPrimary,
                    marginBottom: 6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    width: '100%',
                  }}
                >
                  {entry}
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            ...cardBaseStyle,
            display: 'flex',
            gap: 0,
            minHeight: 520,
            padding: 0,
          }}
        >
          <div
            style={{
              width: 320,
              borderRight: '1px solid rgba(15, 23, 42, 0.08)',
              padding: 22,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              background: 'rgba(255, 255, 255, 0.85)',
              borderTopLeftRadius: 28,
              borderBottomLeftRadius: 28,
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              <button
                onClick={handleAddRoot}
                style={{
                  borderRadius: 12,
                  border: '1px solid #93c5fd',
                  background: '#93c5fd',
                  color: '#1e3a8a',
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: (savingOutline || loadingOutline) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: (savingOutline || loadingOutline) ? 0.6 : 1,
                  boxShadow: '0 4px 12px rgba(147, 197, 253, 0.3)',
                }}
                disabled={savingOutline || loadingOutline}
                onMouseEnter={(e) => {
                  if (!savingOutline && !loadingOutline) {
                    e.target.style.background = '#7dd3fc';
                    e.target.style.borderColor = '#7dd3fc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!savingOutline && !loadingOutline) {
                    e.target.style.background = '#93c5fd';
                    e.target.style.borderColor = '#93c5fd';
                  }
                }}
              >
                + Root Ingredient
              </button>
              <button
                onClick={handleSaveOutlineClick}
                style={{
                  borderRadius: 12,
                  border: '1px solid #86efac',
                  background: '#86efac',
                  color: '#14532d',
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: (savingOutline || loadingOutline) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: (savingOutline || loadingOutline) ? 0.6 : 1,
                  boxShadow: '0 4px 12px rgba(134, 239, 172, 0.3)',
                }}
                disabled={savingOutline || loadingOutline}
                onMouseEnter={(e) => {
                  if (!savingOutline && !loadingOutline) {
                    e.target.style.background = '#6ee7b7';
                    e.target.style.borderColor = '#6ee7b7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!savingOutline && !loadingOutline) {
                    e.target.style.background = '#86efac';
                    e.target.style.borderColor = '#86efac';
                  }
                }}
              >
                {savingOutline ? 'Saving…' : 'Save Outline'}
              </button>
              <button
                onClick={handleCopyOutline}
                disabled={loadingOutline || outline.length === 0 || outlineLocked}
                style={{
                  borderRadius: 12,
                  border: copiedOutline
                    ? '1px solid #86efac'
                    : '1px solid #c4b5fd',
                  background: outline.length === 0
                    ? '#f3f4f6'
                    : copiedOutline
                      ? '#86efac'
                      : '#c4b5fd',
                  color: outline.length === 0
                    ? '#9ca3af'
                    : copiedOutline
                      ? '#14532d'
                      : '#4c1d95',
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: (outline.length === 0 || outlineLocked) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  opacity: (outline.length === 0 || outlineLocked) ? 0.6 : 1,
                  boxShadow: outline.length === 0
                    ? 'none'
                    : copiedOutline
                      ? '0 4px 12px rgba(134, 239, 172, 0.3)'
                      : '0 4px 12px rgba(196, 181, 253, 0.3)',
                }}
                title={outline.length === 0 ? 'No outline to copy' : copiedOutline ? 'Copied!' : 'Copy outline JSON to clipboard'}
                onMouseEnter={(e) => {
                  if (outline.length > 0 && !outlineLocked) {
                    if (copiedOutline) {
                      e.target.style.background = '#6ee7b7';
                      e.target.style.borderColor = '#6ee7b7';
                    } else {
                      e.target.style.background = '#a78bfa';
                      e.target.style.borderColor = '#a78bfa';
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  if (outline.length > 0 && !outlineLocked) {
                    if (copiedOutline) {
                      e.target.style.background = '#86efac';
                      e.target.style.borderColor = '#86efac';
                    } else {
                      e.target.style.background = '#c4b5fd';
                      e.target.style.borderColor = '#c4b5fd';
                    }
                  }
                }}
              >
                {copiedOutline ? 'Copied!' : 'Copy Outline'}
              </button>
              <button
                onClick={handleNewProposalClick}
                disabled={loadingOutline}
                style={{
                  borderRadius: 12,
                  border: '1px solid #60a5fa',
                  background: loadingOutline ? '#f3f4f6' : '#60a5fa',
                  color: loadingOutline ? '#9ca3af' : '#1e3a8a',
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: loadingOutline ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                  opacity: loadingOutline ? 0.6 : 1,
                  boxShadow: loadingOutline ? 'none' : '0 4px 12px rgba(96, 165, 250, 0.3)',
                  marginRight: '8px',
                }}
                title="Create a new proposal. Current proposal will be saved and available in Management."
                onMouseEnter={(e) => {
                  if (!loadingOutline) {
                    e.target.style.background = '#3b82f6';
                    e.target.style.borderColor = '#3b82f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loadingOutline) {
                    e.target.style.background = '#60a5fa';
                    e.target.style.borderColor = '#60a5fa';
                  }
                }}
              >
                New Proposal
              </button>
              <button
                onClick={handleDeleteOutlineClick}
                disabled={loadingOutline || outline.length === 0}
                style={{
                  borderRadius: 12,
                  border: '1px solid #fca5a5',
                  background: outline.length === 0 ? '#f3f4f6' : '#fca5a5',
                  color: outline.length === 0 ? '#9ca3af' : '#7f1d1d',
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: outline.length === 0 ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                  opacity: outline.length === 0 ? 0.6 : 1,
                  boxShadow: outline.length === 0 ? 'none' : '0 4px 12px rgba(252, 165, 165, 0.3)',
                }}
                title={outline.length === 0 ? 'No proposal to delete' : 'Delete entire proposal permanently. This will remove it from Management and cannot be undone.'}
                onMouseEnter={(e) => {
                  if (outline.length > 0) {
                    e.target.style.background = '#f87171';
                    e.target.style.borderColor = '#f87171';
                  }
                }}
                onMouseLeave={(e) => {
                  if (outline.length > 0) {
                    e.target.style.background = '#fca5a5';
                    e.target.style.borderColor = '#fca5a5';
                  }
                }}
              >
                Delete Proposal
              </button>
              <button
                onClick={handleGenerateAllContent}
                disabled={loadingOutline || outline.length === 0 || generatingAllContent || connecting || outlineLocked}
                style={{
                  borderRadius: 12,
                  border: '1px solid #a78bfa',
                  background: (outline.length === 0 || generatingAllContent || outlineLocked) ? '#f3f4f6' : '#a78bfa',
                  color: (outline.length === 0 || generatingAllContent || outlineLocked) ? '#9ca3af' : '#4c1d95',
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: (outline.length === 0 || generatingAllContent || outlineLocked) ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                  opacity: (outline.length === 0 || generatingAllContent || outlineLocked) ? 0.6 : 1,
                  boxShadow: (outline.length === 0 || generatingAllContent || outlineLocked) ? 'none' : '0 4px 12px rgba(167, 139, 250, 0.3)',
                }}
                title={
                  resourceScriptApplying
                    ? 'Applying resource scripts…'
                    : outlineBusy
                      ? 'Outline generation in progress…'
                      : generatingAllContent
                        ? `Generating ${generationProgress.current}/${generationProgress.total}: ${generationProgress.currentNode}`
                        : 'Generate content for all sections'
                }
                onMouseEnter={(e) => {
                  if (outline.length > 0 && !generatingAllContent && !outlineLocked) {
                    e.target.style.background = '#8b5cf6';
                    e.target.style.borderColor = '#8b5cf6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (outline.length > 0 && !generatingAllContent && !outlineLocked) {
                    e.target.style.background = '#a78bfa';
                    e.target.style.borderColor = '#a78bfa';
                  }
                }}
              >
                {generatingAllContent
                  ? `Generating ${generationProgress.current}/${generationProgress.total}...`
                  : 'Generate All Content'}
              </button>
            </div>
            {generatingAllContent && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280' }}>
                {generationProgress.currentNode && (
                  <div>Current: {generationProgress.currentNode}</div>
                )}
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 6, minHeight: 0, maxHeight: '500px' }}>
              {loadingOutline ? (
                <div style={{ color: '#6b7280', fontSize: 13 }}>Loading outline…</div>
              ) : outline.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: 13 }}>Outline is empty. Generate automatically or add a root ingredient.</div>
              ) : (
                outline.map((node) => renderNode(node))
              )}
            </div>
          </div>

          <div
            style={{
              flex: 1,
              padding: 28,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              background: 'rgba(255, 255, 255, 0.92)',
              borderTopRightRadius: 28,
              borderBottomRightRadius: 28,
            }}
          >
            {selectedNode ? (
              <>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 220px' }}>
                    <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Title</label>
                    <input
                      value={nodeTitle}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNodeTitle(value);
                        handleNodeFieldChange({ title: value });
                      }}
                      placeholder="Ingredient title"
                      style={{
                        borderRadius: 6,
                        border: '1px solid #d1d5db',
                        padding: '8px 10px',
                        background: '#ffffff',
                        color: '#111827',
                        fontSize: 12,
                        width: '100%',
                      }}
                    />
                  </div>
                  <div style={{ flex: '0 0 180px' }}>
                    <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Type</label>
                    <select
                      value={nodeType}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNodeType(value);
                        handleNodeFieldChange({ type: value });
                      }}
                      style={{
                        borderRadius: 6,
                        border: '1px solid #d1d5db',
                        padding: '8px 10px',
                        background: '#ffffff',
                        color: '#111827',
                        fontSize: 12,
                        width: '100%',
                      }}
                    >
                      {NODE_TYPES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '0 0 180px' }}>
                    <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Resource Type</label>
                    <select
                      value={resourceType}
                      onChange={(e) => {
                        const value = e.target.value;
                        setResourceType(value);
                        if (!value) {
                          setResourceId('');
                        }
                        handleNodeFieldChange({ resource_type: value, resource_id: value ? resourceId : '' });
                      }}
                      style={{
                        borderRadius: 6,
                        border: '1px solid #d1d5db',
                        padding: '8px 10px',
                        background: '#ffffff',
                        color: '#111827',
                        fontSize: 12,
                        width: '100%',
                      }}
                    >
                      {RESOURCE_TYPES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '0 0 220px' }}>
                    <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Resource ID</label>
                    <input
                      value={resourceId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setResourceId(value);
                        handleNodeFieldChange({ resource_id: value });
                      }}
                      placeholder="Optional resource identifier"
                      style={{
                        borderRadius: 8,
                        border: '1px solid #d1d5db',
                        padding: '10px 12px',
                        background: '#ffffff',
                        color: '#111827',
                        fontSize: 14,
                        width: '100%',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Custom Description</label>
                  <textarea
                    value={customDescription}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomDescription(value);
                      handleNodeFieldChange({ custom_description: value });
                    }}
                    placeholder="Describe the ingredient when no catalog resource is attached."
                    rows={3}
                    style={{
                      borderRadius: 8,
                      border: '1px solid #d1d5db',
                      padding: '12px 14px',
                      background: '#ffffff',
                      color: '#111827',
                      fontSize: 14,
                      width: '100%',
                      resize: 'vertical',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      fontFamily: 'inherit',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Custom Notes / Metadata</label>
                  <textarea
                    value={customNotes}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomNotes(value);
                      handleNodeFieldChange({ custom_notes: value });
                    }}
                    placeholder="Optional notes that help the LLM (constraints, time, audience, etc.)."
                    rows={2}
                    style={{
                      borderRadius: 8,
                      border: '1px solid #d1d5db',
                      padding: '12px 14px',
                      background: '#ffffff',
                      color: '#111827',
                      fontSize: 14,
                      width: '100%',
                      resize: 'vertical',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      fontFamily: 'inherit',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Node Content</label>
                    {nodeContent && (
                      <button
                        id="copy-content-btn"
                        onClick={handleCopyNodeContent}
                        style={{
                          fontSize: 11,
                          color: '#6b7280',
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = '#9ca3af';
                          e.target.style.color = '#374151';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.color = '#6b7280';
                        }}
                      >
                        Copy
                      </button>
                    )}
                  </div>
                  <textarea
                    value={nodeContent}
                    onChange={(e) => setNodeContent(e.target.value)}
                    placeholder="Draft or generated content for this ingredient."
                    style={{
                      flex: 1,
                      minHeight: 180,
                      width: '100%',
                      borderRadius: 8,
                      border: '1px solid #d1d5db',
                      padding: '12px 14px',
                      background: '#ffffff',
                      color: '#111827',
                      fontSize: 14,
                      lineHeight: 1.6,
                      resize: 'vertical',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      fontFamily: 'inherit',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div
                  style={{
                    marginTop: 12,
                    padding: 14,
                    border: '1px solid #e5e7eb',
                    borderRadius: 10,
                    background: '#f9fafb',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>AI Edit (node scope)</span>
                    {nodeAiProcessing && <span style={{ fontSize: 11, color: '#d97706' }}>Working…</span>}
                  </div>
                  <textarea
                    value={nodeAiInstruction}
                    onChange={(e) => setNodeAiInstruction(e.target.value)}
                    placeholder="Describe how you want this node content to change (e.g., 'make activities more experiential', 'add metrics')."
                    rows={3}
                    style={{
                      borderRadius: 8,
                      border: '1px solid #d1d5db',
                      padding: '12px 14px',
                      background: '#ffffff',
                      color: '#111827',
                      fontSize: 14,
                      width: '100%',
                      resize: 'vertical',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      fontFamily: 'inherit',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      onClick={handleAiEditSelectedNode}
                      disabled={nodeAiProcessing}
                      style={{
                        borderRadius: 12,
                        border: '1px solid #fb923c',
                        background: nodeAiProcessing ? '#fb923c' : '#fb923c',
                        color: '#ffffff',
                        padding: '10px 20px',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: nodeAiProcessing ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        opacity: nodeAiProcessing ? 0.7 : 1,
                        boxShadow: nodeAiProcessing ? 'none' : '0 4px 12px rgba(251, 146, 60, 0.3)',
                      }}
                      onMouseEnter={(e) => {
                        if (!nodeAiProcessing) {
                          e.target.style.background = '#f97316';
                          e.target.style.borderColor = '#f97316';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!nodeAiProcessing) {
                          e.target.style.background = '#fb923c';
                          e.target.style.borderColor = '#fb923c';
                        }
                      }}
                    >
                      {nodeAiProcessing ? 'Running Edit…' : 'Run AI Edit'}
                    </button>
                  </div>
                  {nodeAiPlan && nodeAiPlan.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ fontSize: 11, color: '#4f46e5', marginBottom: 4 }}>Latest AI Plan</div>
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#6b7280' }}>
                        {nodeAiPlan.map((op, idx) => (
                          <li key={`${op.type}-${idx}`}>
                            {op.type?.toUpperCase()} lines {op.start_line}-{op.end_line}: {op.rationale}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {nodeAiStatus && (
                    <div style={{ fontSize: 11, color: nodeAiStatus.startsWith('Error') ? '#dc2626' : '#059669' }}>
                      {nodeAiStatus}
                    </div>
                  )}
                  {nodeDiffs.length > 0 && (
                    <div style={{ marginTop: 12, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 8,
                          alignItems: 'center',
                          marginBottom: 10,
                        }}
                      >
                        <button
                          onClick={() => setNodeDiffView(!nodeDiffView)}
                          style={{
                            borderRadius: 10,
                            border: '1px solid #c4b5fd',
                            background: nodeDiffView ? '#e9d5ff' : '#f3e8ff',
                            color: '#7c3aed',
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(196, 181, 253, 0.25)',
                            transition: 'all 0.2s',
                          }}
                        >
                          {nodeDiffView ? 'Hide Diffs' : 'View Diffs'}
                        </button>
                        <button
                          onClick={handleNodeAcceptAllDiffs}
                          disabled={nodeDiffs.every((diff) => diff.status !== 'pending')}
                          style={{
                            borderRadius: 10,
                            border: '1px solid #86efac',
                            background: '#dcfce7',
                            color: '#166534',
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 500,
                            opacity: nodeDiffs.every((diff) => diff.status !== 'pending') ? 0.5 : 1,
                            cursor: nodeDiffs.every((diff) => diff.status !== 'pending') ? 'not-allowed' : 'pointer',
                            boxShadow: nodeDiffs.every((diff) => diff.status !== 'pending') ? 'none' : '0 2px 8px rgba(134, 239, 172, 0.25)',
                            transition: 'all 0.2s',
                          }}
                        >
                          Accept All
                        </button>
                        <button
                          onClick={handleNodeRejectAllDiffs}
                          disabled={nodeDiffs.every((diff) => diff.status === 'error')}
                          style={{
                            borderRadius: 10,
                            border: '1px solid #fca5a5',
                            background: '#fee2e2',
                            color: '#991b1b',
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 500,
                            opacity: nodeDiffs.every((diff) => diff.status === 'error') ? 0.5 : 1,
                            cursor: nodeDiffs.every((diff) => diff.status === 'error') ? 'not-allowed' : 'pointer',
                            boxShadow: nodeDiffs.every((diff) => diff.status === 'error') ? 'none' : '0 2px 8px rgba(252, 165, 165, 0.25)',
                            transition: 'all 0.2s',
                          }}
                        >
                          Reject All
                        </button>
                        <button
                          onClick={handleApplyNodeDiffs}
                          disabled={
                            nodeApplyingDiffs ||
                            nodeDiffs.filter((diff) => diff.status === 'accepted' && !diff.error).length === 0
                          }
                          style={{
                            borderRadius: 10,
                            border: '1px solid #86efac',
                            background:
                              nodeApplyingDiffs ||
                                nodeDiffs.filter((diff) => diff.status === 'accepted' && !diff.error).length === 0
                                ? '#f3f4f6'
                                : '#dcfce7',
                            color:
                              nodeApplyingDiffs ||
                                nodeDiffs.filter((diff) => diff.status === 'accepted' && !diff.error).length === 0
                                ? '#9ca3af'
                                : '#166534',
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 500,
                            opacity:
                              nodeApplyingDiffs ||
                                nodeDiffs.filter((diff) => diff.status === 'accepted' && !diff.error).length === 0
                                ? 0.6
                                : 1,
                            cursor:
                              nodeApplyingDiffs ||
                                nodeDiffs.filter((diff) => diff.status === 'accepted' && !diff.error).length === 0
                                ? 'not-allowed'
                                : 'pointer',
                            boxShadow:
                              nodeApplyingDiffs ||
                                nodeDiffs.filter((diff) => diff.status === 'accepted' && !diff.error).length === 0
                                ? 'none'
                                : '0 2px 8px rgba(134, 239, 172, 0.25)',
                            transition: 'all 0.2s',
                          }}
                        >
                          {nodeApplyingDiffs
                            ? 'Applying...'
                            : `Apply (${nodeDiffs.filter((diff) => diff.status === 'accepted' && !diff.error).length})`}
                        </button>
                        <button
                          onClick={handleCancelNodeDiffs}
                          style={{
                            borderRadius: 10,
                            border: '1px solid #d1d5db',
                            background: '#f3f4f6',
                            color: '#374151',
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            transition: 'all 0.2s',
                          }}
                        >
                          Clear Diffs
                        </button>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                          Accepted:{' '}
                          {nodeDiffs.filter((diff) => diff.status === 'accepted' && !diff.error).length} • Rejected:{' '}
                          {nodeDiffs.filter((diff) => diff.status === 'rejected').length} • Pending:{' '}
                          {nodeDiffs.filter((diff) => diff.status === 'pending').length} • Errors:{' '}
                          {nodeDiffs.filter((diff) => diff.status === 'error').length}
                        </div>
                      </div>
                      {nodeDiffView && (
                        <div
                          style={{
                            marginTop: 12,
                            border: '1px solid #1f2937',
                            borderRadius: 10,
                            padding: 12,
                            background: '#0b1120',
                          }}
                        >
                          {renderNodeDiffView()}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button
                    onClick={handleGenerateNodeContent}
                    style={{
                      borderRadius: 12,
                      border: '1px solid #86efac',
                      background: (generatingNodeContent || !selectedNodeId) ? '#86efac' : '#86efac',
                      color: (generatingNodeContent || !selectedNodeId) ? '#14532d' : '#14532d',
                      padding: '10px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: (generatingNodeContent || !selectedNodeId) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      opacity: (generatingNodeContent || !selectedNodeId) ? 0.7 : 1,
                      boxShadow: (generatingNodeContent || !selectedNodeId) ? 'none' : '0 4px 12px rgba(134, 239, 172, 0.3)',
                    }}
                    disabled={generatingNodeContent || !selectedNodeId}
                    onMouseEnter={(e) => {
                      if (!generatingNodeContent && selectedNodeId) {
                        e.target.style.background = '#6ee7b7';
                        e.target.style.borderColor = '#6ee7b7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!generatingNodeContent && selectedNodeId) {
                        e.target.style.background = '#86efac';
                        e.target.style.borderColor = '#86efac';
                      }
                    }}
                  >
                    {generatingNodeContent ? 'Generating…' : 'Generate Content'}
                  </button>
                  <button
                    onClick={handleSaveNodeContent}
                    style={{
                      borderRadius: 12,
                      border: '1px solid #93c5fd',
                      background: savingNode ? '#93c5fd' : '#93c5fd',
                      color: '#1e3a8a',
                      padding: '10px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: savingNode ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      opacity: savingNode ? 0.7 : 1,
                      boxShadow: savingNode ? 'none' : '0 4px 12px rgba(147, 197, 253, 0.3)',
                    }}
                    disabled={savingNode}
                    onMouseEnter={(e) => {
                      if (!savingNode) {
                        e.target.style.background = '#7dd3fc';
                        e.target.style.borderColor = '#7dd3fc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!savingNode) {
                        e.target.style.background = '#93c5fd';
                        e.target.style.borderColor = '#93c5fd';
                      }
                    }}
                  >
                    {savingNode ? 'Saving…' : 'Save Node'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: '#6b7280', fontSize: 14 }}>Select an ingredient from the outline to edit its details.</div>
            )}
          </div>
        </div>

        {/* Advanced Model Overrides - hidden
        <div
          style={{
            ...cardBaseStyle,
            marginTop: 20,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, color: dashboardTheme.accentNavy }}>Advanced Model Overrides</h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: dashboardTheme.accentSlate }}>
                Customize which LLMs power both the planner (line selection) and the content generator. Leave any field blank
                to keep using the server defaults.
              </p>
            </div>
            <button
              onClick={() => setShowModelSettings((prev) => !prev)}
              style={{
                borderRadius: 18,
                border: 'none',
                background: showModelSettings ? dashboardTheme.accentPrimary : 'rgba(88, 102, 242, 0.12)',
                color: showModelSettings ? '#ffffff' : dashboardTheme.accentPrimary,
                padding: '8px 18px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: showModelSettings ? '0 12px 30px rgba(88, 102, 242, 0.3)' : 'none',
              }}
            >
              {showModelSettings ? 'Hide' : 'Show'}
            </button>
          </div>
          {showModelSettings && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: dashboardTheme.accentNavy, marginBottom: 4 }}>
                  Planner model override
                </label>
                <input
                  type="text"
                  value={plannerModelOverride}
                  onChange={(e) => setPlannerModelOverride(e.target.value)}
                  placeholder="e.g., openai/gpt-4.1-mini"
                  style={{ ...inputSurfaceStyle, width: '100%' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = dashboardTheme.accentPrimary;
                    e.target.style.boxShadow = '0 0 0 4px rgba(88, 102, 242, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(15, 23, 42, 0.08)';
                    e.target.style.boxShadow = '0 5px 20px rgba(15, 23, 42, 0.08)';
                  }}
                />
                <div style={{ fontSize: 11, color: dashboardTheme.accentSlate, marginTop: 4 }}>
                  Default server model: <code style={{ background: '#f3f4f6', padding: '2px 4px', borderRadius: 4 }}>mistralai/mistral-nemo:free</code>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: dashboardTheme.accentNavy, marginBottom: 4 }}>
                  Planner fallback model (optional)
                </label>
                <input
                  type="text"
                  value={plannerFallbackOverride}
                  onChange={(e) => setPlannerFallbackOverride(e.target.value)}
                  placeholder="e.g., openai/gpt-4o-mini"
                  style={{ ...inputSurfaceStyle, width: '100%' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = dashboardTheme.accentPrimary;
                    e.target.style.boxShadow = '0 0 0 4px rgba(88, 102, 242, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(15, 23, 42, 0.08)';
                    e.target.style.boxShadow = '0 5px 20px rgba(15, 23, 42, 0.08)';
                  }}
                />
                <div style={{ fontSize: 11, color: dashboardTheme.accentSlate, marginTop: 4 }}>
                  Default server fallback: <code style={{ background: '#f3f4f6', padding: '2px 4px', borderRadius: 4 }}>deepseek/deepseek-chat-v3.1:free</code>
                  Many providers enforce rate limits; use a model your OpenRouter account can access.
                </div>
              </div>
            </div>
          )}
        </div>
        */}

        {/* Full Document View Card */}
        <div style={{ ...cardBaseStyle, marginTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 18, color: dashboardTheme.accentNavy }}>Full Document</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleDownloadTXT}
                disabled={outline.length === 0}
                style={{
                  display: 'none', // Hidden
                  borderRadius: 12,
                  border: '1px solid #93c5fd',
                  background: outline.length === 0 ? '#f3f4f6' : '#93c5fd',
                  color: outline.length === 0 ? '#9ca3af' : '#1e3a8a',
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: outline.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: outline.length === 0 ? 0.6 : 1,
                  boxShadow: outline.length === 0 ? 'none' : '0 4px 12px rgba(147, 197, 253, 0.3)',
                }}
                title="Download as TXT file"
                onMouseEnter={(e) => {
                  if (outline.length > 0) {
                    e.target.style.background = '#7dd3fc';
                    e.target.style.borderColor = '#7dd3fc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (outline.length > 0) {
                    e.target.style.background = '#93c5fd';
                    e.target.style.borderColor = '#93c5fd';
                  }
                }}
              >
                📄 Download TXT
              </button>
              <button
                onClick={handleDownloadPDF}
                disabled={outline.length === 0}
                style={{
                  borderRadius: 12,
                  border: '1px solid #fca5a5',
                  background: outline.length === 0 ? '#f3f4f6' : '#fca5a5',
                  color: outline.length === 0 ? '#9ca3af' : '#7f1d1d',
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: outline.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: outline.length === 0 ? 0.6 : 1,
                  boxShadow: outline.length === 0 ? 'none' : '0 4px 12px rgba(252, 165, 165, 0.3)',
                }}
                title="Download as PDF file"
                onMouseEnter={(e) => {
                  if (outline.length > 0) {
                    e.target.style.background = '#f87171';
                    e.target.style.borderColor = '#f87171';
                  }
                }}
                onMouseLeave={(e) => {
                  if (outline.length > 0) {
                    e.target.style.background = '#fca5a5';
                    e.target.style.borderColor = '#fca5a5';
                  }
                }}
              >
                Full Proposal PDF
              </button>
              <button
                onClick={handleDownloadDeckPDF}
                disabled={outline.length === 0 || deckGenerating}
                style={{
                  borderRadius: 12,
                  border: '1px solid #86efac',
                  background:
                    outline.length === 0
                      ? '#f3f4f6'
                      : deckGenerating
                        ? '#86efac'
                        : '#86efac',
                  color: outline.length === 0 ? '#9ca3af' : '#14532d',
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: outline.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: outline.length === 0 ? 0.6 : 1,
                  boxShadow: (outline.length === 0 || deckGenerating) ? 'none' : '0 4px 12px rgba(134, 239, 172, 0.3)',
                }}
                title="Generate executive deck PDF on demand"
                onMouseEnter={(e) => {
                  if (outline.length > 0 && !deckGenerating) {
                    e.target.style.background = '#6ee7b7';
                    e.target.style.borderColor = '#6ee7b7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (outline.length > 0 && !deckGenerating) {
                    e.target.style.background = '#86efac';
                    e.target.style.borderColor = '#86efac';
                  }
                }}
              >
                Proposal Deck PDF
              </button>
              <button
                onClick={handleDownloadPPT}
                disabled={outline.length === 0 || (deckGenerating && Object.keys(deckSummaries).length === 0)}
                style={{
                  borderRadius: 12,
                  border: '1px solid #c4b5fd',
                  background:
                    outline.length === 0
                      ? '#f3f4f6'
                      : (deckGenerating && Object.keys(deckSummaries).length === 0)
                        ? '#c4b5fd'
                        : '#c4b5fd',
                  color: outline.length === 0 ? '#9ca3af' : '#4c1d95',
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: outline.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: outline.length === 0 ? 0.6 : 1,
                  boxShadow: (outline.length === 0 || (deckGenerating && Object.keys(deckSummaries).length === 0)) ? 'none' : '0 4px 12px rgba(196, 181, 253, 0.3)',
                }}
                title="Download deck content as PowerPoint presentation (will generate summaries if needed)"
                onMouseEnter={(e) => {
                  if (outline.length > 0 && !(deckGenerating && Object.keys(deckSummaries).length === 0)) {
                    e.target.style.background = '#a78bfa';
                    e.target.style.borderColor = '#a78bfa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (outline.length > 0 && !(deckGenerating && Object.keys(deckSummaries).length === 0)) {
                    e.target.style.background = '#c4b5fd';
                    e.target.style.borderColor = '#c4b5fd';
                  }
                }}
              >
                Proposal PPT
              </button>
              <button
                onClick={handleDownloadFullDocPPT}
                disabled={outline.length === 0}
                style={{
                  display: 'none', // Hidden
                  borderRadius: 12,
                  border: '1px solid #93c5fd',
                  background: outline.length === 0 ? '#f3f4f6' : '#93c5fd',
                  color: outline.length === 0 ? '#9ca3af' : '#1e3a8a',
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: outline.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: outline.length === 0 ? 0.6 : 1,
                  boxShadow: outline.length === 0 ? 'none' : '0 4px 12px rgba(147, 197, 253, 0.3)',
                }}
                title="Convert leaf-level full document content into a PPT"
                onMouseEnter={(e) => {
                  if (outline.length > 0) {
                    e.target.style.background = '#7dd3fc';
                    e.target.style.borderColor = '#7dd3fc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (outline.length > 0) {
                    e.target.style.background = '#93c5fd';
                    e.target.style.borderColor = '#93c5fd';
                  }
                }}
              >
                🗂️ Full Doc PPT
              </button>
              <button
                onClick={handleClearDeckSummaries}
                disabled={deckGenerating || Object.keys(deckSummaries).length === 0}
                style={{
                  borderRadius: 12,
                  border: '1px solid #cbd5e1',
                  background:
                    deckGenerating || Object.keys(deckSummaries).length === 0
                      ? '#f3f4f6'
                      : '#e2e8f0',
                  color:
                    deckGenerating || Object.keys(deckSummaries).length === 0
                      ? '#9ca3af'
                      : '#475569',
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor:
                    deckGenerating || Object.keys(deckSummaries).length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  transition: 'all 0.2s',
                  opacity: (deckGenerating || Object.keys(deckSummaries).length === 0) ? 0.6 : 1,
                  boxShadow: (deckGenerating || Object.keys(deckSummaries).length === 0) ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}
                title="Clear cached deck summaries so you can regenerate fresh output"
                onMouseEnter={(e) => {
                  if (!deckGenerating && Object.keys(deckSummaries).length > 0) {
                    e.target.style.background = '#cbd5e1';
                    e.target.style.borderColor = '#cbd5e1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!deckGenerating && Object.keys(deckSummaries).length > 0) {
                    e.target.style.background = '#e2e8f0';
                    e.target.style.borderColor = '#e2e8f0';
                  }
                }}
              >
                Reset Deck
              </button>
            </div>
            <div
              style={{
                ...insetCardStyle,
                marginTop: 12,
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 13,
                  color: dashboardTheme.accentNavy,
                  fontWeight: 500,
                  marginBottom: 6,
                }}
              >
                <div style={{ position: 'relative' }}>
                  <input
                    type="checkbox"
                    checked={useResourceContentDirectly}
                    onChange={handleResourceContentToggle}
                    disabled={resourceScriptApplying}
                    style={useResourceContentDirectly ? checkboxCheckedStyle : checkboxStyle}
                  />
                  {useResourceContentDirectly && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 10,
                        height: 10,
                        pointerEvents: 'none',
                      }}
                    >
                      <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M2 6L5 9L10 2"
                          stroke="#ffffff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                Use resource scripts verbatim (skip AI for resource-backed nodes)
              </label>
              <div style={{ fontSize: 12, color: dashboardTheme.accentSlate, lineHeight: 1.5 }}>
                When enabled, any node that has catalog resources will paste those scripts directly into its content.
                Nodes without resources continue to use the AI writer.
              </div>
              {resourceScriptStatus && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: resourceScriptApplying ? dashboardTheme.accentPrimary : dashboardTheme.accentSuccess,
                  }}
                >
                  {resourceScriptStatus}
                </div>
              )}
            </div>
            {deckStatus && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#fbbf24' }}>{deckStatus}</div>
            )}
          </div>
          <FullDocumentViewer
            planId={planId}
            outline={outline}
            conceptDocument={conceptDocument}
            generateProgramOverview={generateProgramOverview}
            fullDocumentOverride={fullDocumentOverride}
            setFullDocumentOverride={setFullDocumentOverride}
            modelOverrides={{
              plannerModel: plannerModelOverride?.trim() || '',
              plannerFallbackModel: plannerFallbackOverride?.trim() || '',
              generatorModel: generatorModelOverride?.trim() || '',
              generatorFallbackModel: generatorFallbackOverride?.trim() || '',
            }}
            showNewProposalModal={showNewProposalModal}
            onCloseNewProposalModal={() => setShowNewProposalModal(false)}
            onConfirmNewProposal={handleNewProposalConfirm}
            showDeleteProposalModal={showDeleteProposalModal}
            onCloseDeleteProposalModal={() => setShowDeleteProposalModal(false)}
            onConfirmDeleteProposal={handleDeleteOutlineConfirm}
          />
        </div>
      </div>
      {exportProgress.show && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 12,
              padding: 24,
              minWidth: 400,
              maxWidth: 500,
              border: '1px solid #e5e7eb',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
          >
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 8 }}>
                {exportProgress.type === 'ppt' ? '📽️ Generating PowerPoint' : '📑 Generating PDF'}
              </h3>
              <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
                {exportProgress.message || 'Please wait...'}
              </p>
            </div>
            <div
              style={{
                width: '100%',
                height: 8,
                backgroundColor: '#334155',
                borderRadius: 4,
                overflow: 'hidden',
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  width: `${(exportProgress.current / exportProgress.total) * 100}%`,
                  height: '100%',
                  backgroundColor: exportProgress.type === 'ppt' ? '#a855f7' : '#3b82f6',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: '#64748b', textAlign: 'center' }}>
              {exportProgress.current}% complete
            </div>
          </div>
        </div>
      )}
      {/* Add Missing Child Modal */}
      {addMissingChildModal?.open && addMissingChildModal?.parentNode && preliminaryProposal && (
        <AddMissingChildModal
          isOpen={addMissingChildModal.open}
          onClose={() => setAddMissingChildModal({ open: false, parentId: null })}
          preliminaryProposal={preliminaryProposal}
          parentNode={{ ...addMissingChildModal.parentNode, planId }}
          existingChildren={addMissingChildModal.parentNode.children || []}
          onAddChild={handleMissingChildAdded}
          wsRef={wsRef}
        />
      )}
    </div>
  );
};

// Helper function to extract temporal tag from node (Day, Week, Month)
const getTemporalTag = (node) => {
  const nodeType = (node.type || '').toLowerCase();
  const title = (node.title || '').trim();
  const titleLower = title.toLowerCase();

  // Check for journey_day type first (most specific)
  if (nodeType === 'journey_day') {
    const dayMatch = title.match(/day\s*(\d+)/i) || title.match(/^(\d+)$/);
    if (dayMatch) return `[Day ${dayMatch[1]}]`;
    if (titleLower.includes('day')) return '[Day]';
    return '[Day]';
  }

  if (titleLower.includes('month')) {
    const monthMatch = title.match(/month\s*(\d+)/i) || title.match(/(\d+)\s*month/i);
    if (monthMatch) return `[Month ${monthMatch[1]}]`;
    return '[Month]';
  }

  if (titleLower.includes('week')) {
    const weekMatch = title.match(/week\s*(\d+)/i) || title.match(/(\d+)\s*week/i);
    if (weekMatch) return `[Week ${weekMatch[1]}]`;
    return '[Week]';
  }

  return null;
};

// Helper function to compute module number for a parent node (1, 2, 3...)
const getModuleNumber = (node, outline) => {
  if (!outline || !node) return null;

  // Filter out title, problem_statement, and commercials to get program nodes
  const programNodes = outline.filter(n => {
    const type = n.type || '';
    const hasChildren = Array.isArray(n.children) && n.children.length > 0;
    return type !== 'title' && type !== 'problem_statement' && type !== 'commercials' && hasChildren;
  });

  // Find the index of this node in program nodes
  const index = programNodes.findIndex(n => n.id === node.id);
  return index >= 0 ? index + 1 : null;
};

// Helper function to compute child number for a child node (1.1, 1.2, 1.3...)
const getChildNumber = (node, outline) => {
  if (!outline || !node) return null;

  // Find parent node
  const findParent = (targetNode, nodes, parent = null) => {
    for (const n of nodes || []) {
      if (n.id === targetNode.id) {
        return parent;
      }
      if (n.children && n.children.length > 0) {
        const found = findParent(targetNode, n.children, n);
        if (found !== null) return found;
      }
    }
    return null;
  };

  const parent = findParent(node, outline);
  if (!parent || !parent.children) return null;

  // Get module number of parent
  const moduleNum = getModuleNumber(parent, outline);
  if (!moduleNum) return null;

  // Find child index in parent's children
  const childIndex = parent.children.findIndex(c => c.id === node.id);
  if (childIndex < 0) return null;

  return `${moduleNum}.${childIndex + 1}`;
};

// Helper function to get parent module title for a child node
const getParentModuleTitle = (node, outline) => {
  if (!outline || !node) return null;

  // Find parent node
  const findParent = (targetNode, nodes, parent = null) => {
    for (const n of nodes || []) {
      if (n.id === targetNode.id) {
        return parent;
      }
      if (n.children && n.children.length > 0) {
        const found = findParent(targetNode, n.children, n);
        if (found !== null) return found;
      }
    }
    return null;
  };

  const parent = findParent(node, outline);
  if (!parent) return null;

  // Check if parent is a module (has children and is not temporal)
  const parentType = (parent.type || '').toLowerCase();
  const hasChildren = Array.isArray(parent.children) && parent.children.length > 0;
  const isTemporal = getTemporalTag(parent) !== null;

  // If parent is a module (not temporal), return its title
  if (hasChildren && !isTemporal && parentType !== 'title' && parentType !== 'problem_statement') {
    return parent.title || null;
  }

  return null;
};

// Helper function to build hierarchical path with numbering (Day 1 - 1.1 : DEIB > Introduction)
const buildHierarchicalPath = (node, outline) => {
  const temporalPath = [];

  const findParentChain = (targetNode, nodes, currentPath = []) => {
    for (const n of nodes || []) {
      if (n.id === targetNode.id) {
        return currentPath;
      }

      if (n.children && n.children.length > 0) {
        const found = findParentChain(targetNode, n.children, [...currentPath, n]);
        if (found !== null) return found;
      }
    }
    return null;
  };

  const chain = findParentChain(node, outline);

  // If no chain, this is a root-level node (not a child), so no path needed
  if (!chain || chain.length === 0) {
    return '';
  }

  // Build temporal path (Day, Week, Month) from chain
  for (const n of chain) {
    const tag = getTemporalTag(n);
    if (tag) {
      const match = tag.match(/\[(Day|Week|Month)\s*(\d+)?\]/);
      if (match) {
        const [, type, num] = match;
        if (num) {
          temporalPath.push(`${type} ${num}`);
        } else {
          const title = (n.title || '').toLowerCase();
          const numMatch = title.match(/(?:day|week|month)\s*(\d+)/i);
          if (numMatch) {
            temporalPath.push(`${type} ${numMatch[1]}`);
          } else {
            temporalPath.push(type);
          }
        }
      }
    }
  }

  // Get child number and parent title
  const childNum = getChildNumber(node, outline);
  const parentTitle = getParentModuleTitle(node, outline);

  // Build final path
  let result = '';

  // Add temporal path if exists
  if (temporalPath.length > 0) {
    result += temporalPath.join(' - ') + ' - ';
  }

  // Add child number and parent title
  if (childNum) {
    result += `${childNum} : `;

    // Add parent title if exists
    if (parentTitle) {
      result += `${parentTitle} > `;
    }
  }

  return result;
};

// Helper to build a position map: maps original (line, char) to edited (line, char)
const buildPositionMap = (original, edited, operations) => {
  const originalLines = original.split('\n');
  const editedLines = edited.split('\n');
  const sortedOps = [...operations].sort((a, b) => a.start_line - b.start_line);

  // Track line shifts from operations and precise position mapping
  const lineShifts = [];
  const operationRanges = []; // Store actual edited ranges for each operation

  sortedOps.forEach((op, idx) => {
    const startLine = op.start_line;
    const endLine = op.end_line;
    const startIdx = Math.max(0, startLine - 1);
    const endIdx = Math.min(originalLines.length, endLine);

    // Calculate cumulative line offset
    let cumulativeOffset = 0;
    for (let i = 0; i < idx; i++) {
      if (sortedOps[i].start_line < startLine) {
        cumulativeOffset += lineShifts[i] || 0;
      }
    }

    // Find original content
    const originalChunk = originalLines.slice(startIdx, endIdx).join('\n');
    const originalLineCount = endIdx - startIdx;

    // Find edited content position
    const newStartIdx = Math.max(0, startIdx + cumulativeOffset);
    let newEndIdx = newStartIdx;
    let editedChunk = '';

    if (op.type === 'replace') {
      if (newStartIdx < editedLines.length) {
        if (originalChunk.trim() === '') {
          // Original empty - find where new content ends
          const maxSearch = Math.min(newStartIdx + 300, editedLines.length);
          for (let i = newStartIdx; i < maxSearch; i++) {
            const line = editedLines[i] || '';
            if (line.trim().startsWith('#') && i > newStartIdx + 10) {
              newEndIdx = i;
              break;
            }
            newEndIdx = i + 1;
          }
        } else {
          // Original had content - estimate replacement length
          const extractWindow = Math.max(originalLineCount * 2, 50, Math.min(200, editedLines.length - newStartIdx));
          newEndIdx = newStartIdx + extractWindow;
        }
        editedChunk = editedLines.slice(newStartIdx, newEndIdx).join('\n');
      }
    } else if (op.type === 'add') {
      const addLength = endLine - startLine + 1;
      newEndIdx = newStartIdx + addLength;
      if (newStartIdx < editedLines.length) {
        editedChunk = editedLines.slice(newStartIdx, newEndIdx).join('\n');
      }
    } else if (op.type === 'remove') {
      newEndIdx = newStartIdx;
      editedChunk = '';
    }

    const lineDelta = (newEndIdx - newStartIdx) - originalLineCount;
    lineShifts[idx] = lineDelta;

    // Calculate precise character positions
    // For original: start at beginning of start line, end at end of end line
    const originalStartChar = 0; // Always start at beginning of line for operations
    const originalEndChar = endIdx > 0 && endIdx <= originalLines.length
      ? (originalLines[endIdx - 1]?.length || 0)
      : 0;

    // For edited: start at beginning of start line, end at end of end line
    const editedStartChar = 0; // Always start at beginning of line for operations
    const editedEndChar = newEndIdx > 0 && newEndIdx <= editedLines.length
      ? (editedLines[newEndIdx - 1]?.length || 0)
      : 0;

    operationRanges.push({
      op,
      originalStartLine: startLine,
      originalEndLine: endLine,
      originalStartChar,
      originalEndChar,
      editedStartLine: newStartIdx + 1,
      editedEndLine: newEndIdx || newStartIdx + 1,
      editedStartChar,
      editedEndChar,
      originalText: originalChunk,
      editedText: editedChunk
    });
  });

  return { operationRanges, originalLines, editedLines };
};

// Helper to calculate diff with precise line and character position tracking
const calculateDiff = (original, edited, operations = []) => {
  const diffs = [];

  // If we have operations from the AI plan, use position-aware tracking
  if (operations && operations.length > 0) {
    const { operationRanges, originalLines, editedLines } = buildPositionMap(original, edited, operations);

    operationRanges.forEach((range, idx) => {
      const { op, originalText, editedText, originalStartLine, originalEndLine, editedStartLine, editedEndLine } = range;

      // Extract with precise line and character tracking
      const originalChunk = originalText || '';
      const editedChunk = editedText || (op.type === 'remove' ? '' : '');

      // Calculate precise positions
      const originalStartPos = {
        line: originalStartLine,
        char: range.originalStartChar,
        display: `Line ${originalStartLine}, Char ${range.originalStartChar}`
      };
      const originalEndPos = {
        line: originalEndLine,
        char: range.originalEndChar,
        display: `Line ${originalEndLine}, Char ${range.originalEndChar}`
      };
      const editedStartPos = {
        line: editedStartLine,
        char: range.editedStartChar,
        display: `Line ${editedStartLine}, Char ${range.editedStartChar}`
      };
      const editedEndPos = {
        line: editedEndLine,
        char: range.editedEndChar,
        display: `Line ${editedEndLine}, Char ${range.editedEndChar}`
      };

      diffs.push({
        id: idx,
        operation: op,
        startLine: originalStartLine,
        endLine: originalEndLine,
        originalText: originalChunk || '(empty)',
        editedText: editedChunk || (op.type === 'remove' ? '(removed)' : '(empty)'),
        type: op.type || 'replace',
        rationale: op.rationale || '',
        // Precise position tracking
        positions: {
          original: { start: originalStartPos, end: originalEndPos },
          edited: { start: editedStartPos, end: editedEndPos }
        }
      });
    });
  } else {
    // Fallback: simple line-by-line diff
    let origIdx = 0;
    let editIdx = 0;
    let currentDiff = null;

    while (origIdx < originalLines.length || editIdx < editedLines.length) {
      const origLine = originalLines[origIdx] || '';
      const editLine = editedLines[editIdx] || '';

      if (origLine === editLine) {
        if (currentDiff) {
          diffs.push(currentDiff);
          currentDiff = null;
        }
        origIdx++;
        editIdx++;
      } else {
        if (!currentDiff) {
          currentDiff = {
            id: diffs.length,
            startLine: origIdx + 1,
            endLine: origIdx + 1,
            originalText: origLine,
            editedText: editLine,
            type: 'replace',
            rationale: ''
          };
        } else {
          currentDiff.endLine = origIdx + 1;
          currentDiff.originalText += '\n' + origLine;
          currentDiff.editedText += '\n' + editLine;
        }
        origIdx++;
        editIdx++;
      }
    }

    if (currentDiff) {
      diffs.push(currentDiff);
    }
  }

  return diffs;
};

const FullDocumentViewer = ({
  planId,
  outline,
  conceptDocument,
  generateProgramOverview,
  modelOverrides = {},
  fullDocumentOverride = null,
  setFullDocumentOverride = () => { },
  showNewProposalModal = false,
  onCloseNewProposalModal = () => { },
  onConfirmNewProposal = () => { },
  showDeleteProposalModal = false,
  onCloseDeleteProposalModal = () => { },
  onConfirmDeleteProposal = () => { },
}) => {
  const [fullDocument, setFullDocument] = useState('');
  const [originalDocument, setOriginalDocument] = useState('');
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiPlan, setAiPlan] = useState([]);
  const [aiStatus, setAiStatus] = useState('');
  const [editedDocument, setEditedDocument] = useState(null);
  const [diffView, setDiffView] = useState(false);
  const [currentDiffIndex, setCurrentDiffIndex] = useState(0);
  const [diffs, setDiffs] = useState([]);
  const [applyingDiffs, setApplyingDiffs] = useState(false);
  const [memoryMap, setMemoryMap] = useState(null);
  const fullDiffSelectionsRef = useRef({});

  const outlineKey = useMemo(() => {
    if (!outline || outline.length === 0) return '';
    return JSON.stringify(outline.map(n => ({ id: n.id, title: n.title }))) + (conceptDocument || '');
  }, [outline, conceptDocument]);

  const resetFullDocDiffState = (statusMessage = '', nextOriginal = null) => {
    setEditedDocument(null);
    setDiffView(false);
    setDiffs([]);
    setAiPlan([]);
    setMemoryMap(null);
    setAiStatus(statusMessage);
    if (nextOriginal) {
      setOriginalDocument(nextOriginal);
    }
  };

  useEffect(() => {
    const loadFullDocument = async () => {
      if (!planId || outline.length === 0) {
        setFullDocument('');
        return;
      }
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;
      setLoading(true);
      try {
        let doc = '';
        let problemStatementFound = false;
        let programOverviewInserted = false;

        const compileNode = async (node, depth = 0) => {
          const indent = '  '.repeat(depth);
          const nodeType = node.type || '';
          const hasChildren = Array.isArray(node.children) && node.children.length > 0;
          const isParentNode = hasChildren;

          if (nodeType === 'problem_statement' && !programOverviewInserted) {
            problemStatementFound = true;
          }

          let nodeTitle = node.title || 'Untitled';
          if (!isParentNode && depth > 0) {
            const hierarchicalPath = buildHierarchicalPath(node, outline);
            if (hierarchicalPath) {
              nodeTitle = hierarchicalPath + nodeTitle;
            }
          }

          doc += `${indent}# ${nodeTitle}\n\n`;

          let nodeContent = '';
          if (planId && node.id) {
            try {
              const data = await apiService.getProposalNode(planId, node.id);
              if (data?.content && data.content.trim()) {
                nodeContent = data.content.trim();
              }
            } catch (error) {
              // ignore missing node content
            }
          }

          if (!isParentNode) {
            if (nodeContent) {
              doc += `${indent}${nodeContent}\n\n`;
            } else if (node.custom_description) {
              doc += `${indent}${node.custom_description}\n\n`;
            }
          }

          if (problemStatementFound && !programOverviewInserted && nodeType === 'problem_statement') {
            const overviewMarkdown = generateProgramOverview(outline);
            doc += '\n' + overviewMarkdown + '\n\n';
            programOverviewInserted = true;
          }

          if (node.children && node.children.length > 0) {
            for (const child of node.children) {
              await compileNode(child, depth + 1);
            }
          }

          doc += '\n';
        };

        for (const node of outline) {
          await compileNode(node);
        }

        if (!programOverviewInserted) {
          const overviewMarkdown = generateProgramOverview(outline);
          doc = overviewMarkdown + '\n\n' + doc;
        }

        const compiledDoc = doc.trim();
        setFullDocument(compiledDoc);
        setOriginalDocument(compiledDoc);
        setFullDocumentOverride(null);
      } catch (error) {
        console.error('Failed to compile full document:', error);
        setFullDocument('Error loading document content.');
        setOriginalDocument('');
        setFullDocumentOverride(null);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadFullDocument();
  }, [planId, outlineKey, generateProgramOverview]);

  const handleAiEditFullDocument = async () => {
    if (!fullDocument || !fullDocument.trim()) {
      alert('Full document content is empty or still loading.');
      return;
    }
    if (!aiInstruction.trim()) {
      alert('Enter an instruction for the AI edit.');
      return;
    }
    setAiProcessing(true);
    setAiStatus('');
    try {
      // Store original before editing
      const original = fullDocument;

      // Use new content-based discovery endpoint
      const overridesPayload = {};
      const plannerOverride =
        typeof modelOverrides?.plannerModel === 'string' ? modelOverrides.plannerModel.trim() : '';
      const plannerFallbackOverride =
        typeof modelOverrides?.plannerFallbackModel === 'string'
          ? modelOverrides.plannerFallbackModel.trim()
          : '';
      if (plannerOverride) {
        overridesPayload.planner_model = plannerOverride;
      }
      if (plannerFallbackOverride) {
        overridesPayload.planner_fallback_model = plannerFallbackOverride;
      }

      const requestOptions = {
        scope: 'document',
        ...(Object.keys(overridesPayload).length ? { model_overrides: overridesPayload } : {}),
      };

      const result = await apiService.brainDiscoverDiffs(original, aiInstruction, requestOptions);

      console.log('🔍 Discover Diffs Response:', result);
      console.log('🔍 Diff Set:', result?.diff_set);
      console.log('🔍 Diffs Array:', result?.diff_set?.diffs);

      if (result?.diff_set?.diffs) {
        setOriginalDocument(original);
        setMemoryMap(result.memory_map);
        const operations = result?.plan?.operations || [];
        setAiPlan(operations);

        // Convert backend diffs to frontend format
        const discoveredDiffs = result.diff_set.diffs.map((diff, idx) => {
          const originalText = diff.original_text || '';
          const editedText = diff.new_text || '';
          const startLine = diff.start_line ?? 0;
          const endLine = diff.end_line ?? startLine;
          return {
            id: `${diff.operation?.type || 'op'}-${diff.start_line}-${diff.end_line}-${idx}-${Date.now()}`,
            originalText,
            editedText,
            startLine,
            endLine,
            type: diff.operation?.type || 'replace',
            rationale: diff.operation?.rationale || '',
            sectionName: diff.section_name || '',
            status: diff.status || 'pending', // pending, accepted, rejected, error
            error: diff.error || null,
            startChar: diff.start_char,
            endChar: diff.end_char,
            matchScore: diff.match_score || 1.0,
            beforeContext: diff.before_context || '',
            afterContext: diff.after_context || '',
            operation: diff.operation,
            initialStartLine: startLine,
            initialEndLine: endLine,
            fullOriginalText: originalText,
            fullEditedText: editedText,
          }
        });

        setDiffs(discoveredDiffs);
        console.log('🔍 Setting diffs:', discoveredDiffs.length, 'diffs');
        console.log('🔍 First diff:', discoveredDiffs[0]);

        // Show diff view if there are changes
        if (discoveredDiffs.length > 0) {
          console.log('🔍 Enabling diff view');
          setDiffView(true);
          setCurrentDiffIndex(0);
          const errorCount = discoveredDiffs.filter(d => d.status === 'error').length;
          const pendingCount = discoveredDiffs.filter(d => d.status === 'pending').length;

          if (result.diff_set.has_overlaps) {
            setAiStatus(
              `⚠️ Found ${discoveredDiffs.length} diff(s) with ${errorCount} error(s). ` +
              `${result.diff_set.overlap_errors.length} overlap(s) detected. ` +
              `Review and accept/reject individual diffs.`
            );
          } else {
            setAiStatus(
              `✅ Found ${pendingCount} diff(s) ready for review. ` +
              `${errorCount > 0 ? `${errorCount} error(s) detected. ` : ''}` +
              `Review and accept/reject individual diffs below.`
            );
          }
        } else {
          setDiffView(false);
          setAiStatus('AI edit completed but no changes detected.');
        }
      } else {
        console.warn('⚠️ No diffs in response:', result);
        setAiStatus('No diffs returned from AI edit. Check console for details.');
        // Still try to show what we got
        if (result) {
          console.log('Full response:', JSON.stringify(result, null, 2));
        }
      }
    } catch (error) {
      console.error('Full document AI edit failed:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(error?.message || 'Failed to edit document with AI.');
      setAiStatus('Error: ' + (error?.message || 'Failed to edit document'));
    } finally {
      setAiProcessing(false);
    }
  };

  const handleAcceptDiff = (diffId) => {
    setDiffs(prevDiffs =>
      prevDiffs.map(diff =>
        diff.id === diffId && !diff.error ? { ...diff, status: 'accepted' } : diff
      )
    );
  };

  const handleRejectDiff = (diffId) => {
    setDiffs(prevDiffs =>
      prevDiffs.map(diff =>
        diff.id === diffId && !diff.error ? { ...diff, status: 'rejected' } : diff
      )
    );
  };

  const handleAcceptAllDiffs = () => {
    setDiffs(prevDiffs =>
      prevDiffs.map(diff =>
        diff.status === 'pending' && !diff.error ? { ...diff, status: 'accepted' } : diff
      )
    );
  };

  const handleRejectAllDiffs = () => {
    setDiffs(prevDiffs =>
      prevDiffs.map(diff =>
        !diff.error && (diff.status === 'pending' || diff.status === 'accepted')
          ? { ...diff, status: 'rejected' }
          : diff
      )
    );
  };

  const handleFullDiffSelectionChange = (diffId, selectionStart, selectionEnd) => {
    fullDiffSelectionsRef.current[diffId] = {
      start: selectionStart,
      end: selectionEnd,
    };
  };

  const handleFullDiffEditedTextChange = (diffId, newValue) => {
    setDiffs(prevDiffs =>
      prevDiffs.map(diff =>
        diff.id === diffId ? { ...diff, editedText: newValue } : diff
      )
    );
  };

  const handleFullTrimToSelection = (diffId) => {
    const selection = fullDiffSelectionsRef.current[diffId];
    if (!selection || selection.start === selection.end) {
      alert('Select text in the original block before trimming.');
      return;
    }

    setDiffs(prevDiffs =>
      prevDiffs.map(diff => {
        if (diff.id !== diffId) return diff;

        const baseText = diff.originalText || '';
        const start = Math.max(0, Math.min(selection.start, baseText.length));
        const end = Math.max(0, Math.min(selection.end, baseText.length));
        if (start >= end) {
          alert('Invalid selection. Please try again.');
          return diff;
        }

        const textBeforeSelection = baseText.slice(0, start);
        const textUntilSelectionEnd = baseText.slice(0, end);
        const startOffsetLines = (textBeforeSelection.match(/\n/g) || []).length;
        const endOffsetLines = (textUntilSelectionEnd.match(/\n/g) || []).length;

        const newStartLine = diff.startLine + startOffsetLines;
        const newEndLine = diff.startLine + endOffsetLines;
        const newOriginalText = baseText.slice(start, end);

        return {
          ...diff,
          startLine: newStartLine,
          endLine: Math.max(newStartLine, newEndLine),
          originalText: newOriginalText,
          operation: diff.operation
            ? { ...diff.operation, start_line: newStartLine, end_line: Math.max(newStartLine, newEndLine) }
            : diff.operation,
        };
      })
    );
  };

  const handleFullDiffOriginalReset = (diffId) => {
    setDiffs(prevDiffs =>
      prevDiffs.map(diff => {
        if (diff.id !== diffId) return diff;
        const initialStart = diff.initialStartLine ?? diff.startLine ?? 0;
        const initialEnd = diff.initialEndLine ?? diff.endLine ?? initialStart;
        const baseText = diff.fullOriginalText ?? diff.originalText ?? '';
        return {
          ...diff,
          startLine: initialStart,
          endLine: initialEnd,
          originalText: baseText,
          operation: diff.operation
            ? { ...diff.operation, start_line: initialStart, end_line: initialEnd }
            : diff.operation,
        };
      })
    );
  };

  const handleFullDiffLineChange = (diffId, field, newValue) => {
    setDiffs(prevDiffs =>
      prevDiffs.map(diff => {
        if (diff.id !== diffId) return diff;
        const initialStart = diff.initialStartLine ?? diff.startLine ?? 0;
        const initialEnd = diff.initialEndLine ?? diff.endLine ?? initialStart;
        const baseText = diff.fullOriginalText ?? diff.originalText ?? '';
        const baseLines = baseText ? baseText.split('\n') : [];
        if (!baseLines.length) return diff;

        let parsedValue = Number(newValue);
        if (Number.isNaN(parsedValue)) return diff;

        let newStart = diff.startLine ?? initialStart;
        let newEnd = diff.endLine ?? initialEnd;

        if (field === 'start') {
          newStart = Math.min(Math.max(parsedValue, initialStart), newEnd);
        } else {
          newEnd = Math.max(Math.min(parsedValue, initialEnd), newStart);
        }

        if (newStart < initialStart) newStart = initialStart;
        if (newEnd > initialEnd) newEnd = initialEnd;
        if (newStart > newEnd) {
          if (field === 'start') {
            newEnd = newStart;
          } else {
            newStart = newEnd;
          }
        }

        const startOffset = Math.max(0, newStart - initialStart);
        const endOffset = Math.max(startOffset, newEnd - initialStart + 1);
        const originalSlice = baseLines.slice(startOffset, endOffset).join('\n');

        return {
          ...diff,
          startLine: newStart,
          endLine: newEnd,
          originalText: originalSlice,
          operation: diff.operation
            ? { ...diff.operation, start_line: newStart, end_line: newEnd }
            : diff.operation,
        };
      })
    );
  };

  const handleFullDiffSpanReset = (diffId) => {
    setDiffs(prevDiffs =>
      prevDiffs.map(diff => {
        if (diff.id !== diffId) return diff;
        const initialStart = diff.initialStartLine ?? diff.startLine ?? 0;
        const initialEnd = diff.initialEndLine ?? diff.endLine ?? initialStart;
        const baseText = diff.fullOriginalText ?? diff.originalText ?? '';

        return {
          ...diff,
          startLine: initialStart,
          endLine: initialEnd,
          originalText: baseText,
          operation: diff.operation
            ? { ...diff.operation, start_line: initialStart, end_line: initialEnd }
            : diff.operation,
        };
      })
    );
  };

  const handleFullDiffResetEditedText = (diffId) => {
    setDiffs(prevDiffs =>
      prevDiffs.map(diff =>
        diff.id === diffId
          ? { ...diff, editedText: diff.fullEditedText ?? diff.editedText }
          : diff
      )
    );
  };

  const handleApplyEdit = async () => {
    const acceptedDiffs = diffs.filter(d => d.status === 'accepted' && !d.error);

    if (acceptedDiffs.length === 0) {
      alert('No diffs accepted. Please accept at least one diff before applying.');
      return;
    }

    if (!originalDocument || !memoryMap) {
      alert('Missing original document or memory map. Please run discovery again.');
      return;
    }

    setApplyingDiffs(true);
    setAiStatus('Applying accepted diffs...');

    try {
      // Convert frontend diff format back to backend format
      const backendDiffs = acceptedDiffs.map(diff => ({
        operation: diff.operation,
        original_text: diff.originalText,
        new_text: diff.editedText,
        before_context: diff.beforeContext,
        after_context: diff.afterContext,
        section_name: diff.sectionName,
        start_char: diff.startChar,
        end_char: diff.endChar,
        start_line: diff.startLine,
        end_line: diff.endLine,
        match_score: diff.matchScore,
        status: 'accepted',
        error: diff.error,
      }));

      const result = await apiService.brainApplyDiffs(originalDocument, memoryMap, backendDiffs);

      if (result?.edited_content) {
        const updatedDocument = result.edited_content;
        setFullDocument(updatedDocument);
        setFullDocumentOverride(updatedDocument);
        const message =
          result.warnings && result.warnings.length > 0
            ? `✅ Edit applied. Warnings: ${result.warnings.join('; ')}`
            : `✅ ${acceptedDiffs.length} diff(s) applied successfully.`;
        resetFullDocDiffState(message, updatedDocument);
      } else {
        setAiStatus('Error: No edited content returned.');
      }
    } catch (error) {
      console.error('Apply diffs failed:', error);
      alert(error?.message || 'Failed to apply diffs.');
      setAiStatus('Error: ' + (error?.message || 'Failed to apply diffs'));
    } finally {
      setApplyingDiffs(false);
    }
  };

  const handleRejectEdit = () => {
    resetFullDocDiffState('Edit rejected. Document unchanged.', fullDocument);
  };

  const renderDiffView = () => {
    if (diffs.length === 0 || currentDiffIndex >= diffs.length) {
      return <div style={{ color: '#6b7280', fontSize: 13 }}>No diffs to display.</div>;
    }

    const currentDiff = diffs[currentDiffIndex];
    const originalLines = currentDiff.originalText.split('\n');
    const editedLines = currentDiff.editedText.split('\n');

    return (
      <div>
        {/* Diff Navigation */}
        {diffs.length > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px',
            background: 'rgba(30, 41, 59, 0.5)',
            borderRadius: 8,
            marginBottom: 16,
            border: '1px solid #334155'
          }}>
            <button
              onClick={() => setCurrentDiffIndex(Math.max(0, currentDiffIndex - 1))}
              disabled={currentDiffIndex === 0}
              style={{
                borderRadius: 6,
                border: '1px solid rgba(59,130,246,0.6)',
                background: currentDiffIndex === 0 ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.25)',
                color: '#bfdbfe',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 500,
                cursor: currentDiffIndex === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              ← Previous
            </button>
            <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div>
                Change {currentDiffIndex + 1} of {diffs.length}
                {currentDiff.status === 'accepted' && ' ✅ Accepted'}
                {currentDiff.status === 'rejected' && ' ❌ Rejected'}
                {currentDiff.status === 'error' && ' ⚠️ Error'}
                {currentDiff.status === 'pending' && ' ⏳ Pending'}
              </div>
              {currentDiff.rationale && (
                <div style={{ fontSize: 11, maxWidth: 300, textAlign: 'center' }}>
                  {currentDiff.rationale}
                </div>
              )}
            </div>
            <button
              onClick={() => setCurrentDiffIndex(Math.min(diffs.length - 1, currentDiffIndex + 1))}
              disabled={currentDiffIndex === diffs.length - 1}
              style={{
                borderRadius: 6,
                border: '1px solid rgba(59,130,246,0.6)',
                background: currentDiffIndex === diffs.length - 1 ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.25)',
                color: '#bfdbfe',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 500,
                cursor: currentDiffIndex === diffs.length - 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Next →
            </button>
          </div>
        )}

        {/* Diff Display */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          marginBottom: 16
        }}>
          {/* Original */}
          <div style={{
            border: '1px solid #334155',
            borderRadius: 8,
            padding: 12,
            background: 'rgba(30, 41, 59, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              fontSize: 11,
              color: '#ef4444',
              fontWeight: 600,
              marginBottom: 8,
              paddingBottom: 8,
              borderBottom: '1px solid #334155'
            }}>
              <span>
                ORIGINAL (Lines {currentDiff.startLine}-{currentDiff.endLine})
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: '#fbbf24', fontWeight: 500 }}>
                  Initial {currentDiff.initialStartLine}-{currentDiff.initialEndLine}
                </span>
                {(currentDiff.startLine !== currentDiff.initialStartLine ||
                  currentDiff.endLine !== currentDiff.initialEndLine ||
                  currentDiff.originalText !== currentDiff.fullOriginalText) && (
                    <button
                      onClick={() => handleFullDiffOriginalReset(currentDiff.id)}
                      style={{
                        borderRadius: 4,
                        border: '1px solid rgba(148,163,184,0.6)',
                        background: 'rgba(148,163,184,0.2)',
                        color: '#111827',
                        padding: '2px 8px',
                        fontSize: 11,
                        cursor: 'pointer',
                      }}
                    >
                      Reset original
                    </button>
                  )}
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10, fontSize: 11, color: '#cbd5f5' }}>
              <span>Span:</span>
              <input
                type="number"
                value={currentDiff.startLine}
                min={currentDiff.initialStartLine}
                max={currentDiff.endLine}
                onChange={(e) => handleFullDiffLineChange(currentDiff.id, 'start', e.target.value)}
                style={{
                  width: 80,
                  padding: '2px 6px',
                  borderRadius: 4,
                  border: '1px solid #475569',
                  background: '#020617',
                  color: '#111827',
                }}
              />
              <span>to</span>
              <input
                type="number"
                value={currentDiff.endLine}
                min={currentDiff.startLine}
                max={currentDiff.initialEndLine}
                onChange={(e) => handleFullDiffLineChange(currentDiff.id, 'end', e.target.value)}
                style={{
                  width: 80,
                  padding: '2px 6px',
                  borderRadius: 4,
                  border: '1px solid #475569',
                  background: '#020617',
                  color: '#111827',
                }}
              />
              {(currentDiff.startLine !== currentDiff.initialStartLine ||
                currentDiff.endLine !== currentDiff.initialEndLine) && (
                  <button
                    onClick={() => handleFullDiffSpanReset(currentDiff.id)}
                    style={{
                      borderRadius: 4,
                      border: '1px solid rgba(148,163,184,0.6)',
                      background: 'rgba(148,163,184,0.2)',
                      color: '#111827',
                      padding: '2px 8px',
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    Reset span
                  </button>
                )}
              <button
                onClick={() => handleFullTrimToSelection(currentDiff.id)}
                style={{
                  borderRadius: 4,
                  border: '1px solid rgba(59,130,246,0.5)',
                  background: 'rgba(59,130,246,0.2)',
                  color: '#bfdbfe',
                  padding: '2px 10px',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                Trim to selection
              </button>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>
                Select text below, then trim.
              </span>
            </div>
            <textarea
              readOnly
              value={currentDiff.originalText}
              onSelect={(e) =>
                handleFullDiffSelectionChange(
                  currentDiff.id,
                  e.target.selectionStart,
                  e.target.selectionEnd
                )
              }
              rows={Math.min(12, (currentDiff.originalText || '').split('\n').length + 1)}
              style={{
                width: '100%',
                borderRadius: 6,
                border: '1px solid rgba(239,68,68,0.3)',
                background: 'rgba(239,68,68,0.1)',
                color: '#111827',
                fontSize: 12,
                lineHeight: 1.6,
                fontFamily: 'monospace',
                padding: 10,
                resize: 'vertical',
                minHeight: 140,
              }}
            />
          </div>

          {/* Edited */}
          <div style={{
            border: '1px solid #334155',
            borderRadius: 8,
            padding: 12,
            background: 'rgba(30, 41, 59, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              color: '#22c55e',
              fontWeight: 600,
              marginBottom: 8,
              paddingBottom: 8,
              borderBottom: '1px solid #334155'
            }}>
              <span>EDITED ({currentDiff.type?.toUpperCase() || 'CHANGE'})</span>
              {currentDiff.fullEditedText && currentDiff.editedText !== currentDiff.fullEditedText && (
                <button
                  onClick={() => handleFullDiffResetEditedText(currentDiff.id)}
                  style={{
                    borderRadius: 4,
                    border: '1px solid rgba(34,197,94,0.4)',
                    background: 'rgba(34,197,94,0.2)',
                    color: '#bbf7d0',
                    padding: '2px 8px',
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Reset text
                </button>
              )}
            </div>
            <textarea
              value={currentDiff.editedText}
              onChange={(e) => handleFullDiffEditedTextChange(currentDiff.id, e.target.value)}
              rows={Math.min(12, (currentDiff.editedText || '').split('\n').length + 1)}
              placeholder="Edit the replacement content before accepting."
              style={{
                width: '100%',
                borderRadius: 6,
                border: '1px solid #475569',
                background: '#020617',
                color: '#111827',
                fontSize: 12,
                lineHeight: 1.6,
                fontFamily: 'monospace',
                padding: 10,
                resize: 'vertical',
                minHeight: 140,
              }}
            />
          </div>
        </div>

        {/* Quick diff overview */}
        <div style={{
          fontSize: 11,
          color: '#94a3b8',
          padding: 8,
          background: 'rgba(15, 23, 42, 0.5)',
          borderRadius: 6,
          marginTop: 8,
          marginBottom: 12
        }}>
          <strong>Operation:</strong> {currentDiff.type?.toUpperCase()} |
          <strong> Section:</strong> {currentDiff.sectionName || 'N/A'} |
          <strong> Position:</strong> Lines {currentDiff.startLine}-{currentDiff.endLine}
          {currentDiff.matchScore !== undefined && currentDiff.matchScore < 1.0 && (
            <> | <strong>Match Score:</strong> {(currentDiff.matchScore * 100).toFixed(0)}%</>
          )}
          {currentDiff.error && (
            <><br /><strong style={{ color: '#ef4444' }}>Error:</strong> {currentDiff.error}</>
          )}
          {currentDiff.rationale && (
            <><br /><strong>Reason:</strong> {currentDiff.rationale}</>
          )}
        </div>

        {/* Accept/Reject Actions for Current Diff */}
        {currentDiff.status !== 'error' && (
          <div style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
            padding: 12,
            background: 'rgba(30, 41, 59, 0.3)',
            borderRadius: 8,
            border: '1px solid #334155',
            marginTop: 8
          }}>
            <button
              onClick={() => handleAcceptDiff(currentDiff.id)}
              disabled={currentDiff.status === 'accepted' || applyingDiffs}
              style={{
                borderRadius: 6,
                border: '1px solid rgba(34,197,94,0.6)',
                background: currentDiff.status === 'accepted'
                  ? 'rgba(34,197,94,0.4)'
                  : 'rgba(34,197,94,0.25)',
                color: '#86efac',
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 500,
                cursor: currentDiff.status === 'accepted' || applyingDiffs ? 'not-allowed' : 'pointer',
                opacity: currentDiff.status === 'accepted' || applyingDiffs ? 0.6 : 1,
              }}
            >
              {currentDiff.status === 'accepted' ? '✅ Accepted' : '✅ Accept This Diff'}
            </button>
            <button
              onClick={() => handleRejectDiff(currentDiff.id)}
              disabled={currentDiff.status === 'rejected' || applyingDiffs}
              style={{
                borderRadius: 6,
                border: '1px solid rgba(239,68,68,0.6)',
                background: currentDiff.status === 'rejected'
                  ? 'rgba(239,68,68,0.4)'
                  : 'rgba(239,68,68,0.25)',
                color: '#fca5a5',
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 500,
                cursor: currentDiff.status === 'rejected' || applyingDiffs ? 'not-allowed' : 'pointer',
                opacity: currentDiff.status === 'rejected' || applyingDiffs ? 0.6 : 1,
              }}
            >
              {currentDiff.status === 'rejected' ? '❌ Rejected' : '❌ Reject This Diff'}
            </button>
          </div>
        )}

        {/* Summary Stats */}
        <div style={{
          fontSize: 11,
          color: '#94a3b8',
          padding: 12,
          background: 'rgba(30, 41, 59, 0.3)',
          borderRadius: 8,
          border: '1px solid #334155',
          marginTop: 12,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center'
        }}>
          <div>
            <strong>Total:</strong> {diffs.length}
          </div>
          <div style={{ color: '#86efac' }}>
            <strong>Accepted:</strong> {diffs.filter(d => d.status === 'accepted').length}
          </div>
          <div style={{ color: '#fca5a5' }}>
            <strong>Rejected:</strong> {diffs.filter(d => d.status === 'rejected').length}
          </div>
          <div style={{ color: '#94a3b8' }}>
            <strong>Pending:</strong> {diffs.filter(d => d.status === 'pending').length}
          </div>
          {diffs.filter(d => d.status === 'error').length > 0 && (
            <div style={{ color: '#fbbf24' }}>
              <strong>Errors:</strong> {diffs.filter(d => d.status === 'error').length}
            </div>
          )}
        
      </div>
    </div>
  );
};

  const renderDocumentBody = () => {
    if (loading) {
      return <div style={{ color: '#6b7280', fontSize: 13 }}>Loading full document…</div>;
    }
    if (!fullDocument) {
      return <div style={{ color: '#6b7280', fontSize: 13 }}>No content available. Generate an outline to see the full document.</div>;
    }

    // Show diff view if enabled and we have diffs
    console.log('🔍 renderDocumentBody - diffView:', diffView, 'diffs.length:', diffs.length);
    if (diffView && diffs.length > 0) {
      console.log('🔍 Rendering diff view');
      return renderDiffView();
    }

    // Otherwise show normal document view
    const documentToShow = fullDocumentOverride || fullDocument;
    const segments = parseMarkdownTables(documentToShow);
    return segments.map((seg, idx) => {
      if (seg.type === 'html') {
        return (
          <div
            key={idx}
            dangerouslySetInnerHTML={{ __html: seg.content }}
            style={{ marginBottom: '16px' }}
            className="proposal-table-container"
          />
        );
      }
      let content = seg.content;
      if (content.trim().startsWith('#')) {
        const match = content.match(/^(#+)\s*(.+)$/);
        if (match) {
          const level = match[1].length;
          const text = match[2];
          const fontSize = 24 - (level - 1) * 2;
          return (
            <div
              key={idx}
              style={{
                fontSize: `${fontSize}px`,
                fontWeight: 600,
                margin: '16px 0 8px 0',
                color: '#111827',
              }}
            >
              {renderTextWithBold(text)}
            </div>
          );
        }
      }
      return (
        <div
          key={idx}
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            marginBottom: seg.content.trim() ? '8px' : '4px',
            color: '#111827'
          }}
        >
          {renderTextWithBold(content)}
        </div>
      );
    });
  };

  return (
    <div style={{ ...cardBaseStyle, marginBottom: 20 }}>
      {/* AI Edit (Full Document) section - HIDDEN */}
      {/*
      <h3 style={{ marginTop: 0, fontSize: 18, color: dashboardTheme.accentNavy }}>AI Edit (Full Document)</h3>
      <p style={{ fontSize: 12, color: dashboardTheme.accentSlate, marginTop: 4 }}>
        Provide instructions to edit the full proposal document. Review and accept/reject changes before applying.
      </p>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginTop: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: dashboardTheme.accentSlate }}>AI Edit (full proposal scope)</span>
          {aiProcessing && <span style={{ fontSize: 11, color: dashboardTheme.accentPrimary }}>Working…</span>}
        </div>
        <textarea
          value={aiInstruction}
          onChange={(e) => setAiInstruction(e.target.value)}
          placeholder="Example: 'Expand the client brief, add more detail to activities, convert all costs to INR.'"
          rows={3}
          style={{
            ...inputSurfaceStyle,
            width: '100%',
            resize: 'vertical',
            fontFamily: 'inherit',
            minHeight: 120,
          }}
          onFocus={(e) => {
            e.target.style.borderColor = dashboardTheme.accentPrimary;
            e.target.style.boxShadow = '0 0 0 4px rgba(88, 102, 242, 0.15)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(15, 23, 42, 0.08)';
            e.target.style.boxShadow = '0 5px 20px rgba(15, 23, 42, 0.08)';
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {diffs.length > 0 && (
              <>
                <button
                  onClick={() => setDiffView(!diffView)}
                  style={{
                    borderRadius: 12,
                    border: '1px solid #93c5fd',
                    background: diffView ? dashboardTheme.accentPrimary : '#ffffff',
                    color: diffView ? '#ffffff' : '#1e3a8a',
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: diffView ? '0 4px 12px rgba(88, 102, 242, 0.3)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!diffView) {
                      e.target.style.background = '#dfe4ff';
                      e.target.style.borderColor = '#93c5fd';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!diffView) {
                      e.target.style.background = '#ffffff';
                      e.target.style.borderColor = '#93c5fd';
                    }
                  }}
                >
                  {diffView ? '📄 View Document' : '🔍 View Diffs'}
                </button>
                <button
                  onClick={handleAcceptAllDiffs}
                  disabled={applyingDiffs || diffs.every(d => d.status !== 'pending' || d.error)}
                  style={{
                    borderRadius: 12,
                    border: '1px solid #93c5fd',
                    background: (applyingDiffs || diffs.every(d => d.status !== 'pending' || d.error)) ? 'rgba(147, 197, 253, 0.6)' : '#93c5fd',
                    color: '#1e3a8a',
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor:
                      applyingDiffs || diffs.every(d => d.status !== 'pending' || d.error)
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      applyingDiffs || diffs.every(d => d.status !== 'pending' || d.error) ? 0.6 : 1,
                    transition: 'all 0.2s',
                    boxShadow: (applyingDiffs || diffs.every(d => d.status !== 'pending' || d.error)) ? 'none' : '0 4px 12px rgba(147, 197, 253, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (!applyingDiffs && !diffs.every(d => d.status !== 'pending' || d.error)) {
                e.target.style.background = '#7dd3fc';
                e.target.style.borderColor = '#7dd3fc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!applyingDiffs && !diffs.every(d => d.status !== 'pending' || d.error)) {
                e.target.style.background = '#93c5fd';
                e.target.style.borderColor = '#93c5fd';
                    }
                  }}
                >
                  ✅ Accept All
                </button>
                <button
                  onClick={handleRejectAllDiffs}
                  disabled={
                    applyingDiffs ||
                    diffs.every(d => d.error || (d.status !== 'pending' && d.status !== 'accepted'))
                  }
                  style={{
                    borderRadius: 12,
                    border: '1px solid #93c5fd',
                    background: (applyingDiffs || diffs.every(d => d.error || (d.status !== 'pending' && d.status !== 'accepted'))) ? 'rgba(147, 197, 253, 0.6)' : '#93c5fd',
                    color: '#1e3a8a',
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor:
                      applyingDiffs ||
                      diffs.every(
                        d => d.error || (d.status !== 'pending' && d.status !== 'accepted')
                      )
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      applyingDiffs ||
                      diffs.every(
                        d => d.error || (d.status !== 'pending' && d.status !== 'accepted')
                      )
                        ? 0.6
                        : 1,
                    transition: 'all 0.2s',
                    boxShadow: (applyingDiffs || diffs.every(d => d.error || (d.status !== 'pending' && d.status !== 'accepted'))) ? 'none' : '0 4px 12px rgba(147, 197, 253, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (!applyingDiffs && !diffs.every(d => d.error || (d.status !== 'pending' && d.status !== 'accepted'))) {
                e.target.style.background = '#7dd3fc';
                e.target.style.borderColor = '#7dd3fc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!applyingDiffs && !diffs.every(d => d.error || (d.status !== 'pending' && d.status !== 'accepted'))) {
                e.target.style.background = '#93c5fd';
                e.target.style.borderColor = '#93c5fd';
                    }
                  }}
                >
                  ❌ Reject All
                </button>
                <button
                  onClick={handleApplyEdit}
                  disabled={applyingDiffs || diffs.filter(d => d.status === 'accepted' && !d.error).length === 0}
                  style={{
                    borderRadius: 12,
                    border: '1px solid #93c5fd',
                    background: (applyingDiffs || diffs.filter(d => d.status === 'accepted' && !d.error).length === 0) ? 'rgba(147, 197, 253, 0.6)' : '#93c5fd',
                    color: '#1e3a8a',
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: applyingDiffs || diffs.filter(d => d.status === 'accepted' && !d.error).length === 0
                      ? 'not-allowed'
                      : 'pointer',
                    opacity: applyingDiffs || diffs.filter(d => d.status === 'accepted' && !d.error).length === 0
                      ? 0.6
                      : 1,
                    transition: 'all 0.2s',
                    boxShadow: (applyingDiffs || diffs.filter(d => d.status === 'accepted' && !d.error).length === 0) ? 'none' : '0 4px 12px rgba(147, 197, 253, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (!applyingDiffs && diffs.filter(d => d.status === 'accepted' && !d.error).length > 0) {
                e.target.style.background = '#7dd3fc';
                e.target.style.borderColor = '#7dd3fc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!applyingDiffs && diffs.filter(d => d.status === 'accepted' && !d.error).length > 0) {
                e.target.style.background = '#93c5fd';
                e.target.style.borderColor = '#93c5fd';
                    }
                  }}
                >
                  {applyingDiffs ? '⏳ Applying...' : `✅ Apply (${diffs.filter(d => d.status === 'accepted' && !d.error).length})`}
                </button>
                <button
                  onClick={handleRejectEdit}
                  style={{
                    borderRadius: 12,
                    border: '1px solid #93c5fd',
                    background: '#93c5fd',
                    color: '#1e3a8a',
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(147, 197, 253, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                e.target.style.background = '#7dd3fc';
                e.target.style.borderColor = '#7dd3fc';
                  }}
                  onMouseLeave={(e) => {
                e.target.style.background = '#93c5fd';
                e.target.style.borderColor = '#93c5fd';
                  }}
                >
                  ❌ Reject Edit
                </button>
              </>
            )}
          </div>
          <button
            onClick={handleAiEditFullDocument}
            disabled={aiProcessing || loading || !fullDocument}
            style={{
              borderRadius: 20,
              border: 'none',
              background: (aiProcessing || loading || !fullDocument) ? 'rgba(88, 102, 242, 0.35)' : dashboardTheme.accentPrimary,
              color: '#ffffff',
            padding: '12px 28px',
              fontSize: 14,
            fontWeight: 600,
              cursor: (aiProcessing || loading || !fullDocument) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: (aiProcessing || loading || !fullDocument) ? 0.7 : 1,
              boxShadow: (aiProcessing || loading || !fullDocument) ? 'none' : '0 20px 40px rgba(88, 102, 242, 0.3)',
            }}
            onMouseEnter={(e) => {
              if (!aiProcessing && !loading && fullDocument) {
                e.target.style.background = '#7c3aed';
                e.target.style.boxShadow = '0 20px 40px rgba(88, 102, 242, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!aiProcessing && !loading && fullDocument) {
                e.target.style.background = dashboardTheme.accentPrimary;
                e.target.style.boxShadow = '0 20px 40px rgba(88, 102, 242, 0.3)';
              }
            }}
          >
            {aiProcessing ? 'Running Edit…' : 'Run AI Edit (Full Doc)'}
          </button>
        </div>
        {aiStatus && (
          <div style={{ fontSize: 12, color: dashboardTheme.accentSlate }}>{aiStatus}</div>
        )}
        {aiPlan && aiPlan.length > 0 && !diffView && (
          <div>
            <div style={{ fontSize: 12, color: dashboardTheme.accentPrimary, marginBottom: 4 }}>Latest AI Plan</div>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: dashboardTheme.accentSlate }}>
              {aiPlan.map((op, idx) => (
                <li key={`${op.type}-${idx}`}>
                  {op.type?.toUpperCase()} lines {op.start_line}-{op.end_line}: {op.rationale}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      */}

      <div
        style={{
          ...insetCardStyle,
          marginTop: 14,
          background: dashboardTheme.insetBg,
          color: dashboardTheme.accentNavy,
          fontSize: 13,
          lineHeight: 1.8,
          maxHeight: 600,
          overflowY: 'auto',
        }}
      >
        {renderDocumentBody()}
      </div>

      {/* New Proposal Confirmation Modal - portal to body so it overlays entire page */}
      {showNewProposalModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={onCloseNewProposalModal}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 16,
              padding: 24,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 18, color: '#1e3a8a' }}>
              Create New Proposal?
            </h3>
            <p style={{ marginBottom: 20, fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
              Are you sure you want to create a new proposal? Your current proposal will be saved and retained in the database. You can access it later from the Management page.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={onCloseNewProposalModal}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={onConfirmNewProposal}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#60a5fa',
                  color: '#ffffff',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Create New Proposal
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Proposal Confirmation Modal - portal to body so it overlays entire page */}
      {showDeleteProposalModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={onCloseDeleteProposalModal}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 16,
              padding: 24,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 18, color: '#7f1d1d' }}>
              Delete Proposal?
            </h3>
            <p style={{ marginBottom: 20, fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
              Are you sure you want to permanently delete this proposal? This will delete the proposal, outline, all content, and it will be removed from Management. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={onCloseDeleteProposalModal}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={onConfirmDeleteProposal}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#fca5a5',
                  color: '#7f1d1d',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Delete Proposal
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProposalOutlineBuilder;

